/**
 * Draft night.
 *
 * This is the screen the whole app is for. The spreadsheet version works, more or less, but it
 * has three failures that repeat every year: keepers have to be typed in by hand before anyone
 * can start, nothing stops two people writing into the same cell, and nobody in the room can
 * see whose turn it is without counting backwards along a snake.
 *
 * So: keepers arrive already placed on the picks they cost, the pick on the clock is the single
 * loudest thing on the page, and only the GM who owns that pick (or somebody he has asked to
 * cover for him) can type into it.
 *
 * Everything renders from the board plus the saved picks, so a late-arriving pick from another
 * browser just repaints — there is no local turn state to get out of sync.
 */

import { goTo, Linkify } from './RuleText.js';
import {
  useCallback, useEffect, useMemo, useRef, useState, type CSSProperties,
} from 'react';
import keeperRows from '../data/keepers-2026.json';
import { gmOfTeam, managerShort } from '../domain/identity.js';
import {
  keeperAssignments, keeperContexts, type SlateRow,
} from '../domain/liveBoard.js';

export type { SlateRow };
import { useOwnership } from './useOwnership.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { assignKeeperPicks } from '../domain/draftOrder.js';
import { projectedOrder } from '../domain/season2025.js';
import {
  applyKeepers, applyPickTrades, buildBoard, canEnter, guardPick, onTheClock,
  openPicks, pickCode,
  type BoardPick, type PickRef,
} from '../domain/board.js';
import {
  BUNDLED_BANK, positionsOf, searchPlayers,
  type PlayerBank,
} from '../domain/playerBank.js';
import { adjusted, belowStarters, consensus, hasAdp, needs } from '../domain/draftTools.js';
import { BYE_WEEKS, NflMark, nflInfoOf } from './NflChip.js';
import {
  atLeast, type DraftState, type KeeperDeclaration, type LeagueSettings, type LeagueStore,
  type LiveTradeAsset, type LiveTradeDoc, type MockKeeperSet, type PickListEntry, type Role,
} from '../store/types.js';
import { gateOf } from '../domain/deadlines.js';
import { GMS } from '../store/session.js';
import { FarmDraft } from './FarmDraft.js';
import { RailGrip, useRailWidth } from './RailGrip.js';
import { hueFor } from './GmBadge.js';
import { PauseMark, PlayMark, RestartIcon, UndoMark } from './ClockMarks.js';
import { BoardGrid, shortSurname, splitName } from './BoardGrid.js';
import { PhoneBoard } from './PhoneBoard.js';
import { PhoneConfirm } from './PhoneConfirm.js';
import { boardKeepers } from '../domain/mockKeepers.js';
import {
  feasibility, fitKeepers, moveKeeper, shortfallSays, type MoveResult,
} from '../domain/keeperSlots.js';
import { MockKeepers } from './MockKeepers.js';
import { PickClock } from './PickClock.js';
import { countdown, intermissionAfter, pickSeconds } from '../domain/clocks.js';
import {
  firstPickInvolved, overallOf, picksUntilClose, tradeState, type DraftClock,
} from '../domain/tradeWindow.js';
import { downloadXlsx } from './xlsx.js';
import { TeamCards } from './TeamCards.js';
import { Wallboard, enterFullscreen } from './Wallboard.js';
import { MyDraft } from './MyDraft.js';
import { isCoarsePointer } from './pointer.js';
import { useScrollLock } from './useScrollLock.js';
import { dock } from './dock.js';

const SEASON = 2026;
const TEAMS = LEAGUE_2026.teams;
const shortDay = (iso: string) => new Date(iso).toLocaleDateString('en-US', {
  weekday: 'short', day: 'numeric', month: 'short', timeZone: 'America/New_York',
});

type RefTab = 'roster' | 'counts' | 'byes' | 'room';
const REF_TABS: ReadonlyArray<{ key: RefTab; label: string }> = [
  { key: 'roster', label: 'Roster' }, { key: 'counts', label: 'Counts' },
  { key: 'byes', label: 'Byes' }, { key: 'room', label: 'Room' },
];
const ROUNDS = LEAGUE_2026.draftRounds;
/** How many available names the Best available card keeps ranked, and how many it shows. */
const BEST_BANK = 400;
const BEST_SHOWN = 100;

/** Team name → manager, with any seat change made in the app applied on top. */
const GM_OF = new Proxy({} as Record<string, string>, {
  get: (_t, team) => (typeof team === 'string' ? gmOfTeam(team) : undefined),
});

interface KeeperRow {
  player: string; team: string; pos: string; cost: number | null; tagCost: number | null;
  nflTeam: string | null;
  farm?: boolean;
  mandatory?: boolean;
}
const KEEPERS = keeperRows as unknown as KeeperRow[];

/**
 * Round ownership after every pick trade in the ledger.
 *
 * Built by `domain/picks2026`, which Draft tools now shares — the projection used to hand the
 * keeper resolver a flat 1..16 for everybody and disagreed with this board about who owns what.
 */


interface MockTrade { id: number; a: string; b: string; aGets: PickRef[]; bGets: PickRef[] }

/**
 * What each GM's declaration costs him, as concrete rounds. Same resolution the declaration
 * screen runs — collisions and traded picks push a keeper EARLIER (2015-III-7), and the GM's own
 * pins win where he set them.
 */

/** The name a manager would say out loud — `Harrison`, not `Marvin Harrison Jr.` */
function said(player: string): string {
  return shortSurname(splitName(player).last) || player;
}

function andList(xs: readonly string[]): string {
  if (xs.length <= 1) return xs[0] ?? '';
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/**
 * What an escalation actually does, said in names and rounds.
 *
 * "Escalate to accommodate the move" is the mechanism, not the consequence, and nobody agrees to
 * a mechanism at 8pm on draft night. `Ridley to Round 3` is a thing a manager can weigh.
 */
function movedWords(
  before: ReadonlyMap<string, number>, after: ReadonlyMap<string, number>, mover: string,
): string {
  const moves = [...after]
    .filter(([p, r]) => p !== mover && before.get(p) !== r)
    .map(([p, r]) => `${said(p)} to Round ${r}`);
  return moves.length ? andList(moves) : 'nobody else moves';
}

/* ------------------------------------------------------------------------------------------ */

/** Which of the card's two lists this browser last had open. */
const PQ_TAB_KEY = 'ffl.pqtab.v1';

/**
 * A SCROLLER THAT SAYS WHICH WAY IT CARRIES ON.
 *
 * The rows are opaque — the queue's wash is the point — so the usual trick of painting scroll
 * shadows on the scroller's own background is invisible behind them. They go over the rows
 * instead, on a wrapper, and each end is lit only when there is something at that end: no shadow
 * at the top until you have scrolled away from it, none at the bottom once you are at the end.
 *
 * The fade is the list's OWN colour, not a shadow: a drop shadow says something is floating above,
 * a name dissolving into what it is sitting on says it carries on underneath. `.plain` is the
 * white variant, for the player list (Tim, 7 September: "give player list the same gradient at the
 * bottom that picklist gets in scroll").
 */
function useFadeEdges(ref: React.RefObject<HTMLElement | null>) {
  const [edge, setEdge] = useState('');
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const room = el.scrollHeight - el.clientHeight;
    setEdge(`${el.scrollTop > 2 ? ' up' : ''}${room > 2 && el.scrollTop < room - 2 ? ' down' : ''}`);
  }, [ref]);
  /* No dependency list: the lists themselves change as men are queued, taken and drafted. */
  useEffect(measure);
  return { edge, measure };
}

/** The player list's wrapper: same cue, white instead of the queue's wash. */
function FadeList({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLUListElement | null>(null);
  const { edge, measure } = useFadeEdges(ref);
  return (
    <div className={`pqwrap plain${edge}`}>
      <ul className="balist" ref={ref} onScroll={measure}>{children}</ul>
    </div>
  );
}

/*
 * How the queue's auto-scroll behaves when a dragged row is held past an edge.
 *
 * PER SECOND, NOT PER FRAME. A frame is 8ms on a 120Hz phone and 16ms on a laptop, so a
 * per-frame step is a control that runs at twice the speed on half the hardware. Every frame
 * multiplies by its own elapsed time instead, capped at 64ms so a stalled tab cannot jump the
 * list a hundred rows when it comes back.
 */
const PQ_EDGE = 26;      /* px of the list, at each end, that count as "past it" */
const PQ_SLOW = 130;     /* px per second the moment you get there */
const PQ_FAST = 1000;    /* px per second it will not go beyond */
const PQ_RAMP = 900;     /* ms held before it reaches full speed */

/**
 * THE QUEUE, AND DRAGGING A MAN UP IT (Tim, 7 September: "picklist should allow for click/tap and
 * drag of the players to rearrange — and if held below the bottom border, begin scrolling the list
 * down, slowly speeding up that scroll, the longer it's down there, then immediately stopping the
 * scroll when it's dragged back into the visible list. provide visual indication of where the
 * dragged player will be dropped").
 *
 * HTML5 `draggable` did the reordering before. It does not exist on touch, which is where nine of
 * ten managers will be on the night, and it cannot scroll a box it is not aware of. This is
 * pointer events, so a finger and a mouse are one code path.
 *
 * **Starting.** A mouse drags as soon as it has moved four pixels with the button down. A finger
 * must HOLD for 240ms first, because the same gesture at the same place also scrolls the list —
 * `touch-action: pan-y` leaves the flick to the browser, and if the browser claims the gesture it
 * sends `pointercancel` and the press is abandoned. Hold, then move, which is what a phone has
 * meant by "pick this up" for fifteen years.
 *
 * **The edges.** Past either end the list scrolls itself, from 2.2px a frame to 17px over nine
 * tenths of a second — slow enough at first to nudge one row, fast enough after a moment to cross
 * twenty. The ramp restarts from zero the moment the finger comes back inside, so returning stops
 * it dead rather than gliding on. The top edge does the same upwards; the ask named the bottom,
 * but a list you can only drag one way is half a control.
 *
 * **Where it will land.** The row it would drop above wears a 3px accent line on that edge — the
 * gap between two rows, not a highlight on one, because the answer is a position and not a
 * player. The line is recomputed every frame while the list scrolls under a finger that is not
 * moving.
 */
function PickQueue({ items, readonly, onMove, onRemove, onPick, canPick, pickSay }: {
  items: PickListEntry[];
  readonly: boolean;
  onMove: (from: number, to: number) => void;
  onRemove: (player: string) => void;
  /**
   * DRAFT HIM FROM THE LIST HE IS ON (Tim, 8 September: "add plusses on the left side of the
   * picklist players"). The whole point of keeping a list is that when the clock comes round you
   * take the top of it — and until now that meant switching back to the ranking and finding him
   * there again. Same button, same place in the row, same rules as the ranking's.
   */
  onPick?: ((player: string) => void) | null;
  canPick?: boolean;
  pickSay?: string;
}) {
  const ref = useRef<HTMLOListElement | null>(null);
  const { edge, measure } = useFadeEdges(ref);
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);
  /* The live gesture, off React: a drag reads and writes these forty times a second. */
  const live = useRef({ from: -1, to: -1, y: 0, since: 0, last: 0, frame: 0, armed: false });

  /** Where the pointer sits, as an insertion index: the first row whose middle is below it. */
  const indexAt = useCallback((y: number) => {
    const el = ref.current;
    if (!el) return 0;
    const rows = [...el.querySelectorAll<HTMLLIElement>('li')];
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i]!.getBoundingClientRect();
      if (y < r.top + r.height / 2) return i;
    }
    return rows.length;
  }, []);

  const step = useCallback(() => {
    const el = ref.current;
    const g = live.current;
    if (!el || !g.armed) { g.frame = 0; return; }
    const box = el.getBoundingClientRect();
    const past = g.y > box.bottom - PQ_EDGE ? 1 : g.y < box.top + PQ_EDGE ? -1 : 0;
    /*
     * An edge with nothing behind it is not an edge. Picking up the top row puts the finger inside
     * the upper zone by construction, and without this the ramp would spend that whole time
     * winding up against a scrollTop of zero — so the moment it DID have somewhere to go it would
     * leave at full speed.
     */
    const room = past > 0 ? el.scrollHeight - el.clientHeight - el.scrollTop : el.scrollTop;
    const now = performance.now();
    const dt = Math.min(64, now - (g.last || now));
    g.last = now;
    if (past === 0 || room < 1) {
      /* Back inside, or there is nothing that way: the ramp is not paused, it is forgotten. */
      g.since = 0;
    } else {
      if (!g.since) g.since = now;
      const t = Math.min(1, (now - g.since) / PQ_RAMP);
      el.scrollTop += past * (PQ_SLOW + (PQ_FAST - PQ_SLOW) * t) * (dt / 1000);
    }
    const to = indexAt(g.y);
    if (to !== g.to) { g.to = to; setDrag({ from: g.from, to }); }
    g.frame = requestAnimationFrame(step);
  }, [indexAt]);

  const onPointerDown = (e: React.PointerEvent<HTMLLIElement>, i: number) => {
    if (readonly || e.button !== 0) return;
    /* The arrows and the × are buttons on this row; pressing one is not picking the row up. */
    if ((e.target as HTMLElement).closest('button')) return;
    const row = e.currentTarget;
    const startY = e.clientY;
    const touch = e.pointerType !== 'mouse';
    /*
     * A MOUSE PRESS ON A ROW IS NOT THE START OF A SELECTION. Left un-prevented it is, and
     * Chromium then runs its OWN auto-scroll on the box while the button is held past the edge —
     * measured at almost exactly double the intended speed, because both were scrolling it. A
     * finger is left alone here: `touch-action: pan-y` still owes it a flick.
     */
    if (!touch) e.preventDefault();
    const g = live.current;
    g.from = i; g.to = i; g.y = startY; g.since = 0; g.last = 0; g.armed = false;

    let hold = 0;
    /*
     * ONCE IT IS PICKED UP, THE LIST STOPS SCROLLING ITSELF (Tim, 8 September: "picklist tap and
     * hold to rearrange doesn't highlight text anymore (great!) but does not reliably follow the
     * finger drag to a new position").
     *
     * `preventDefault` on a POINTER move does not stop a touch scroll — the browser's panning is
     * the default action of `touchmove`, and the pointer event is a separate stream reported
     * alongside it. So the row was picked up, the finger moved, `touch-action: pan-y` handed the
     * gesture to the scroller, and the drag died on the `pointercancel` that follows. Preventing
     * `touchmove` itself is what holds the gesture, and it can only be done from a non-passive
     * listener added before the move arrives — which is what arming is for. The list keeps
     * `pan-y`, so a flick that never becomes a hold still scrolls it.
     */
    const holdScroll = (ev: TouchEvent) => { if (ev.cancelable) ev.preventDefault(); };
    const arm = () => {
      if (g.armed) return;
      g.armed = true;
      try { row.setPointerCapture(e.pointerId); } catch { /* the row may have gone */ }
      if (touch) window.addEventListener('touchmove', holdScroll, { passive: false });
      setDrag({ from: i, to: i });
      g.frame = requestAnimationFrame(step);
    };
    if (touch) hold = window.setTimeout(arm, 240);

    const move = (ev: PointerEvent) => {
      g.y = ev.clientY;
      if (!g.armed) {
        if (touch) { if (Math.abs(ev.clientY - startY) > 9) { window.clearTimeout(hold); off(); } return; }
        if (Math.abs(ev.clientY - startY) > 4) arm(); else return;
      }
      ev.preventDefault();
    };
    const done = (commit: boolean) => {
      window.clearTimeout(hold);
      off();
      if (g.frame) cancelAnimationFrame(g.frame);
      g.frame = 0;
      const { from, to, armed } = g;
      g.armed = false;
      setDrag(null);
      if (!armed || !commit) return;
      /* An insertion index counts the row itself while it is still in the list. */
      const at = to > from ? to - 1 : to;
      if (at !== from) onMove(from, at);
    };
    const up = () => done(true);
    const cancel = () => done(false);
    function off() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('touchmove', holdScroll);
    }
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
  };

  return (
    <div className={`pqwrap${edge}${drag ? ' sorting' : ''}`}>
      <ol className="pqlist" ref={ref} onScroll={measure}>
        {items.map((q, i) => {
          const { last, first } = splitName(q.player);
          const mark = drag && drag.to === i ? ' dropbefore'
            : drag && drag.to === items.length && i === items.length - 1 ? ' dropafter' : '';
          return (
            <li key={q.player}
              className={`${drag?.from === i ? 'lifting' : ''}${mark}`}
              onPointerDown={(e) => onPointerDown(e, i)}>
              <span className="pqn">{i + 1}</span>
              {onPick && (
                <button type="button" className="bapick" disabled={!canPick}
                  title={canPick ? 'Pick this player' : pickSay ?? 'Not your pick to make'}
                  aria-label={canPick ? `Pick ${q.player}` : `${q.player} — not your pick to make`}
                  onClick={(e) => { e.stopPropagation(); onPick(q.player); }}
                  onPointerDown={(e) => e.stopPropagation()} />
              )}
              {/*
                THE SAME NAME THE PLAYER LIST DRAWS (Tim, 7 September), in the queue's own ink:
                surname in caps, given name after it. One reading for one player, on both tabs.
              */}
              <span className="baname" title={q.player}><b>{last}</b> <i>{first}</i></span>
              {q.position && <span className={`poschip ident pos-${q.position}`}>{q.position}</span>}
              <NflMark player={q.player} />
              {/*
                NO REORDERING FROM THE SANDBOX. Adding is allowed from the ranking's star, because
                a rehearsal is where a man finds out he wants somebody — but the rows in here are a
                FILTERED view, with whoever this mock has taken left out, so an arrow or an × would
                be aimed at a list that is not the one on the screen. Order and removal stay on the
                live board, where the list is whole.

                The arrows stay beside the drag. Dragging is the fast way and the only way on a
                phone to move a man five places; an arrow is the only way to move him one without
                aiming, and it is the one a keyboard can reach.
              */}
              {!readonly && (<>
                {/* The carets are drawn, like the plus and the ×; the labels carry the meaning. */}
                <span className="pqmove">
                  <button type="button" className="pqarrow up" disabled={i === 0}
                    title={`Move ${last} up`} aria-label={`Move ${last} up`}
                    onClick={() => onMove(i, i - 1)} />
                  <button type="button" className="pqarrow down" disabled={i === items.length - 1}
                    title={`Move ${last} down`} aria-label={`Move ${last} down`}
                    onClick={() => onMove(i, i + 1)} />
                </span>
                {/* The mark is two bars from the button's own pseudo-elements, like the plus. */}
                <button type="button" className="pqx" title={`Take ${last} off your list`}
                  aria-label={`Take ${last} off your list`}
                  onClick={() => onRemove(q.player)} />
              </>)}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function DraftBoard(
  { gm, actor, role, store, mock = false, onGoMock, onGoLive, onResetMock }:
  {
    gm: string; actor: string; role: Role; store: LeagueStore; mock?: boolean;
    /**
     * Navigation lives in the shell, so the board is handed a way to reach the sandbox and back.
     * Both directions matter: a three-way toggle you can only travel one way along is a trapdoor.
     */
    onGoMock?: () => void;
    onGoLive?: () => void;
    /**
     * Throw the sandbox away and reseed it from the live board.
     *
     * The mock used to announce itself in a banner above the page — how many keepers it had
     * seeded, from how many slates, and a Reset button — which pushed the board down by a row
     * of chrome that said nothing you could act on. The one control on it belongs with the
     * other controls, so it sits in the commissioner's bar with Undo and Redo.
     */
    onResetMock?: () => void;
  },
) {
  const [draft, setDraft] = useState<DraftState | null>(null);

  const [declarations, setDeclarations] = useState<KeeperDeclaration[]>([]);
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  const [saved, setSaved] = useState<Record<number, { player: string; pos?: string; team?: string; bye?: number | null; madeBy?: string; madeAt?: string }>>({});
  const [bank, setBank] = useState<PlayerBank>(BUNDLED_BANK);
  /**
   * Ten columns of sixteen rounds is not usable on a phone, and on draft night what a manager
   * actually wants there is his own column and the clock. So narrow screens open on the by-team
   * view; the grid is still one tap away and scrolls sideways.
   */
  /*
   * 8b: "Opens on your own column, as the app already does under 760px." It half did — it opened
   * the TEN-card teams view, which is your card plus nine others to scroll past. `mine` is the
   * drawn phone default: one card of your picks, one of your needs.
   */
  const [view, setView] = useState<'grid' | 'team' | 'mine' | 'avail'>(
    typeof window !== 'undefined' && window.innerWidth < 760 ? 'mine' : 'grid',
  );
  const [stage, setStage] = useState<'main' | 'farm'>('main');
  /**
   * Whether the rail is on screen. Open by default — the pick panel lives there — and the one
   * control that gives the grid the whole 1400 when somebody wants to read the room's board.
   */
  const [railOpen, setRailOpen] = useState(true);
  /*
   * ONE RAIL WIDTH FOR ALL THREE BOARDS (Tim, 8 September). Owned here and handed to the farm
   * screen, because live, mock and the farm rounds are the same rail doing the same job and a
   * width that reverted when you crossed between them is a setting you stop using.
   */
  const [railW, setRailW] = useRailWidth();
  /**
   * Which pane of the rail's Reference card is open (17a rev C).
   *
   * Roster leads and is the default: the question a GM asks between his own picks is "what have
   * I got", and the four tabs read as one card only if the first of them is about him. Room is
   * still one tap away and still carries its dot when a cover changes.
   */
  const [refTab, setRefTab] = useState<RefTab>('roster');
  /** The wall: the board and the farm rounds alone, full screen, for the TV in the room. */
  const [wall, setWall] = useState(false);
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState<number | null>(null);
  /** The man chosen but not yet committed (11b). Live board only; the mock enters on one tap. */
  const [pending, setPending] = useState<{ name: string; pos?: string; team?: string } | null>(null);
  /** Why a pick was refused — a race somebody else won. Shown where the button is, not swallowed. */
  const [pickRefused, setPickRefused] = useState<string | null>(null);

  const privileged = atLeast(role, 'commissioner');

  /*
   * One second, only while a pick clock is actually configured. Without a clock the bar uses
   * `ElapsedClock`, which owns its own timer and repaints one span instead of the whole board.
   */
  const [now, setNow] = useState(() => Date.now());
  const hasPickClock = Boolean(draft?.status === 'live' && settings?.clocks?.pickBands?.length);
  useEffect(() => {
    if (!hasPickClock) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasPickClock]);

  useEffect(() => {
    let live = true;
    const load = async () => {
      const [d, decls, picks, saved, st] = await Promise.all([
        store.draftState(SEASON), store.declarations(SEASON),
        store.picks(SEASON), store.playerBank(SEASON), store.settings(SEASON),
      ]);
      if (!live) return;
      setDraft(d);
      setSettings(st);
      setDeclarations(decls);
      if (saved) setBank(saved);
      setSaved(Object.fromEntries(picks.map((p) => [p.overall, {
        player: p.player ?? '', pos: p.position, team: p.nflTeam, madeBy: p.madeBy,
        madeAt: p.madeAt,
      }])));
    };
    void load();
    return store.subscribe(SEASON, (what) => {
      if (what === 'picks' || what === 'draftState' || what === 'declarations'
        || what === 'playerBank' || what === 'settings') {
        void load();
      }
    });
  }, [store]);

  /**
   * Before the lottery is published there is still a board worth looking at — the one you get if
   * the draw goes to form and nobody exercises an option. That is not a guess: a chalk lottery
   * gives the top four choosing positions to the four worst finishers, which is the same
   * worst-first order everyone else already follows, and a league where nobody submits a
   * preference drafts in exactly that order (2020-II-1 §6).
   *
   * It is clearly labelled and read-only. Being able to see where your keepers land, and which
   * picks you actually hold, is most of what people want the board for in August — but entering
   * a real pick against an order that has not happened yet would be worse than an empty page.
   */
  /*
   * ONE PROJECTION, SHARED WITH HOME (see `projectedOrder`). This used to chalk from
   * `LAST.standings` — the finish order with the playoff places on top — while Home chalked from
   * `LOTTERY_BASIS`, the regular-season table the odds are actually keyed to. The two differ for
   * everybody below the playoff line, so the same manager was shown two different projected picks
   * depending on which screen he was on, and the farm rounds ran their serpentine against this
   * one without saying which it was.
   */
  const { order, projected } = projectedOrder(draft, TEAMS);

  /**
   * Hypothetical trades, mock only.
   *
   * A mock without trades answers "who falls to me at 3.04" but not "what if I move up", which is
   * the half of the question that actually costs FAAB. Each entry moves specific PICKS — round
   * plus the seat they originally belonged to, which is how the board identifies a slot — so a
   * three-for-one is expressible and the keeper placement re-runs against the new ownership.
   * State lives here rather than in the sandbox store because it is a view of a hypothetical,
   * not something even the throwaway store should think is real.
   */
  const [mockTrades, setMockTrades] = useState<MockTrade[]>([]);
  /**
   * The manager's guesses at everybody else's keepers. Saved, and private to him — see
   * `firestore.rules`, where this is the one collection no other manager may read.
   */
  const [mockKeeperSet, setMockKeeperSet] = useState<MockKeeperSet | null>(null);
  /*
   * THE LEDGER PLUS EVERY TRADE SETTLED SINCE, not the build-time file. A pick sold on the Trades
   * page used to stay in the seller's column here until somebody regenerated the data and
   * deployed; now the board reads what the league actually holds. The mock layers its own
   * hypothetical swaps over that, as before.
   */
  const { ownership: real } = useOwnership(store);
  const ownership = useMemo(
    () => (mock && mockTrades.length ? applyPickTrades(real, mockTrades) : real),
    [mock, mockTrades, real],
  );

  /*
   * A carried keeper is dropped by Esc, the same as everywhere else that carries something. It
   * also clears the moves when the board switches between live and sandbox: pins made against a
   * hypothetical slate mean nothing against the real one, and carrying them across would put
   * keepers on picks nobody moved them to.
   */
  useEffect(() => {
    setKeeperPins({});
    setDropped({});
    setKeeperUndo([]);
    setHeldKeeper(null);
    setBlocked(null);
    setDropNote(null);
    /*
     * AND OFF THE FARM BOARD. Today the mock is a separate route, so this board remounts on
     * `main` and the line never fires. It is here because the day somebody makes the mock a
     * toggle on a mounted board instead, the farm tab would otherwise still be showing when the
     * sandbox came on — which is the exact trap the tab was hidden to close.
     */
    if (mock) setStage('main');
  }, [mock]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setHeldKeeper(null);
      setDropNote(null);
      setBlocked(null);
      setConfirmDrop(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Read only in practice mode — the live board has no use for a guess. */
  useEffect(() => {
    if (!mock) { setMockKeeperSet(null); return undefined; }
    let live = true;
    const load = () => store.mockKeepers(SEASON, gm).then((m) => { if (live) setMockKeeperSet(m); });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'mockKeepers') void load(); });
    return () => { live = false; off(); };
  }, [mock, store, gm]);

  /**
   * Which keeper landscape the board is built on.
   *
   * THE LIVE BOARD ONLY EVER USES SUBMITTED DECLARATIONS. In practice mode it uses the manager's
   * own guesses instead — that is the entire point of guessing at them — synthesised into the
   * same declaration shape so the two paths share one resolver. His own team is taken from his
   * real slate either way (see `boardKeepers`), because it is the one team he cannot be wrong
   * about and a sandbox that mis-places his own keepers is worse than no sandbox.
   */
  /** Farm graduates nobody may decline, by GM — the same list the guess card locks on. */
  const mandatoryByGm = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const k of KEEPERS) {
      if (!k.mandatory) continue;
      const who = (GM_OF[k.team] ?? k.team).toUpperCase();
      (out[who] ??= []).push(k.player);
    }
    return out;
  }, []);

  const keeperSource = useMemo((): KeeperDeclaration[] => {
    const submitted = declarations.filter((d) => d.status === 'submitted');
    if (!mock || !mockKeeperSet) return submitted;
    return Object.entries(boardKeepers(mockKeeperSet, declarations, gm, mandatoryByGm)).map(([who, rows]) => ({
      season: SEASON,
      gm: who,
      status: 'submitted' as const,
      updatedAt: mockKeeperSet.updatedAt,
      keepers: rows.map((r) => ({
        player: r.player, franchiseTagged: r.franchiseTagged, elevatedTo: r.elevatedTo,
      })),
    }));
  }, [declarations, mock, mockKeeperSet, gm, mandatoryByGm]);

  /**
   * Keepers moved by hand on this board, gm -> player -> round.
   *
   * NOT THE BOARD'S STATE — a layer over the declarations the board is derived from. In the
   * sandbox it lives and dies here. On the live board a commissioner's move is written back to
   * the declaration as `elevatedTo`, which is the field that already means exactly this, so the
   * board stays a pure function of the slates and nobody has to reconcile two records of where
   * a keeper sits.
   */
  const [keeperPins, setKeeperPins] = useState<Record<string, Record<string, number>>>({});
  /**
   * Keepers taken off a slate here, gm -> players. The other half of a blocked move: when two
   * keepers want one pick and no escalation exists, somebody stops being kept, and that is a
   * removal from the declaration rather than a pin on it.
   */
  const [droppedKeepers, setDropped] = useState<Record<string, string[]>>({});
  /** The keeper currently being carried: which player, whose team, and from which round. */
  const [coverFor, setCoverFor] = useState('');
  const [coverBy, setCoverBy] = useState('');
  const [heldKeeper, setHeldKeeper] = useState<
    { player: string; gm: string; round: number } | null
  >(null);
  /** Why the last drop was refused. Cleared by the next thing the manager does. */
  const [dropNote, setDropNote] = useState<string | null>(null);

  const contexts = useMemo(
    () => keeperContexts(keeperSource, ownership), [keeperSource, ownership],
  );

  /*
   * `liveBoard` in the domain does the same job for Home and anywhere else that needs the board
   * the room sees; the sandbox's own contexts are layered here, which is why this one composes
   * the pieces rather than calling it. The two must not drift, so `tests/liveBoard.test.ts`
   * holds them to the same result on the same inputs.
   */
  const { board, errors } = useMemo(() => {
    if (!order.length) return { board: [] as BoardPick[], errors: [] as string[] };
    const empty = buildBoard(order, ROUNDS, ownership);
    const withKeepers = applyKeepers(empty, keeperAssignments(contexts, keeperPins, droppedKeepers));
    const final = withKeepers.board.map((p) => {
      const s = saved[p.overall];
      return s?.player ? { ...p, ...s, keeper: false } : p;
    });
    return { board: final, errors: withKeepers.errors };
  }, [order, contexts, keeperPins, droppedKeepers, saved, ownership]);

  /**
   * THE OVERRIDE MARKER (Pass 17 rev B, 17n). A keeper standing somewhere other than the pick the
   * rules would have charged him to, because his GM moved him (2015-III-7, earlier never later),
   * carries an amber MOVED pill with the sentence on its tooltip. "Moved" means the declaration
   * pins him (`elevatedTo`) to a round that is not his cost round; the resolver's own escalation
   * is not a move and is not marked.
   */
  const movedMarks = useMemo(() => {
    const out = new Map<number, string>();
    for (const p of board) {
      if (!p.keeper || !p.player) continue;
      const who = p.owner.toUpperCase();
      const decl = declarations.find((d) => d.gm.toUpperCase() === who);
      const k = decl?.keepers.find((x) => x.player === p.player);
      const row = KEEPERS.find((x) => x.player === p.player && (GM_OF[x.team] ?? x.team.toUpperCase()) === who);
      const cost = k?.franchiseTagged ? row?.tagCost ?? null : row?.cost ?? null;
      const pin = keeperPins[who]?.[p.player] ?? k?.elevatedTo ?? null;
      if (pin === null || cost === null || pin === cost) continue;
      out.set(p.overall, `Moved by ${managerShort(who)} to ${pickCode(p, TEAMS)} from his Rd ${cost} cost. `
        + 'A keeper may be charged to an earlier pick his GM holds, never a later one (2015-III-7).');
    }
    return out;
  }, [board, declarations, keeperPins]);

  /**
   * Keepers held under the FRANCHISE TAG — the green `T` badge on the cell (17a rev C).
   *
   * Read off the declaration rather than the board, because a tag is a property of the
   * declaration: the resolver charges a tagged keeper his `tagCost` and the board only sees
   * where he landed. 2025 P05 §3 confines the tag to rounds 3–9, which is exactly why the room
   * looks for it on the board — the band rail and the badge answer the same question together.
   */
  const taggedMarks = useMemo(() => {
    const out = new Set<number>();
    for (const p of board) {
      if (!p.keeper || !p.player) continue;
      const who = p.owner.toUpperCase();
      const decl = declarations.find((d) => d.gm.toUpperCase() === who);
      if (decl?.keepers.find((x) => x.player === p.player)?.franchiseTagged) out.add(p.overall);
    }
    return out;
  }, [board, declarations]);

  /*
   * A TRADE IN FLIGHT, ON THE BOARD (Pass 17 rev B, the trade call). A draft-day trade's window
   * is a position on the board (2017-II-3): it closes when the first pick in it is made. The
   * Trades page is the full view; the board shows that one exists, what it touches, and how many
   * picks until it closes, and marks the two cells.
   */
  const [liveTrades, setLiveTrades] = useState<LiveTradeDoc[]>([]);
  useEffect(() => {
    if (mock) return undefined;
    let live = true;
    const load = () => void store.liveTrades?.(SEASON).then((t) => { if (live) setLiveTrades(t); }).catch(() => {});
    load();
    const off = store.subscribe(SEASON, (w) => { if (w === 'liveTrades') load(); });
    return () => { live = false; off(); };
  }, [store, mock]);

  const clock = useMemo(() => onTheClock(board), [board]);

  /*
   * Every pick that may be entered right now, not just the first.
   *
   * A clock that runs out does not forfeit the turn and does not stop the draft: the next man
   * picks on his own time and the one who lapsed keeps his. So the board can have several cells
   * live at once, and the strip has to say so.
   *
   * `since` is when the last pick was actually made, which is the only start the data has. The
   * mock has no clocks and no shared state, so it keeps the single-cell behaviour.
   */

  /**
   * NOBODY COVERS ANYBODY IN THE SANDBOX (Tim, 6 September: "no GM is covered by another in the
   * mock draft").
   *
   * A cover is an arrangement between two people about the real night — Amit is in a car, Rajan
   * types for him. In a mock the man at the keyboard is playing all ten teams himself, so there
   * is nobody to cover and nobody to be covered by: "Amit is covering you" on a board only you
   * can see is a sentence about a room that is not there. The map is emptied for everything the
   * sandbox draws and everything it permits.
   *
   * `liveDesignates` stays for the commissioner's own controls. The designates dialog writes the
   * real map back, and it can be opened while the switch is on — building the next map out of an
   * emptied one would clear every cover in the league from inside a rehearsal.
   */
  const liveDesignates = draft?.designates ?? {};
  const designates = mock ? {} : liveDesignates;
  /**
   * TWO IDENTITIES, AND THE SCREEN USES BOTH.
   *
   * `actor` is the person at the keyboard: who is allowed to enter this pick, whose name goes on
   * it, who somebody is covering for. `mine` is the team the screen is ABOUT — and under `view
   * as` those are different people on purpose, because the whole point of viewing as Luke is to
   * see what Luke sees.
   *
   * `My team` was reading `actor`, so an admin looking at the app as Luke got Luke's name in the
   * header and Tim's keepers in the list. Anything that answers "whose is this" now reads
   * `mine`; anything that answers "may I" still reads `actor`.
   */
  const mine = gm;

  /*
   * WHOSE PERMISSIONS, AND WHOSE NAME — two different people under `view as`, and the banner
   * promises both: "you see and can do exactly what they can" and "anything you save is recorded
   * against your name, not theirs". So the permission check reads the man being acted AS, and
   * the attribution on the pick still reads the man at the keyboard.
   *
   * It read `actor` for both, which meant Tim acting as Luke — with Luke's manager access, so
   * without his own commissioner powers — could not enter Luke's own pick. The panel told him
   * the pick belonged to Luke and to go and ask him.
   */
  const actorInfo = { gm: mine, privileged, designates };
  /**
   * Distinguish "it is my pick" from "I am allowed to type here". A commissioner can enter any
   * pick, but painting all 160 cells as his would make the board unreadable and the clock
   * meaningless — so ownership drives the highlight and privilege only unlocks the keyboard.
   */
  // In a mock you are playing all ten teams, so every open pick is "yours" for highlighting.
  const ownsPick = (p: BoardPick) => (mock || canEnter(p, { ...actorInfo, privileged: false }))
    // Nothing is "your pick" on a board nobody can type into.
    && (!projected || mock);
  const myTurn = clock ? ownsPick(clock) : false;
  // A commissioner defaults to the clock as well — he is usually the one running the room and
  // typing for whoever is arguing about their pick.


  const taken = useMemo(() => board.filter((p) => p.player).map((p) => p.player!), [board]);

  /** One spelling per man, for matching ADP names against bank and board names. */
  const nameKey = (s: string) => s.normalize('NFKD').toLowerCase().replace(/[^a-z ]/g, '')
    .replace(/\b(jr|sr|ii|iii|iv|v)\b/g, '').replace(/\s+/g, ' ').trim();

  /* ==========================================================================================
   * THE PICKLIST (Tim, 6 September).
   *
   * A GM cannot enter a pick before his turn — see `canEnterTarget` — so the thing he actually
   * wants while five other people are picking is somewhere to put the four names he is deciding
   * between. That is this: his own ordered queue, private to him, reorderable, and it NEVER
   * picks for him. When his turn opens and the top of the list is still on the board he is
   * offered it and the board waits.
   *
   * Kept in the store rather than in this browser, because a manager who queues four names on
   * his laptop and then picks from his phone has not changed his mind. Private by the rules,
   * for the plainest possible reason: a queue the room can read is a list of the players
   * everybody else should take first.
   * ========================================================================================== */
  /**
   * THE PANEL'S OWN FIELD, for the people who enter other men's picks.
   *
   * Tim, 6 September: "restore search at top for commissioners and GMs picking for others, so
   * that there's no confusion with the personal picklist below". A commissioner typing into the
   * card that also holds HIS queue, while entering RAJAN's pick, is one keystroke from starring
   * a player onto his own list by mistake. So the men who pick for other people get a field in
   * the panel that does one thing, and it keeps its own text — two fields sharing one string
   * would move each other's cursor.
   */
  const [panelQuery, setPanelQuery] = useState('');

  const [queue, setQueue] = useState<PickListEntry[]>([]);
  /**
   * THE LIST AS IT IS THIS INSTANT, not as it was when the last render closed over it.
   *
   * Three stars tapped in quick succession are three calls in one tick, and each one read the
   * same stale `queue` — so the first two vanished and only the last man was written. A ref is
   * updated by the mutator itself, so the second call sees what the first did.
   */
  const queueRef = useRef<PickListEntry[]>([]);
  /**
   * WHOSE LIST IS IN `queue` RIGHT NOW (Tim, 8 September: "when I switch GMs to impersonate,
   * sometimes it tries to queue players from my picklist and if I select, it's selected for the
   * impersonated GM").
   *
   * A picklist is loaded asynchronously and the actor can change under it. For the render or two
   * between "I am now acting as John" and John's list arriving, `queue` still held TIM's — and
   * two things read it without asking whose it was. The offer threw Tim's top name at John's open
   * pick under the words TOP OF YOUR LIST, and pressing it drafted Tim's man onto John's roster.
   * Worse and quieter: the prune effect writes the list back, so a name going off Tim's list
   * while the actor was John would have saved Tim's list to John's document.
   *
   * So the list carries the manager it belongs to, and nothing reads or writes it until the two
   * agree. Null while a load is in flight, which is the state that used to be invisible.
   */
  const [queueGm, setQueueGm] = useState<string | null>(null);
  /** Which pick numbers this browser has already offered the top of the list for. */
  const [offered, setOffered] = useState<Set<number>>(() => new Set());
  /** The offer waiting on screen: whose pick, and who is at the top of the list. */
  const [queueOffer, setQueueOffer] = useState<{ pick: BoardPick; entry: PickListEntry } | null>(null);
  /**
   * WHICH LIST THE CARD IS SHOWING (Tim, 7 September: "use tabs to switch between the two on that
   * card instead of the slider").
   *
   * The two lists stacked in one card and a handle between them traded height that neither could
   * spare; they are two views of the same question instead — what there is, and what I have
   * decided about it — so they are two tabs. The board is the default because it is where a name
   * is found; the queue is one tap away and says on its own tab how many are in it.
   */
  const [pqTab, setPqTab] = useState<'board' | 'mine'>(() => {
    try { return localStorage.getItem(PQ_TAB_KEY) === 'mine' ? 'mine' : 'board'; } catch { return 'board'; }
  });
  const showTab = (t: 'board' | 'mine') => {
    setPqTab(t);
    try { localStorage.setItem(PQ_TAB_KEY, t); } catch { /* private mode */ }
  };

  useEffect(() => {
    let alive = true;
    /*
     * EMPTY IT FIRST. The actor changed; whatever is in here belongs to the man he was a moment
     * ago, and everything downstream — the offer, the star, the prune's write-back — would treat
     * it as this one's until the load lands.
     */
    queueRef.current = [];
    setQueue([]);
    setQueueGm(null);
    setQueueOffer(null);
    setOffered(new Set());
    const load = async () => {
      /*
       * READ IN A MOCK, NEVER WRITTEN FROM ONE (Tim, 6 September: "using any players added to
       * the live picklist, but a view that's affected by the mock draft board instead"). The
       * list a manager keeps is the same list whichever board he is looking at; what changes in
       * the sandbox is who is still on it, and that is a filter rather than an edit.
       */
      const who = mine.toUpperCase();
      const l = await store.pickListFor(SEASON, mine as never);
      if (!alive) return;
      queueRef.current = l?.players ?? [];
      setQueue(queueRef.current);
      setQueueGm(who);
    };
    void load();
    return () => { alive = false; };
  }, [store, mine]);

  /* A write from another browser reloads it — but only ever into the actor it belongs to. */
  useEffect(() => store.subscribe(SEASON, (what) => {
    if (what !== 'pickList') return;
    const who = mine.toUpperCase();
    void store.pickListFor(SEASON, mine as never).then((l) => {
      if (mine.toUpperCase() !== who) return;
      queueRef.current = l?.players ?? [];
      setQueue(queueRef.current);
      setQueueGm(who);
    });
  }), [store, mine]);

  /** Every change to the list goes through here: paint at once, then write. */
  const editQueue = useCallback((fn: (prev: PickListEntry[]) => PickListEntry[]) => {
    /* Never write one man's list to another man's document — see `queueGm`. */
    if (queueGm !== mine.toUpperCase()) return;
    const next = fn(queueRef.current);
    if (next === queueRef.current) return;
    queueRef.current = next;
    setQueue(next);
    /*
     * THE SANDBOX WRITES THROUGH TO THE REAL LIST (Tim, 6 September: "allow adding to picklist
     * with the star icon while in mock mode"). It used to return here, so a star pressed in a
     * rehearsal moved the row on screen and nothing else. The mock store's `savePickList`
     * delegates to the live one, so this is the same write from either board — and the only
     * edits a rehearsal can reach are the ones a man makes on purpose: the automatic pruning
     * below is still live-only.
     */
    void store.savePickList({
      season: SEASON, gm: mine as never, players: next, updatedAt: new Date().toISOString(),
    });
  }, [store, mine, queueGm]);

  /**
   * SOMEBODY TOOK HIM, SO HE LEAVES THE LIST.
   *
   * Silently, and that is the right call: a queue that keeps drafted players is a queue you have
   * to audit before every pick, which is exactly the job it was meant to do for you. The list is
   * short enough that a name disappearing is visible.
   */
  useEffect(() => {
    if (mock || queue.length === 0) return;
    const gone = new Set(taken.map(nameKey));
    if (queue.some((q) => gone.has(nameKey(q.player)))) {
      editQueue((prev) => prev.filter((q) => !gone.has(nameKey(q.player))));
    }
  }, [taken, queue, mock, editQueue]);

  const queued = useMemo(() => new Set(queue.map((q) => nameKey(q.player))), [queue]);
  /**
   * THE LIST AS THIS BOARD SEES IT. On the live board the stored list is already pruned by the
   * effect above. In a mock nothing is pruned — the stored list belongs to the real draft — so
   * the men this rehearsal has taken are filtered out on the way to the screen and put straight
   * back the moment you leave it.
   */
  const queueView = useMemo(() => {
    if (!mock) return queue;
    const gone = new Set(taken.map(nameKey));
    return queue.filter((q) => !gone.has(nameKey(q.player)));
  }, [queue, taken, mock]);

  /** Put a man at the bottom of the list. Already there is a no-op, not a duplicate. */
  const queueAdd = useCallback((name: string) => {
    const p = bank.players.find((x) => nameKey(x.name) === nameKey(name));
    editQueue((prev) => (prev.some((q) => nameKey(q.player) === nameKey(name)) ? prev : [...prev, {
      player: p?.name ?? name, position: p?.pos, nflTeam: p?.team, bye: p?.bye ?? null,
    }]));
  }, [bank, editQueue]);

  const queueDrop = useCallback((name: string) => {
    editQueue((prev) => prev.filter((q) => nameKey(q.player) !== nameKey(name)));
  }, [editQueue]);

  /** Move one row to a new index. One primitive behind both the arrows and the drag. */
  const queueMove = useCallback((from: number, to: number) => {
    editQueue((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [row] = next.splice(from, 1);
      if (!row) return prev;
      next.splice(to, 0, row);
      return next;
    });
  }, [editQueue]);


  /** ADP consensus average per player, for ordering and for the fill. Empty map without a file. */
  const adpRank = useMemo(() => {
    if (!hasAdp()) return new Map<string, number>();
    return new Map(consensus().map((c) => [nameKey(c.name), c.adp]));
  }, []);

  /**
   * His PLACE on the consensus, 1-based — which is not his ADP.
   *
   * The Best available row printed a rounded average, so the card showed `7 · 7` and `36 · 36`
   * for two different men, which reads as a bug even though both numbers were true. A place is
   * unique by construction; the gaps in the column are the names the room has already taken,
   * which is exactly what the number is there to show.
   */
  const adpOrder = useMemo(() => {
    if (!hasAdp()) return new Map<string, number>();
    return new Map(consensus().map((c, i) => [nameKey(c.name), i + 1]));
  }, []);



  /**
   * Choosing a man and committing him are two acts on the live board — turn `11b`, and the first
   * of its "two deliberate asymmetries": a GM confirms rather than types-and-hopes, "because a
   * mistyped pick on a shared board costs the room ten minutes".
   *
   * The mock keeps the single tap. Nothing there is saved, nobody else sees it, and the mock's
   * whole value is playing sixteen rounds quickly — a confirm dialog on every pick would be a
   * tax on the one screen where a mistake costs nothing.
   */
  function choose(name: string) {
    if (!entering || !canEnterTarget) return;
    if (mock) return void makePick(name);
    const p = bank.players.find((x) => x.name === name);
    setPending({ name, pos: p?.pos, team: p?.team ?? undefined });
    setQuery('');
    return undefined;
  }

  /**
   * Write a pick.
   *
   * `cell` defaults to whatever the panel is pointing at, which is every caller but one: the
   * picklist offer names its own cell, because it is answered from a dialog and must not depend
   * on the panel having caught up with a `setTarget` in the same tick.
   */
  async function makePick(name: string, cell?: BoardPick) {
    const entering = cell ?? enteringNow;
    const canEnterTarget = cell
      ? canEnter(cell, actorInfo) && (!projected || mock)
        && (mock || privileged || openSet.has(cell.overall))
      : canEnterNow;
    if (!entering || !canEnterTarget) return;

    /*
     * THE RE-READ, and it is not belt and braces.
     *
     * A lapsed clock leaves more than one pick open at once, so two managers can be typing at the
     * same moment — and the thing they are most likely to type is the same name, because the best
     * player left is obvious to both. Last-write-wins would take both and show one man drafted
     * twice, discovered an hour and eight picks later.
     *
     * So: read the board again, ask `guardPick` whether the write is still legal, and lose loudly
     * if it is not. That narrows the race to one round trip rather than closing it, and the
     * surviving outcome is the correct one — the pick already recorded wins. A man told "Amit just
     * took him" retypes in four seconds.
     */
    if (!mock) {
      const latest = await store.picks(SEASON);
      const fresh = board.map((cell) => {
        const hit = latest.find((x) => x.overall === cell.overall && x.player);
        return hit ? { ...cell, player: hit.player } : cell;
      });
      const g = guardPick(fresh, entering.overall, name);
      if (!g.ok) {
        setPickRefused(g.why);
        setPending(null);
        return;
      }
      setPickRefused(null);
    }

    const p = bank.players.find((x) => x.name === name);
    await store.savePick({
      season: SEASON,
      round: entering.round,
      pick: entering.round % 2 === 1 ? entering.slot : TEAMS - entering.slot + 1,
      overall: entering.overall,
      gm: entering.owner,
      player: name,
      position: p?.pos,
      nflTeam: p?.team,
      isKeeper: false,
      madeAt: new Date().toISOString(),
      madeBy: actor,
    });
    setQuery('');
    setTarget(null);
    setPending(null);
    // A new pick makes the redo trail meaningless — standard, and the alternative is putting a
    // player back into a cell that now belongs to somebody else.
    setRedoStack([]);
  }

  /**
   * The fill targets, mock only: every empty pick from the clock forward that is NOT the acting
   * GM's. Filling never makes YOUR picks — the mock exists so you can make those yourself — and
   * keeper cells are excluded by construction, because a keeper cell is not empty. That is why
   * the buttons state decisions, not squares.
   *
   * THREE DECISIONS, NOT A ROUND (Tim, 8 September: "adjust options to 'Fill: - This Pick,
   * - To My Next Pick, - Entire Board'"). `round` is gone. It answered a question nobody asked
   * — a round boundary is an artefact of the grid, not a place a mock naturally stops — and the
   * two ends of the range it sat between are the ones worth having: one cell, or all of them.
   */
  const fillable = useMemo(() => {
    const none = {
      thisPick: [] as BoardPick[], toMyTurn: [] as BoardPick[], all: [] as BoardPick[],
    };
    if (!mock || !clock) return none;
    const mine = (p: BoardPick) => p.owner.toUpperCase() === gm.toUpperCase();
    const upcoming = board
      .filter((p) => p.overall >= clock.overall && !p.player)
      .sort((a, b) => a.overall - b.overall);
    const toMyTurn: BoardPick[] = [];
    for (const p of upcoming) {
      if (mine(p)) break;
      toMyTurn.push(p);
    }
    /*
     * `thisPick` is the head of that same walk rather than `clock` itself, so it is empty in the
     * one case where filling would be wrong: when the cell on the clock is the acting GM's own.
     * `all` runs to the end of the board and still steps over every one of his cells, which is
     * the whole point — it hands him a finished board with only his own picks left to make.
     */
    return { thisPick: toMyTurn.slice(0, 1), toMyTurn, all: upcoming.filter((p) => !mine(p)) };
  }, [mock, clock, board, gm]);

  /**
   * Advance the other teams from the ADP consensus — the named source, so nobody mistakes the
   * fill for a prediction of what Rajan will actually do. Each cell takes the best-ranked
   * player not yet on the board; the bank supplies the spelling and position where it knows
   * him, so a filled cell looks exactly like a typed one.
   */
  async function fill(cells: BoardPick[]) {
    if (!mock || cells.length === 0 || adpRank.size === 0) return;
    const gone = new Set(taken.map(nameKey));
    const pool = consensus().filter((c) => !gone.has(nameKey(c.name)));
    let i = 0;
    for (const cell of cells) {
      const next = pool[i++];
      if (!next) break;
      const banked = bank.players.find((x) => nameKey(x.name) === nameKey(next.name));
      await store.savePick({
        season: SEASON,
        round: cell.round,
        pick: cell.round % 2 === 1 ? cell.slot : TEAMS - cell.slot + 1,
        overall: cell.overall,
        gm: cell.owner,
        player: banked?.name ?? next.name,
        position: banked?.pos ?? next.pos,
        nflTeam: banked?.team,
        isKeeper: false,
        madeAt: new Date().toISOString(),
        madeBy: `${actor} (fill)`,
      });
    }
  }

  /*
   * NO PASS (Tim, 6 September). A commissioner could record a GM as having passed, which wrote
   * `PASSED` into the cell and closed it. That is a third state the board does not need: a
   * manager either enters a pick inside his time or his clock lapses, and a lapsed pick stays
   * his, stays open, and stays on the board until somebody types into it. The one thing passing
   * did that lapsing does not is take the pick away from him, and the league does not do that.
   */

  /**
   * Clear a pick.
   *
   * IN THE MOCK, ANYBODY. You are playing all ten teams in a sandbox nobody else can see, so
   * gating undo behind a role there protects nothing and just makes the practice board worse than
   * a pen and paper. On the live board it stays the commissioner's, because clearing a pick
   * repaints the cell for the whole room and pushes every later pick back one.
   */
  /**
   * What undo has taken off, newest first, so it can be put back.
   *
   * IN THIS BROWSER ONLY, and that is a real limit rather than an oversight: a cleared pick is
   * gone from the store, so the only record of what was in the cell is the one the client kept.
   * It empties on refresh, and it knows nothing about a pick another commissioner made from his
   * own laptop — which is why `redoLast` checks the cell is still empty before writing. Redoing
   * into a cell somebody else has since filled would silently overwrite a real pick.
   */
  type RedoEntry = {
    overall: number; round: number; slot: number; owner: string;
    player: string; pos?: string; team?: string; madeBy?: string;
  };
  /**
   * IN THIS BROWSER, BUT NOT ONLY IN THIS MOUNT (Tim, 6 September: "Undo seems to work a few
   * steps, but Redo did not redo").
   *
   * Undo is derived from the board, so it survives anything — a refresh, a trip through the
   * mock, a full-screen toggle. The redo trail was React state on a component that unmounts on
   * every one of those, so the two controls behaved completely differently and the difference
   * looked like redo being broken. It is the same browser-local record either way; keeping it in
   * `sessionStorage` just means it outlives the mount rather than dying with it. It still ends
   * with the tab, which is right: it is a record of what THIS commissioner took off.
   */
  const REDO_KEY = `ffl.redo.${SEASON}`;
  const [redoStack, setRedoStack] = useState<RedoEntry[]>(() => {
    try {
      const raw = sessionStorage.getItem(REDO_KEY);
      const v: unknown = raw ? JSON.parse(raw) : null;
      return Array.isArray(v) ? (v as RedoEntry[]) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(REDO_KEY, JSON.stringify(redoStack)); } catch { /* private mode */ }
  }, [REDO_KEY, redoStack]);

  /* -- moving a keeper by hand ------------------------------------------------------------- */

  /**
   * May this actor move keepers around, and on this board?
   *
   * Everybody in the sandbox, because it is his own hypothetical. Commissioners on the live
   * board, because a keeper sitting on the wrong pick on draft night is a thing somebody has to
   * be able to fix in the room. Not the farm board: a graduate's cost is computed a different
   * way (2013-III-1 §4) and his pick is not the GM's to choose.
   */
  const mayMoveKeepers = stage === 'main' && (mock || privileged);

  /** A keeper cell this actor may pick up. Never a farm graduate — see above. */
  function canGrabKeeper(p: BoardPick): boolean {
    return mayMoveKeepers && Boolean(p.player) && p.keeper === true && p.farm !== true;
  }

  /**
   * A cell the carried keeper could land on. Cheap and generous on purpose: this only decides
   * which cells glow. `moveKeeper` decides what actually happens, and it is the one that gets to
   * say no — a target that turns into a dialog is fine, a legal cell that never lit up is not.
   */
  function canDropKeeper(p: BoardPick): boolean {
    const h = heldKeeper;
    if (!h) return false;
    if (p.owner.toUpperCase() !== h.gm.toUpperCase()) return false;
    // A pick somebody actually made is not a slot; only empty cells and other keepers are.
    if (p.player && !p.keeper) return false;
    /*
     * NOT ONTO A FARM GRADUATE. His cost comes off the farm draft rather than the Grid
     * (2013-III-1 §4) and keeping him is mandatory, so he is not somebody to be swapped aside or
     * dropped to make room. His cell is a keeper cell like any other, which is exactly why it
     * has to be excluded here rather than left to look available.
     */
    if (p.farm) return false;
    const row = contexts[h.gm.toUpperCase()]?.rows.find((r) => r.player === h.player);
    return row !== undefined && p.round <= row.costRound;
  }

  /** A keeper's placement, as `moveKeeper` wants it: the whole team's, not just his. */
  function placementFor(gm: string): Map<string, number> {
    const key = gm.toUpperCase();
    const rows = keeperAssignments(contexts, keeperPins, droppedKeepers)[key] ?? [];
    return new Map(rows.map((r) => [r.player, r.round]));
  }

  function slateFor(gm: string) {
    const key = gm.toUpperCase();
    const ctx = contexts[key];
    const gone = new Set(droppedKeepers[key] ?? []);
    return {
      keepers: (ctx?.rows ?? [])
        .filter((r) => !gone.has(r.player))
        .map((r) => ({ player: r.player, costRound: r.costRound })),
      ownedRounds: ctx?.ownedRounds ?? [],
    };
  }

  /**
   * Write a team's pins down. In the sandbox that is all it is; on the live board it goes back
   * into the declaration, because a commissioner moving a keeper at 8pm has to be a thing the
   * other nine screens see.
   */
  /**
   * Everything a keeper move changed, so it can be changed back.
   *
   * The whole prior declaration rather than a description of the edit, because one of the two
   * options DELETES a keeper from another man's submitted slate and there is no reconstructing
   * a row you have already dropped. A commissioner fixing a placement at 8pm is going to get one
   * wrong, and "sorry, re-declare him" is not an answer on draft night.
   */
  const [keeperUndo, setKeeperUndo] = useState<Array<{
    gm: string;
    pins: Record<string, number> | undefined;
    dropped: string[] | undefined;
    declaration: KeeperDeclaration | null;
  }>>([]);

  /**
   * The FEWEST pins that reproduce a layout.
   *
   * Writing every keeper's round into the declaration would work and would be wrong: it freezes
   * a placement the assigner was choosing freely, so a later cost correction or trade cannot
   * re-place anybody and the slate quietly stops responding to the rules. So only the rounds
   * that differ from what the assigner would have picked on its own are kept — usually one or
   * two — and if the trimmed set does not reproduce the layout exactly, the full set is used.
   */
  function minimalPins(
    gm: string, want: Record<string, number>, drop?: string,
  ): Record<string, number> {
    const key = gm.toUpperCase();
    const slate = slateFor(key);
    const ownedRounds = slate.ownedRounds;
    /*
     * WITHOUT THE MAN BEING DROPPED. `want` is already the layout after he is gone; comparing it
     * against a baseline that still includes him would call every remaining keeper "moved" and
     * pin the whole slate — the exact outcome this function exists to avoid.
     */
    const keepers = slate.keepers.filter((x) => x.player !== drop);
    const free = assignKeeperPicks(
      keepers.map((x) => ({ id: x.player, costRound: x.costRound })), ownedRounds,
    ).assigned;

    const trimmed = Object.fromEntries(
      Object.entries(want).filter(([player, round]) => free.get(player) !== round),
    );
    const check = assignKeeperPicks(
      keepers.map((x) => ({
        id: x.player, costRound: x.costRound, preferred: trimmed[x.player] ?? null,
      })),
      ownedRounds,
    ).assigned;
    const same = Object.entries(want).every(([player, round]) => check.get(player) === round);
    return same ? trimmed : want;
  }

  async function commitPins(gm: string, want: Record<string, number>, drop?: string) {
    const key = gm.toUpperCase();
    const pins = minimalPins(key, want, drop);
    setKeeperUndo((prev) => [{
      gm: key,
      pins: keeperPins[key],
      dropped: droppedKeepers[key],
      declaration: declarations.find((x) => x.gm.toUpperCase() === key) ?? null,
    }, ...prev]);
    /*
     * THE IN-MEMORY LAYER IS THE COMPLETE LAYOUT (`want`), not the trimmed one.
     *
     * `keeperPins` sits over the declarations, and a keeper's saved `elevatedTo` is read whenever
     * the layer says nothing about him. Storing only the rounds that differ from the automatic
     * answer therefore meant that moving a keeper BACK to his natural round trimmed him out of
     * the layer — and the stale `elevatedTo` underneath, written by the move that took him away
     * in the first place, won again. The cell snapped back and it read as the app refusing a
     * legal move.
     */
    setKeeperPins((prev) => ({ ...prev, [key]: want }));
    if (drop) setDropped((prev) => ({ ...prev, [key]: [...(prev[key] ?? []), drop] }));
    if (mock) return;
    const d = declarations.find((x) => x.gm.toUpperCase() === key);
    if (!d) return;
    await store.saveDeclaration({
      ...d,
      updatedAt: new Date().toISOString(),
      /*
       * The DECLARATION still gets the trimmed set — an `elevatedTo` on every keeper freezes a
       * placement that should keep responding to a later cost correction. The other half of the
       * same bug: a keeper outside that set has his `elevatedTo` REMOVED rather than left alone,
       * or the round he was moved away from persists after he has been moved back.
       */
      keepers: d.keepers
        .filter((k) => k.player !== drop)
        .map(({ elevatedTo: _cleared, ...rest }) => (
          pins[rest.player] === undefined ? rest : { ...rest, elevatedTo: pins[rest.player] }
        )),
    });
  }

  /**
   * Has the commissioner said the destructive option twice?
   *
   * ONE CLICK IS NOT ENOUGH FOR THAT ONE. Dropping a keeper edits another manager's submitted
   * slate and changes what he is charged; it sits beside a harmless option in the same row of
   * buttons, and the two are one mis-click apart. The sandbox does not ask, because nothing
   * there is real.
   */
  const [confirmDrop, setConfirmDrop] = useState(false);

  /** Put the last keeper move back, including one that dropped somebody off a slate. */
  async function undoKeeperMove() {
    const top = keeperUndo[0];
    if (!top) return;
    setKeeperUndo((prev) => prev.slice(1));
    setBlocked(null);
    setDropNote(null);
    setHeldKeeper(null);
    setKeeperPins((prev) => {
      const next = { ...prev };
      if (top.pins) next[top.gm] = top.pins; else delete next[top.gm];
      return next;
    });
    setDropped((prev) => {
      const next = { ...prev };
      if (top.dropped) next[top.gm] = top.dropped; else delete next[top.gm];
      return next;
    });
    if (!mock && top.declaration) await store.saveDeclaration(top.declaration);
  }

  /** What a `Fit` implies for the pins, so accepting a dialog option is one call. */
  function pinsOf(fit: { placed: Map<string, number> }): Record<string, number> {
    return Object.fromEntries(fit.placed);
  }

  /**
   * Drop the carried keeper on `to`.
   *
   * The four answers are the four things that can be true, and each gets its own treatment: a
   * legal move just happens, a legal swap just happens, an illegal one says why in the rule's
   * own words, and the one that needs a decision asks for it rather than picking for him.
   */
  async function dropKeeper(to: BoardPick) {
    const held = heldKeeper;
    if (!held) return;
    setHeldKeeper(null);
    if (to.owner.toUpperCase() !== held.gm.toUpperCase()) {
      setDropNote(
        `That pick belongs to ${managerShort(to.owner)}. A keeper can only be charged to a pick `
        + 'his own GM holds.',
      );
      return;
    }
    const { keepers, ownedRounds } = slateFor(held.gm);
    const r = moveKeeper(keepers, ownedRounds, placementFor(held.gm), held.player, to.round);

    if (r.kind === 'illegal') { setDropNote(r.why); return; }
    if (r.kind === 'moved' || r.kind === 'swapped') {
      setDropNote(null);
      await commitPins(held.gm, pinsOf(r));
      return;
    }
    const stuck = fitKeepers(keepers, ownedRounds, { [held.player]: to.round });
    setConfirmDrop(false);
    setBlocked({
      gm: held.gm,
      player: held.player,
      toRound: to.round,
      ...r,
      escalationWords: r.ifEscalated
        ? movedWords(placementFor(held.gm), r.ifEscalated.placed, held.player) : '',
      bumpWords: r.ifBumped
        ? movedWords(placementFor(held.gm), r.ifBumped.placed, held.player) : '',
      stuckWords: stuck.unplaced.length
        ? `${andList(stuck.unplaced.map(said))} would have no pick left to be charged to.`
        : 'Something has to give.',
    });
  }

  /**
   * The drop that needs a decision, with both outcomes already computed AND already worded.
   *
   * Worded here rather than at render time because the sentences compare the placement before
   * the move with the one after, and once a pin is committed the "before" is gone.
   */
  const [blockedMove, setBlocked] = useState<
    {
      gm: string; player: string; toRound: number;
      escalationWords: string; bumpWords: string; stuckWords: string;
    }
    & Extract<MoveResult, { kind: 'blocked' }>
  | null>(null);

  // A modal over a sixteen-round grid: the grid does not move while the decision is up.
  useScrollLock(Boolean(blockedMove));

  async function undo(pick: BoardPick) {
    if (!mock && !privileged) return;
    const had = saved[pick.overall];
    if (had?.player) {
      setRedoStack((prev) => [{
        overall: pick.overall, round: pick.round, slot: pick.slot, owner: pick.owner,
        player: had.player, pos: had.pos, team: had.team, madeBy: had.madeBy,
      }, ...prev]);
    }
    await store.savePick({
      season: SEASON, round: pick.round, overall: pick.overall,
      pick: pick.round % 2 === 1 ? pick.slot : TEAMS - pick.slot + 1,
      gm: pick.owner, isKeeper: false, player: undefined, madeBy: actor,
      /*
        NO `madeAt` ON A CLEARED CELL. Nothing was made, so there is no moment to record — and
        the pick clock reads the newest `madeAt` on the board, which is how stamping one here
        gave the room extra time every time somebody pressed ×.
      */
      madeAt: undefined,
    });
  }

  /**
   * Put back the last thing undo took off.
   *
   * THE CHECK IS AGAINST THE STORE, NOT AGAINST THIS RENDER. `saved` is a snapshot that arrives
   * from a subscription, so pressing Undo and Redo in quick succession asked "is the cell empty?"
   * of a copy that still had the player in it, and redo refused its own undo. Reading the picks
   * back costs one round trip on an action nobody presses twice a minute.
   */
  async function redoLast() {
    const top = redoStack[0];
    if (!top || (!mock && !privileged)) return;
    setPickRefused(null);
    const fresh = await store.picks(SEASON);
    const there = fresh.find((x) => x.overall === top.overall)?.player;
    const keeperThere = board.find((p) => p.overall === top.overall)?.keeper;
    if ((there && there !== top.player) || keeperThere) {
      /* Somebody really has taken the cell. Keep the entry — the trail is still worth having. */
      setPickRefused(
        `${pickCode({ round: top.round, slot: top.slot } as BoardPick, TEAMS)} is not empty any `
        + `more, so there was nothing to put back. ${keeperThere ? 'A keeper has fallen into it.'
          : `${there} is in it now.`}`,
      );
      return;
    }
    setRedoStack((prev) => prev.slice(1));
    await store.savePick({
      season: SEASON, round: top.round, overall: top.overall,
      pick: top.round % 2 === 1 ? top.slot : TEAMS - top.slot + 1,
      gm: top.owner, player: top.player, position: top.pos, nflTeam: top.team,
      isKeeper: false, madeBy: top.madeBy ?? actor, madeAt: new Date().toISOString(),
    });
  }


  async function setDesignate(forGm: string, to: string) {
    if (!draft) return;
    const next = { ...liveDesignates };
    if (to) next[forGm.toUpperCase()] = to.toUpperCase();
    else delete next[forGm.toUpperCase()];
    await store.saveDraftState({ ...draft, designates: next, updatedAt: new Date().toISOString() });
  }

  /**
   * Start, reopen, finish.
   *
   * Nothing in the app set `draft.status` to `live` before this — the board let picks be entered
   * from `setup` and nobody noticed, because on the board it makes no difference. It makes a
   * difference everywhere else: the live strip (10a) and the Draft item's pill only exist while
   * the draft is running, and "is the draft on" is the one fact the rest of the app cannot guess.
   */
  async function setStatus(status: DraftState['status']) {
    if (!draft) return;
    const now_ = new Date().toISOString();
    /*
     * STARTING THE DRAFT STARTS THE CLOCK, which it did not.
     *
     * The pick clock counts from the newest pick's timestamp, and before the first pick there is
     * no such stamp — so a commissioner pressed Start on an empty board and the clock bar showed
     * nothing at all, which reads as "the draft is not really live". Going live stamps
     * `clockRestartedAt`, the field that already means "his time starts now", so 1.01 is on a
     * clock from the moment the room is told it is. Reopening a finished draft does the same;
     * pausing deliberately does not, because the pause is meant to stop the count.
     */
    /*
     * PAUSING IS AN INTERVAL, NOT A RESET (Tim, 6 September: "pausing the draft caused all the
     * pick timers to reset and clicking Start to unpause reset the pick clock back to the first
     * of those late picks").
     *
     * Going live used to stamp `clockRestartedAt` on every transition, which is right for a
     * draft starting and catastrophic for one resuming: it is the commissioner's "your time
     * starts now", and applying it to a board with four lapsed clocks on it wipes the only
     * record of who has been waiting and how long. So a stop opens an interval, a start closes
     * it, and only a draft that has never run gets its clock stamped.
     */
    const pauses = [...(draft.pauses ?? [])];
    const openPause = pauses.length && !pauses[pauses.length - 1]!.until
      ? pauses.length - 1 : -1;
    const stopping = status !== 'live' && draft.status === 'live';
    /*
     * ASKED TO RUN IS ASKED TO RUN (Tim, 9 September: "when the intermission came up, the clock
     * still started for the next round. that shouldn't happen").
     *
     * The intermission stops the room by opening a pause and LEAVING the status live, because the
     * board is still live — it is at its break, not shut. That left `starting` false on the press
     * that was meant to end the break: it asked whether the status was changing, and the status
     * was not. So the pause never closed and the room needed two presses to come back.
     *
     * Going live means the room runs, whatever it said a moment ago; the open interval closes
     * either way. A start that is not a change of status is still a start.
     */
    const starting = status === 'live';
    if (stopping && openPause < 0) {
      pauses.push({ at: now_, by: actor.toUpperCase() as never });
    }
    if (starting && openPause >= 0) pauses[openPause] = { ...pauses[openPause]!, until: now_ };
    /* A first start, not a resume: nothing has been paused and nothing has been entered. */
    const firstStart = starting && openPause < 0 && !draft.clockRestartedAt;
    await store.saveDraftState({
      ...draft,
      status,
      pauses,
      ...(firstStart ? { clockRestartedAt: now_, clockRestartedBy: actor.toUpperCase() } : {}),
      /*
       * AND THE MOMENT THE DRAFT BEGAN, which is not the same field. `clockRestartedAt` moves
       * every time a commissioner hands somebody his time back; this one is stamped once and
       * stays put, so "had the draft begun when that happened" has an answer all night.
       */
      ...(starting && !draft.startedAt ? { startedAt: now_, startedBy: actor.toUpperCase() } : {}),
      updatedAt: now_,
    });
  }

  /**
   * Give the man on the clock his full time back.
   *
   * Distinct from pausing, and both are needed for different rooms. Pausing stops the draft —
   * the pizza, the rules argument, the ten minutes finding whose turn it is. Restarting keeps
   * the draft running and just says "his time starts now", which is what you want when a phone
   * dropped or the previous pick took four minutes to type.
   */
  async function restartClock() {
    if (!draft || mock) return;
    const now_ = new Date().toISOString();
    await store.saveDraftState({
      ...draft, clockRestartedAt: now_, clockRestartedBy: actor.toUpperCase(), updatedAt: now_,
    });
  }

  /**
   * Which commissioner action is waiting to be confirmed, if any.
   *
   * One piece of state for all three, because only one of them can be up: they are all on the
   * same row and the dialog is modal. `null` is the ordinary state of the board.
   */
  const [confirm, setConfirm] = useState<'restart-pick' | 'restart-draft' | 'done' | 'uncover' | null>(null);
  /** True while the board is being wiped, so the dialog can say so and refuse a second press. */
  const [wiping, setWiping] = useState(false);

  /**
   * Take the board back to an empty night.
   *
   * KEEPERS AND TRADES SURVIVE, because neither is something the draft produced: a keeper is a
   * declaration and a traded pick is a deal struck weeks ago, and a commissioner restarting the
   * draft at 7.15 because the order was wrong is not undoing either of them. What goes is every
   * cell somebody typed into tonight, and the clock, and `live` — the room starts again from
   * `Start`, which is the state it was in before anybody sat down.
   */
  async function restartDraft() {
    if (!draft || mock) return;
    setWiping(true);
    try {
      const typed = board
        .filter((p) => p.player && !p.keeper)
        .sort((a, b) => b.overall - a.overall);
      for (const p of typed) {
        await store.savePick({
          season: SEASON, round: p.round, overall: p.overall,
          pick: p.round % 2 === 1 ? p.slot : TEAMS - p.slot + 1,
          gm: p.owner, isKeeper: false, player: undefined, madeBy: actor, madeAt: undefined,
        });
      }
      const now_ = new Date().toISOString();
      await store.saveDraftState({
        ...draft,
        status: 'setup',
        onTheClock: 1,
        clockRestartedAt: undefined,
        clockRestartedBy: undefined,
        startedAt: undefined,
        startedBy: undefined,
        /* A board starting over starts its clocks over: the old ledger is about a draft
           that no longer exists. */
        pauses: [],
        updatedAt: now_,
      });
      setRedoStack([]);
      setPickRefused(null);
    } finally {
      setWiping(false);
    }
  }

  /**
   * THE LAST THING THAT HAPPENED TO THE BOARD, whoever did it.
   *
   * Read off the board rather than kept in a stack, deliberately. A stack lives in one browser:
   * it empties on refresh and knows nothing about the pick another commissioner just entered
   * from his own laptop. The newest `madeAt` among cells that hold somebody is the last action
   * this board actually took — a pick made, or a pick passed, which is also a write.
   */
  const lastAction = useMemo(() => {
    let best: { pick: BoardPick; at: string } | null = null;
    for (const p of board) {
      if (!p.player || p.keeper) continue;
      const at = saved[p.overall]?.madeAt;
      if (!at) continue;
      if (!best || at > best.at) best = { pick: p, at };
    }
    return best;
  }, [board, saved]);

  /** Whether the sandbox is still available. Shut from 30 minutes before the draft. */
  /*
   * THE SANDBOX DOES NOT CLOSE (Tim, 6 September: "don't cut off the mock draft by time at
   * all"). It shut half an hour before the draft, which is exactly the half hour somebody wants
   * it — the room is assembling, the keepers are in, and the last rehearsal is the useful one.
   * Nothing in it is written anywhere shared, so there is nothing for a deadline to protect.
   */

  const made = board.filter((p) => p.player).length;
  /*
   * When the previous pick landed, for the elapsed clock on the confirm card. Read off the saved
   * picks rather than kept in state, so it is the same number the strip shows on every other
   * screen.
   */
  /**
   * WHERE THE ROOM IS, as an instant — the moment the FURTHEST pick down the board was made.
   *
   * Not the newest timestamp. Entering a delinquent GM's pick stamps `now` on a cell whose
   * window closed four minutes ago; taking the newest stamp made that the origin of every open
   * clock on the board, so filling one late pick handed everybody else a fresh two minutes and
   * dragged the clock backwards. The draft moves forward through the board, so how far it has
   * got is the furthest pick made, and that is what the man up now counts from.
   *
   * ONLY CELLS THAT HOLD A PLAYER COUNT, and that is a bug fix rather than a nicety. Clearing a
   * pick writes the cell back with no player, and this used to take the newest `madeAt` of any
   * saved row — so pressing × stamped `now`, the clock's origin jumped forward, and the room
   * watched the pick clock gain time from an undo. A cleared cell is the absence of a pick; it
   * cannot be the moment the last one was made.
   *
   * AND A COMMISSIONER'S RESTART IS NOT THIS NUMBER (Tim, 8 September: "tried to reset the pick
   * clock for the latest pick on the board but it reset the whole board").
   *
   * `clockRestartedAt` used to win here when it was the later of the two, which handed
   * `openPicks` a walk that begins at the restart — so the cascade started over from the first
   * unmade cell, four lapsed picks lost their elapsed figures, and 1.01 went back on the clock
   * with a full two minutes. The dialog had just promised the opposite: "Ari gets his full time
   * back on 1.04. The 4 picks behind him stay open and keep their elapsed time."
   *
   * The restart is a separate input to `openPicks`, and it already does exactly what the dialog
   * says — it hands the time back to the FRONT of the window and moves nothing else. Passing it
   * as the origin as well destroyed the history it was meant to leave alone. So this is the
   * furthest pick made and nothing else, with the draft's own start behind it for a board that
   * has no picks yet.
   */
  const sinceLastPick = useMemo(() => {
    let furthest: { overall: number; at: string } | null = null;
    for (const [overall, v] of Object.entries(saved)) {
      if (!v.player || !v.madeAt) continue;
      const o = Number(overall);
      if (!furthest || o > furthest.overall) furthest = { overall: o, at: v.madeAt };
    }
    if (furthest) return furthest.at;
    /*
     * A LIVE BOARD ALWAYS HAS AN ORIGIN. Before the first pick there is no `madeAt` to count
     * from, so 1.01 sat on a board that said LIVE NOW with no clock on it at all. The moment the
     * room was told it was live is the true answer; `clockRestartedAt` is what a draft that went
     * live before `startedAt` existed has instead, and the state's own `updatedAt` is the last
     * resort, only ever reached in that one gap.
     */
    if (draft?.status !== 'live') return null;
    return draft.startedAt ?? draft.clockRestartedAt ?? draft.updatedAt ?? null;
  }, [saved, draft?.startedAt, draft?.clockRestartedAt, draft?.status, draft?.updatedAt]);

  /**
   * The finished board as a spreadsheet (11b). One row per pick in draft order, which is the
   * shape the league's own Draftboard workbook has always used — so a season's board can be
   * pasted straight into the archive that every history screen in this app reads from.
   */
  function exportBoard() {
    const header = ['Overall', 'Round', 'Pick', 'Code', 'Manager', 'Player', 'Pos', 'NFL', 'Keeper'];
    const rows = [...board]
      .sort((a, b) => a.overall - b.overall)
      .map((p) => [
        p.overall, p.round, p.round % 2 === 1 ? p.slot : TEAMS - p.slot + 1,
        pickCode(p, TEAMS), managerShort(p.owner), p.player ?? '', p.pos ?? '',
        nflInfoOf(p.player ?? '')?.team ?? p.team ?? '', p.keeper ? 'K' : '',
      ]);
    downloadXlsx(`draft-${SEASON}.xlsx`, `${SEASON} Draft`, [header, ...rows],
      [8, 7, 6, 8, 12, 24, 6, 6, 7]);
  }

  /*
   * EVERY PICK THAT MAY BE ENTERED RIGHT NOW, not just the first.
   *
   * A clock running out does not forfeit the turn and does not stop the draft: the next man picks
   * on his own time, and the one whose clock lapsed keeps his and may still enter it. So the board
   * can have several cells live at once. See `openPicks`.
   *
   * The mock has no clocks and no shared state, so it keeps the single-cell behaviour it had.
   */
  /**
   * STOPPED FOR PIZZA IS NOT THE SAME AS NEVER STARTED (Tim, 8 September: "when paused, freeze
   * all clocks ... right now, it looks like the first lapsed pick is the active one when things
   * restart").
   *
   * Pausing writes `setup` back to the status, which is also what a board that has never been
   * started reads — so the whole cascade switched off. No open picks, no lapsed set, and
   * `boardClock` fell back to the first UNMADE cell, which after a lapse is the oldest red one.
   * The board came back from a pizza break drawing a man who had been passed an hour ago as the
   * one everybody was waiting for.
   *
   * An open entry in the ledger is the whole test, and it is sufficient on its own: a pause is
   * only ever written on the way OUT of `live`, so a board carrying one has been started, and a
   * board that has never been started has an empty ledger and counts nothing. Reading
   * `startedAt` as well looked more careful and was strictly worse — the field is younger than
   * some of the boards that can be paused, and a night that predates it would come back from a
   * break with the cascade still switched off.
   */
  const pausedAt = useMemo(() => {
    const open = (draft?.pauses ?? []).find((x) => !x.until);
    const t = open ? Date.parse(open.at) : NaN;
    return Number.isNaN(t) ? null : t;
  }, [draft?.pauses]);
  const paused = !mock && draft?.status !== 'complete' && pausedAt !== null;
  /**
   * The round this stop is the intermission for, or null where a person pressed the button.
   *
   * Both are pauses and the clocks cannot tell them apart, which is the point; what differs is
   * what the room is told — `Paused` is a commissioner's decision and `Intermission` is the
   * schedule's, and the button under them reads `Start` for one and `Resume` for the other.
   */
  const atBreak = useMemo(() => {
    const open = (draft?.pauses ?? []).find((x) => !x.until);
    return open?.intermissionAfter ?? null;
  }, [draft?.pauses]);
  /**
   * THE INSTANT EVERY CLOCK ON THIS SCREEN READS.
   *
   * A pause freezes the figures by freezing the TIME OF DAY they are measured against, which is
   * the only way that works: each `PickClock` owns a one-second timer counting from an origin,
   * so freezing a figure by walking its origin forward instead means handing the component a new
   * `since` every second — and the effect that owns the timer is keyed on `since`, so it tears
   * the interval down and rebuilds it before it can ever fire. The clocks stop, then run
   * backwards. One frozen `now`, read by the cascade and by every clock, and the board is simply
   * the board as it stood when somebody pressed Pause.
   */
  const clockNow = pausedAt ?? now;

  const openNow = useMemo(() => {
    if (mock || (draft?.status !== 'live' && !paused) || !sinceLastPick) return [];
    return openPicks({
      board,
      secondsFor: (round) => pickSeconds(settings?.clocks ?? null, round),
      since: Date.parse(sinceLastPick),
      now: clockNow,
      restartedAt: draft?.clockRestartedAt ? Date.parse(draft.clockRestartedAt) : null,
      pauses: (draft?.pauses ?? []).map((x) => ({
        at: Date.parse(x.at),
        until: x.until ? Date.parse(x.until) : null,
      })),
    });
  }, [mock, paused, draft, board, settings, sinceLastPick, clockNow]);

  /** The overall numbers of cells that are live only because somebody's clock ran out. */
  /**
   * How far into the draft a trade was announced, as a pick number.
   *
   * 2017-II-3 measures the counter window in picks, so the band has to say where the room was
   * when the deal was declared. That is the count of picks already made at that instant, which
   * the board itself holds — no field to store, and nothing to keep in step.
   */
  const announcedAt = useCallback((at: string) => {
    const t = Date.parse(at);
    if (Number.isNaN(t)) return made;
    /*
     * A PICK TYPED BEFORE THE DRAFT BEGAN IS NOT A PICK THE ROOM HAD MADE (Tim, 8 September:
     * "matt and john's trade was announced almost 24 hours before the draft was expected to
     * begin … not pick 43").
     *
     * Keepers and pre-loaded picks carry the wall-clock time somebody typed them, and a board
     * is routinely filled in the evening before. So a deal agreed at 6:59pm the night before
     * counted every one of them and reported a position halfway down the third round on a draft
     * that had not started — which is not a cosmetic number: 2017-II-3 §2 measures the counter
     * window from it, and eight picks short of a full round would have made a legal trade
     * illegal.
     *
     * The board is on nothing until Start, and after it only the picks made since count.
     */
    const began = Date.parse(draft?.startedAt ?? draft?.clockRestartedAt ?? '');
    if (!Number.isFinite(began) || t < began) return 0;
    /* Where the BOARD was, not how many cells were filled — "at pick 34" is a position. */
    let at_ = 0;
    for (const [overall, x] of Object.entries(saved)) {
      if (!x.player || !x.madeAt) continue;
      const m = Date.parse(x.madeAt);
      if (!Number.isFinite(m) || m > t || m < began) continue;
      if (Number(overall) > at_) at_ = Number(overall);
    }
    return at_;
  }, [saved, made, draft?.startedAt, draft?.clockRestartedAt]);

  /**
   * More than one cell is enterable right now, so the panel offers a choice rather than a name.
   * Only where the actor may actually type into more than one of them: a GM with one pick open
   * and somebody else's lapsed beside it is not choosing between them.
   */
  /** The open picks whose clocks have run out, in board order — the clock bar lists them all. */
  const lapsedNow = useMemo(() => openNow.filter((o) => o.lapsed), [openNow]);
  /** The reference card folds away; the tab row stays, so it can be brought back with one tap. */
  const [refOpen, setRefOpen] = useState(true);
  /** Whether the designates row is showing its selects. Collapsed it is just the covers. */
  const [desigOpen, setDesigOpen] = useState(false);
  /** Byes: your own weeks, or every team's worst one. */
  const [byeView, setByeView] = useState<'mine' | 'league'>('mine');
  /** Which position the Best available card is narrowed to. `All` is the ranking itself. */
  /**
   * WHICH POSITIONS THE LIST IS NARROWED TO. Empty is All (Tim, 6 September: "best available
   * filters can be multi-selected (except All)") — "the best receiver or tight end left" is one
   * question, and a single-select answered a different one.
   */
  const [baFilters, setBaFilters] = useState<string[]>([]);
  /**
   * THE PICKS THIS ACTOR MAY ACTUALLY ENTER (Tim, 6 September, signed in as Luke).
   *
   * The target chips listed every open pick on the board, so a GM with one late pick of his own
   * was offered three of other people's and told, on choosing one, that it was not his. A
   * commissioner sees them all, because entering somebody else's pick is his job; everybody
   * else sees his own and anyone he is covering for, which is exactly `canEnter`.
   */
  const myOpen = useMemo(
    () => (privileged || mock ? openNow : openNow.filter((o) => canEnter(o.pick, actorInfo))),
    [openNow, privileged, mock, actorInfo],
  );
  /*
   * The pick the panel is ABOUT, which is not the same as a pick you may enter. 11a draws a
   * watching manager the pick on the clock and says whose it is — "Not your pick · 3.04 belongs
   * to Ryan" — rather than an empty panel and a vague instruction to wait. So this falls back to
   * the clock for everybody; `canEnterTarget` below is what decides whether anything can be done
   * with it.
   */
  const enteringNow = target !== null
    ? board.find((p) => p.overall === target)
    /*
     * A GM's panel opens on HIS pick when he has one open, rather than on whatever the board is
     * doing. With four picks in play and one of them his, landing on somebody else's and being
     * told to go and ask him is the panel refusing the only thing it was opened to do.
     */
    : (!privileged && !mock ? myOpen[0]?.pick ?? clock : clock);
  /** The picks that are live this second, by overall — "may I type here YET". */
  const openSet = useMemo(() => new Set(openNow.map((o) => o.pick.overall)), [openNow]);
  /**
   * MAY I TYPE HERE, AND MAY I TYPE HERE YET (Tim, 6 September).
   *
   * `canEnter` answers the first — it is about identity, and it says yes to a manager about
   * every cell he owns, all sixteen rounds of them. That was the whole test, so a GM could
   * click his 3.08 in the first round, search, and confirm a player into it twenty-seven picks
   * before the board got there. Nothing downstream stopped him: `guardPick` protects the
   * player, not the moment.
   *
   * So a manager may only enter a pick that is OPEN — on the clock, or lapsed and still his. A
   * commissioner keeps every cell, because entering somebody else's pick out of order is his
   * job and it is logged; the mock keeps everything, because it is a sandbox.
   */
  const canEnterNow = enteringNow
    ? canEnter(enteringNow, actorInfo) && (!projected || mock)
      && (mock || privileged || openSet.has(enteringNow.overall))
    : false;
  /* The two names the JSX below reads. `makePick` shadows them when it is given a cell. */
  const entering = enteringNow;
  const canEnterTarget = canEnterNow;
  /**
   * He may enter this cell, but the board has not reached it — a different sentence from
   * "not yours", and it has to cover the cover: a designate holds the covered man's picks
   * exactly as if they were his own, so he hits this state on Vishal's 3.09 as readily as on
   * his own 3.04. `canEnter` already reads the designates; what changes is the wording, which
   * must not tell Rajan that Vishal's pick is his.
   */
  const notYetOpen = Boolean(entering && !mock && !privileged && !canEnterTarget
    && canEnter(entering, actorInfo));
  /** True when the only reason he may touch this cell is that he is covering its owner. */
  const coveringEntering = Boolean(entering && !mock
    && entering.owner.toUpperCase() !== mine.toUpperCase()
    && designates[entering.owner.toUpperCase()]?.toUpperCase() === mine.toUpperCase());
  /** Whether the pick being entered is the actor's own, which is what makes the card red. */
  const ownsEntering = Boolean(entering && entering.owner.toUpperCase() === mine.toUpperCase());

  /**
   * Does this actor ever enter somebody else's pick? A commissioner always; a designate while
   * the cover stands. Nobody else, and a plain manager's panel stays a confirm card.
   */
  const picksForOthers = !mock && (privileged
    || Object.values(designates).some((d) => d?.toUpperCase() === mine.toUpperCase()));
  const panelResults = useMemo(
    () => (panelQuery.trim().length < 2 ? []
      : searchPlayers(bank, panelQuery, { taken, limit: 12 })),
    [bank, panelQuery, taken],
  );
  /** Two or more cells enterable at once: the panel offers the choice instead of naming one. */
  /* Leaving the sandbox takes its two extra panes with it; the board is what is left. */
  useEffect(() => {
    if (!mock && (view === 'mine' || view === 'avail')) setView('grid');
  }, [mock, view]);

  const manyOpen = !mock && myOpen.length > 1;

  /**
   * THE OFFER, NOT THE PICK (Tim, 6 September: "so the board doesn't automatically select the
   * pick for them when their turn is up").
   *
   * When a manager's own pick opens and the top name still standing on his picklist is one he
   * can have, he is shown it and asked. Nothing is written until he says yes. Offered once per
   * pick per browser: a man who says "somebody else" and starts typing must not have the box
   * thrown back at him a second later, and a lapsed clock would otherwise re-offer forever.
   */
  const queueTop = useMemo(() => {
    /* His own list or none: a list still loading belongs to nobody yet. */
    if (mock || queueGm !== mine.toUpperCase() || queue.length === 0) return null;
    const gone = new Set(taken.map(nameKey));
    return queue.find((q) => !gone.has(nameKey(q.player))) ?? null;
  }, [queue, taken, mock, queueGm, mine]);

  useEffect(() => {
    if (mock || !queueTop || queueOffer) return;
    /*
     * HIS OWN PICK ONLY, and a cover is deliberately not one.
     *
     * A picklist is a list of the players THIS man wants for HIS team. Rajan covering Vishal is
     * drafting Vishal's roster, and throwing Rajan's own queue at him there would offer him his
     * own targets to spend somebody else's pick on — the one thing a cover must not do by
     * accident. He can still enter the covered pick; he just types it, the way he would if the
     * man were sitting beside him.
     */
    const own = myOpen.find((o) => o.pick.owner.toUpperCase() === mine.toUpperCase());
    if (!own || offered.has(own.pick.overall)) return;
    setOffered((prev) => new Set(prev).add(own.pick.overall));
    setQueueOffer({ pick: own.pick, entry: queueTop });
  }, [myOpen, queueTop, queueOffer, offered, mine, mock]);

  /* An offer for a cell somebody has since filled is stale — take it off the screen. */
  useEffect(() => {
    if (!queueOffer) return;
    const still = board.find((p) => p.overall === queueOffer.pick.overall);
    const gone = new Set(taken.map(nameKey));
    if (still?.player || gone.has(nameKey(queueOffer.entry.player))) setQueueOffer(null);
  }, [board, taken, queueOffer]);
  const lapsedOpen = useMemo(
    () => new Set(openNow.filter((o) => o.lapsed).map((o) => o.pick.overall)),
    [openNow],
  );

  /*
   * WHO IS ACTUALLY ON THE CLOCK once picks start lapsing.
   *
   * `onTheClock` returns the first UNMADE pick, which was the right answer while only one pick
   * could be open. With a lapsed clock the first unmade pick is the man who is late, and the man
   * genuinely on the clock is further down — so the board would have painted the accent frame on
   * somebody who had already been passed by, and nothing at all on the man everyone was waiting
   * for. The rest of the app keeps `clock`, which still means "the earliest pick outstanding".
   */
  const boardClock = useMemo(
    () => openNow.find((o) => !o.lapsed)?.pick ?? clock,
    [openNow, clock],
  );
  /**
   * THE JAM. Four picks may be in play at once and a lapsed one holds its slot, so when all four
   * have run out nothing new opens and there is nobody on the clock at all. Saying "on the clock:
   * Ryan" over the oldest red cell is the one thing the bar must not do — it is the state the
   * room needs named, because the board only moves again when one of the four is entered.
   */
  const jammed = openNow.length > 0 && openNow.every((o) => o.lapsed);
  /**
   * WHOSE CLOCK `Restart pick` RESTARTS: the front of the window, not the back of it. One pick
   * open means the man on the clock; four means the newest of the four, because that is the one
   * the room is waiting on. `openPicks` does the same thing with the timestamp.
   */
  const restartTarget = openNow.length ? openNow[openNow.length - 1]!.pick : clock;
  /**
   * WHEN EACH OPEN CLOCK STARTED (17a). The man on the clock after a lapse did not start when the
   * last pick landed — he started when the late man's time ran out — and the late man's own
   * elapsed figure counts from HIS start. One stamp per open pick, so every PickClock on the
   * screen counts from the right moment; `sinceLastPick` stays the fallback for a board with no
   * open picks computed (paused, mock, no clock set).
   */
  const startedAt = useMemo(() => {
    const m = new Map<number, string>();
    for (const o of openNow) m.set(o.pick.overall, new Date(o.startedAt).toISOString());
    return m;
  }, [openNow]);
  /**
   * NOTHING COUNTS UNTIL A COMMISSIONER PRESSES START (Tim, 8 September: "rajan's pick hit time,
   * but it didn't progress to the next picker. we had a whole workflow around that possibility").
   *
   * The workflow was fine. It just was not running: `openNow` is gated on a LIVE draft, so on a
   * board that had not been started there was no cascade, no lapsed list and no jam — while the
   * clock itself was drawn anyway, from the newest `madeAt` on the board. Picks entered ahead of
   * draft night therefore gave every not-yet-started board a countdown that ran out on its own
   * and then sat on `TIME` for ever, naming a man nobody was waiting on.
   *
   * One origin decides it now, and it is null unless the room is actually running. Every clock
   * on this screen and on the wall reads from it, so there is one answer to "is anything
   * counting" rather than one per placement.
   */
  const running = !mock && draft?.status === 'live';
  /*
   * A STOPPED ROOM STILL SHOWS ITS CLOCKS; THEY JUST DO NOT MOVE (Tim, 8 September: "when
   * paused, freeze all clocks"). Hiding them was the same mistake as switching the cascade off:
   * a board that came back from a break had no figures on it at all, so nobody could see how
   * much of his two minutes a man had left when the room stopped. `openPicks` subtracts the time
   * the room spent stopped from every origin it hands out, so the figures freeze on their own —
   * there is nothing here to hold them still, only the decision to keep drawing them.
   */
  const counting = running || paused;
  const clockSince = !counting ? null
    : (boardClock ? startedAt.get(boardClock.overall) ?? sinceLastPick : sinceLastPick);
  /*
   * NO CLOCK IN A SANDBOX — INCLUDING IN THE CELL (Tim, 6 September: "in the mock draft, there is
   * no time"). The bar has said so since rev N: nobody is waiting, so there is nothing to count.
   * The CELL was still handed the live board's last timestamp and the round's allowance, so it
   * counted a real clock down over a rehearsal and then sat there reading `TIME` — a sandbox
   * telling a man he is late for a pick nobody is waiting on.
   */
  const cellSeconds = !counting || !boardClock ? null
    : pickSeconds(settings?.clocks, boardClock.round);
  const cellSince = clockSince;

  const inFlight = useMemo(() => {
    if (mock || !order.length) return [];
    const clockCtx: DraftClock = {
      /*
       * THE PICK ON THE CLOCK, WHICH IS NOT THE FIRST UNMADE CELL once a clock has lapsed. The
       * window closes a fixed number of picks from where the room actually is; measuring it
       * from a cell the draft walked past reported the counter window as longer than it is.
       */
      onTheClock: draft?.status === 'live' ? (boardClock?.overall ?? board.find((p) => !p.player)?.overall ?? null) : null,
      order, teams: TEAMS, startsAt: settings?.draftDate ?? null,
    };
    const ctx = { draftDate: settings?.draftDate ?? null, inSeason: false, now, clock: clockCtx };
    return liveTrades
      .filter((t) => t.season === SEASON && tradeState(t, ctx) === 'open')
      .map((t) => {
        const cells = new Set<number>();
        for (const side of t.sides) {
          for (const a of side.sends) {
            if (a.kind !== 'pick') continue;
            const o = overallOf(a, side.gm, clockCtx);
            if (o !== null) cells.add(o);
          }
        }
        const first = firstPickInvolved(t, SEASON, clockCtx);
        const away = picksUntilClose(t, SEASON, ctx);
        const picksOf = (i: 0 | 1) => t.sides[i].sends
          .filter((a: LiveTradeAsset) => a.kind === 'pick')
          .map((a: LiveTradeAsset) => a.text.replace(`${SEASON} `, ''));
        return { t, cells, first, away, a: t.sides[0].gm, b: t.sides[1].gm, aSends: picksOf(0), bSends: picksOf(1) };
      })
      .filter((x) => x.cells.size > 0);
  }, [liveTrades, mock, order, draft?.status, board, boardClock, settings?.draftDate, now]);

  const inTradeCells = useMemo(() => {
    const all = new Set<number>();
    for (const x of inFlight) for (const c of x.cells) all.add(c);
    return all;
  }, [inFlight]);


  /**
   * HOW FAR THE BOARD HAS GOT, which is not the same as how many cells are filled.
   *
   * Keepers arrive already placed, so a board four rounds in has a keeper sitting on a 12th and
   * counting him made the bar say the draft was further along than the room was. The figure the
   * room means by "35 in" is the cells filled before the pick on the clock.
   */
  /**
   * The pick clock for the round on the clock, if the commissioner has set one for it. Counted
   * from the previous pick's timestamp, which is the only start the data has — and the reason a
   * clock is advisory rather than binding: nobody presses go.
   */
  const pickClock = useMemo(
    () => (clock ? countdown(sinceLastPick, pickSeconds(settings?.clocks, clock.round), now) : null),
    [clock, sinceLastPick, settings, now],
  );

  /** A scheduled breather, announced rather than left to look like a stall. */
  const intermission = useMemo(
    () => (clock ? intermissionAfter(settings?.clocks, clock.round - 1) : null),
    [clock, settings],
  );

  /*
   * AND THE BREATHER ACTUALLY STOPS THE ROOM (Tim, 9 September: "when the intermission came up,
   * the clock still started for the next round. that shouldn't happen").
   *
   * The banner has always said "clocks resume when a commissioner restarts them" and nothing ever
   * stopped them: the intermission was an ANNOUNCEMENT drawn over a board that carried straight on
   * counting, so the first man of the next round burned his ten minutes standing at the snacks.
   *
   * An intermission is a pause. Not a new kind of thing — the same interval a commissioner opens
   * by pressing the button, with the round it was due after written on it, so the clocks freeze
   * through the arithmetic that already exists and nobody's elapsed figure moves. The status stays
   * `live` because the board IS live; it is at its break, not shut, and the banner is drawn on
   * that reading.
   *
   * ONE CLIENT WRITES IT, a commissioner's, for the reason the farm's auto-hammer names: ten
   * browsers all noticing the same intermission would all write, and nine of those writes are a
   * second pause on top of the first. `intermissionAfter` on the entry makes it idempotent
   * besides — the round can only be stopped for once.
   */
  const intermissionRound = clock ? clock.round - 1 : null;
  useEffect(() => {
    if (mock || !privileged || !draft || draft.status !== 'live') return;
    if (intermission === null || intermissionRound === null) return;
    const list = draft.pauses ?? [];
    if (list.some((x) => !x.until)) return;                       // already stopped, by anyone
    if (list.some((x) => x.intermissionAfter === intermissionRound)) return;  // and only once
    void store.saveDraftState({
      ...draft,
      pauses: [...list, {
        at: new Date().toISOString(),
        by: actor.toUpperCase() as never,
        intermissionAfter: intermissionRound,
      }],
      updatedAt: new Date().toISOString(),
    });
  }, [mock, privileged, draft, intermission, intermissionRound, actor, store]);

  /** The acting manager's next pick, for "your 4.08 is 14 away" on the bar and in the panel. */
  /**
   * The best-available list, as its own thing.
   *
   * It is drawn in the rail on the desktop and as a whole TAB on the phone (Turn 12's
   * "Available"), and those are two places one list has to appear. Declared once so a change to
   * the row — a chip, a disabled reason, the pick affordance — cannot land in one and not the
   * other, which is exactly how the rail and the phone drifted apart before.
   */
  /**
   * THE LIST SCROLLED BACK TO THE TOP EVERY SECOND (Tim, 9 September: "scroll is bouncing back to
   * top after scrolling down").
   *
   * This was a COMPONENT declared inside the render body and used as `<AvailList />`. A function
   * declared in a body is a new function object on every render, so React saw a new element TYPE
   * every time, and a new type is not an update — it is an unmount and a remount. The whole
   * subtree was thrown away and rebuilt, the scroll container with it, and `scrollTop` went back
   * to nought. On any other screen that would be invisible; on this one the board ticks once a
   * second for the clocks, so the list reset itself under the hand of anybody reading down it.
   *
   * It is a plain render function now — lower case, and CALLED rather than mounted, so what it
   * returns joins the parent's element tree by position and keeps its identity across renders.
   * It stays inside the body because it closes over a dozen pieces of this component's state;
   * hoisting it would mean a props object with a dozen fields in it, rebuilt every second, which
   * is the same problem wearing a different hat.
   *
   * The rule this is an instance of: never declare a component inside another component. If it
   * needs the parent's state, it is a render function; if it does not, it belongs at module
   * scope.
   */
  function availList() {
    if (bestAvailable.length === 0) {
      return (
        <p className="note railpad">
          Load an ADP file on <b>Admin › Draft tools</b> and this ranks what is left.
        </p>
      );
    }
    /*
     * ONE LIST, TWO QUESTIONS (Tim, 6 September: "move the player search down to the quick
     * picker").
     *
     * The search field sat at the top of the pick panel with its own position chips and its own
     * result list, and this card sat under it with a second ranking, second set of chips and
     * second set of rows — two lists of players, one above the other, doing the same job. The
     * field belongs to the list it searches. Empty, the card answers "who is best"; typing, it
     * answers "where is Nacua" — same rows, same rank column, same star and same pick button
     * either way.
     */
    const searching = query.trim() !== '';
    const wanted = baFilters.length ? new Set(baFilters) : null;
    const ranked = (wanted
      ? bestAvailable.filter(({ p }) => positionsOf(p).some((x) => wanted.has(x)))
      : bestAvailable
    ).slice(0, BEST_SHOWN);
    /*
     * THE BANK TOPS THE LIST BACK UP (Tim, 6 September: "DEF filter shows nothing").
     *
     * The ranking is built by matching our ADP file against the player bank BY NAME, and the two
     * do not agree about defences: the consensus calls them `Broncos D/ST` and `Denver Defense`,
     * the bank calls them `Broncos DEF`. Not one of the thirty-two survived the join, so the DEF
     * chip filtered a list that never had a defence in it — and the same would happen to any
     * position our ADP file happens to under-cover.
     *
     * A consensus is a ranking, not a registry. Where it runs out, the rest of the bank follows
     * in alphabetical order with no rank against it, which is the truth: nobody ranked him.
     */
    const shown = searching
      ? searchPlayers(bank, query, { positions: baFilters, taken, limit: BEST_SHOWN })
        .map((p) => ({ p, code: '', adp: adpOrder.get(nameKey(p.name)) ?? 0 }))
      : (() => {
        if (ranked.length >= BEST_SHOWN) return ranked;
        const have = new Set(ranked.map((x) => nameKey(x.p.name)));
        const gone = new Set(taken.map(nameKey));
        const rest = bank.players
          .filter((pl) => pl.pos !== 'K'
            && !gone.has(nameKey(pl.name)) && !have.has(nameKey(pl.name))
            && (!wanted || positionsOf(pl).some((x) => wanted.has(x))))
          .sort((a, b) => a.name.localeCompare(b.name))
          .slice(0, BEST_SHOWN - ranked.length)
          .map((pl) => ({ p: pl, code: '', adp: adpOrder.get(nameKey(pl.name)) ?? 0 }));
        return [...ranked, ...rest];
      })();
    return (
      <>
      <FadeList>
        {shown.map(({ p, code, adp }) => {
          const { last, first } = splitName(p.name);
          return (
            <li key={`${p.name}|${p.pos}`}>
              {/*
                THE ADP RANK LEADS (17a rev C). The card used to print where our own board would
                take him — a projection, and one the room reads as a promise. His rank on the
                consensus is the fact; where he lands is on the row's tooltip.
              */}
              <span className="bacode" title={`${adp ? `ADP no. ${adp} — ` : ''}the consensus puts him at ${code} on our board`}>
                {adp || '—'}
              </span>
              {/*
                THE PLUS LEADS THE ROW (Tim, 8 September: "move the plusses to before the name in
                the Player List"). It was last on the line, past the chips and the star, so the
                one thing the row is FOR sat at the end of a queue of things it is not — and on a
                narrow rail it was the item the ellipsis pushed against. Reading order is the
                order of the act: take this man, then who he is.
              */}
              {entering && (
                <button type="button" className="bapick" disabled={!canEnterTarget}
                  title={canEnterTarget ? 'Pick this player'
                    : `Only ${managerShort(entering.owner)} can enter ${pickCode(entering, TEAMS)}`}
                  aria-label={canEnterTarget ? `Pick ${p.name}` : `${p.name} — not your pick to make`}
                  onClick={() => choose(p.name)} />
              )}
              <span className="baname" title={p.name}><b>{last}</b> <i>{first}</i></span>
              {positionsOf(p).map((x) => (
                <span key={x} className={`poschip ident pos-${x}`}>{x}</span>
              ))}
              <NflMark player={p.name} />
              {/*
                QUEUE HIM, WHICH IS THE THING YOU CAN DO ON SOMEBODY ELSE'S TURN (Tim, 6
                September). The pick button is grey for twenty-seven picks out of every
                twenty-eight; this one is live the whole time, and it is how most of a
                picklist actually gets built — off the ranking you are already reading.
              */}
              {/*
                AND IT WORKS IN THE SANDBOX (Tim, 6 September: "allow adding to picklist with the
                star icon while in mock mode"). A rehearsal is where a man finds out he wants
                somebody — that is most of what it is for — and the star was drawn out entirely,
                so the finding had nowhere to go until he switched back and tried to remember it.
                It writes to the real list from either board.

                Adding only, in here. Taking a man OFF is the edit the sandbox should not make on
                his behalf: he is looking at a board where the man may already be gone, which is
                a fact about the rehearsal and not about the list. The filled star stays as a
                marker so the answer to "is he on it" is still on the row.
              */}
              {!projected && (
                <button type="button"
                  className={`baqueue${queued.has(nameKey(p.name)) ? ' on' : ''}`}
                  disabled={mock && queued.has(nameKey(p.name))}
                  title={queued.has(nameKey(p.name))
                    ? mock
                      ? `${p.name} is on your picklist — take him off from the live board`
                      : `${p.name} is on your picklist — click to take him off`
                    : mock
                      ? `Queue ${p.name} on your real picklist — it is the same list on both boards`
                      : `Queue ${p.name} on your picklist`}
                  aria-label={queued.has(nameKey(p.name))
                    ? `Take ${p.name} off your picklist` : `Queue ${p.name}`}
                  onClick={() => (queued.has(nameKey(p.name)) ? queueDrop(p.name) : queueAdd(p.name))}>
                  {queued.has(nameKey(p.name)) ? '★' : '☆'}
                </button>
              )}
            </li>
          );
        })}
        {shown.length === 0 && (
          <li className="note">
            {searching ? 'Nobody left matching that.'
              : `Nobody left at ${baFilters.join(', ')}.`}
            {/*
              THE COMMISSIONER'S ESCAPE, kept. A man the bank has never heard of still has to be
              enterable on the night — a rookie signed on Thursday, a name spelled two ways.
            */}
            {searching && privileged && canEnterTarget && (
              <button type="button" className="linkbtn"
                onClick={() => choose(query.trim())}>
                draft &ldquo;{query.trim()}&rdquo; anyway
              </button>
            )}
          </li>
        )}
      </FadeList>
      </>
    );
  }

  const myNext = useMemo(
    () => board.find((p) => !p.player && canEnter(p, actorInfo)
      && p.owner.toUpperCase() === mine.toUpperCase()) ?? null,
    [board, actorInfo, mine],
  );

  /**
   * Who is in the room (11a).
   *
   * The drawing shows ten badges with the away ones dimmed and a line naming the cover. There is
   * no presence signal in this app — nobody heartbeats, and deriving "here" from recent activity
   * would call nine people absent every time a round takes four minutes. What IS known is exactly
   * what the drawing's own example says: "Amit is away — Rajan is covering his picks." A cover is
   * a commissioner writing down that somebody is not here. So the room is read off the designates,
   * and the count says what it actually counts.
   */
  const room = useMemo(() => {
    const away = order.filter((g) => designates[g.toUpperCase()]);
    return {
      here: order.length - away.length,
      total: order.length,
      away: away.map((g) => ({ gm: g, cover: designates[g.toUpperCase()]! })),
    };
  }, [order, designates]);

  /**
   * Finished either by filling the board or by a commissioner saying so. Both are real: most
   * years the last pick lands and that is that, but a draft can also be closed with cells open.
   */
  const finished = !mock && (draft?.status === 'complete'
    || (board.length > 0 && made === board.length));

/* `lastMade` lived here and did the same job as `lastAction` above, which is now the one the bar
   reads. One definition of "the last thing that happened" is enough. */

  /* -- the rail (8a): needs, counts, byes — derived from the board, so a mock pick moves them
   * exactly like a real one. ---------------------------------------------------------------- */

  /** Everyone the acting GM currently has: keepers on their picks plus anyone drafted. */
  const myRoster = useMemo(
    () => board.filter((p) => p.player && p.owner.toUpperCase() === gm.toUpperCase())
      .map((p) => p.player!),
    [board, gm],
  );
  const myNeeds = useMemo(() => needs(myRoster, bank), [myRoster, bank]);

  /**
   * What the man in the sheet does to your roster — Turn 12's "Fills your open RB slot".
   *
   * Read off the SAME `needs()` the rail draws, so the sheet and the Needs card cannot disagree
   * about whether you are short at tight end. It returns null rather than a sentence whenever it
   * cannot tell: an unknown player, a commissioner entering somebody else's pick, a position the
   * starter table has no row for. A confirm sheet that guesses at the consequence is worse than
   * one that states only the part it knows.
   */
  const pendingFills = useMemo(() => {
    if (!pending?.pos || !entering) return null;
    if (entering.owner.toUpperCase() !== gm.toUpperCase()) return null;
    const need = myNeeds.find((n) => n.pos === pending.pos);
    if (!need) return null;
    if (need.urgency >= 1) return `Fills your open ${need.pos} slot.`;
    if (need.urgency > 0) return `You are short at ${need.pos} — this covers one.`;
    return `You are already covered at ${need.pos}.`;
  }, [pending, entering, gm, myNeeds]);

  /**
   * Where our own board says each man belongs, for everybody rather than the top thirty.
   *
   * `bestAvailable` computes the same codes and stops at thirty, which is right for a list and
   * wrong for a lookup — the confirm sheet has to be able to name the ADP of a fourteenth-round
   * flyer. Same `adjusted()` walk, no cap, keyed for lookup.
   */
  const adpCodes = useMemo(() => {
    const m = new Map<string, string>();
    if (adpRank.size === 0) return m;
    const kept = board.filter((p) => p.keeper && p.player).map((p) => p.player!);
    for (const a of adjusted(kept, TEAMS)) {
      const within = ((a.ourPick - 1) % TEAMS) + 1;
      m.set(nameKey(a.name), `${a.ourRound}.${String(within).padStart(2, '0')}`);
    }
    return m;
  }, [adpRank, board]);

  /** Bye and ADP for the sheet's second line, both absent-tolerant. */
  const pendingFacts = useMemo(() => {
    if (!pending) return { bye: null as number | null, adp: undefined as string | undefined };
    const p = bank.players.find((x) => x.name === pending.name);
    return { bye: p?.bye ?? null, adp: adpCodes.get(nameKey(pending.name)) };
  }, [pending, bank, adpCodes]);
  /*
   * The needs headline retired with the Reference card's tab dots. What a manager is short of
   * is still computed — `myNeeds` drives the phone's needs card — but nothing on the desktop
   * summarises it into a label any more, because the label was on a tab and a tab that is
   * always warning is not a warning.
   */

  /**
   * The thirty best players left, ADP order, each with the pick code our own board says he
   * should have gone at.
   *
   * The code is `adjusted()`, not the raw consensus: forty-odd of the best players are kept and
   * never reach the draft, so a national rank means nothing until it is re-seated on a ten-team
   * board with those names removed. It is deliberately a projection of where he BELONGS rather
   * than of where he will go — a 3.02 sitting there at 3.06 is the card earning its place.
   */
  const bestAvailable = useMemo(() => {
    if (adpRank.size === 0) return [] as { p: PlayerBank['players'][number]; code: string; adp: number }[];
    const kept = board.filter((p) => p.keeper && p.player).map((p) => p.player!);
    const gone = new Set(taken.map(nameKey));
    const byKey = new Map(bank.players.map((p) => [nameKey(p.name), p]));
    /*
     * A HUNDRED SHOWN, OUT OF A BANK OF FOUR HUNDRED (Tim, 5 September).
     *
     * Thirty was one round and a bit: by the fourth round the card was mostly names the room
     * had already taken. A hundred is the useful depth — but a hundred is what is SHOWN, and it
     * has to stay a hundred all night, so the list is built off a bank four times as deep and
     * the hundred refills from underneath as picks land. Two hundred and forty names go in a
     * sixteen-round draft plus keepers; four hundred cannot run dry, and the bank is what the
     * position filters read, so "best available TE" is a real list in round twelve rather than
     * whatever tight ends happened to survive inside the first hundred.
     */
    const out: { p: PlayerBank['players'][number]; code: string; adp: number }[] = [];
    for (const a of adjusted(kept, TEAMS)) {
      if (out.length >= BEST_BANK) break;
      const key = nameKey(a.name);
      if (gone.has(key)) continue;
      const p = byKey.get(key);
      if (!p) continue;
      const within = ((a.ourPick - 1) % TEAMS) + 1;
      out.push({
        p,
        code: `${a.ourRound}.${String(within).padStart(2, '0')}`,
        adp: adpOrder.get(key) ?? 0,
      });
    }
    return out;
  }, [adpRank, adpOrder, board, taken, bank]);

  /** Every team's position counts, off the board itself. */
  const RAIL_POS = ['QB', 'RB', 'WR', 'TE', 'DEF'] as const;
  const counts = useMemo(() => {
    const byGm = new Map<string, Record<string, number>>();
    for (const g of order) byGm.set(g.toUpperCase(), { QB: 0, RB: 0, WR: 0, TE: 0, DEF: 0, total: 0 });
    for (const p of board) {
      if (!p.player) continue;
      const row = byGm.get(p.owner.toUpperCase());
      if (!row) continue;
      row.total = (row.total ?? 0) + 1;
      const pos = p.pos ?? bank.players.find((x) => x.name === p.player)?.pos;
      if (pos && pos in row) row[pos] = (row[pos] ?? 0) + 1;
    }
    return byGm;
  }, [board, order, bank]);

  /**
   * Byes by week, for every team — the acting GM's drive the chart, everyone else's the
   * worst-week list under it.
   *
   * The design drew this card as a wireframe "pending real bye data"; the player index has
   * carried a bye per player all along, so it is built as drawn, with real weeks. Two out the
   * same week is what the drawing already treats as worth ink — that is the threshold here, in
   * both the chart and the list.
   */
  const byesByGm = useMemo(() => {
    const byGm = new Map<string, Map<number, string[]>>();
    for (const g of order) byGm.set(g.toUpperCase(), new Map());
    for (const p of board) {
      if (!p.player) continue;
      const week = nflInfoOf(p.player)?.bye ?? p.bye ?? null;
      if (week == null) continue;
      const weeks = byGm.get(p.owner.toUpperCase());
      if (!weeks) continue;
      if (!weeks.has(week)) weeks.set(week, []);
      weeks.get(week)!.push(p.player);
    }
    return byGm;
  }, [board, order]);

  const myByes = useMemo(
    () => byesByGm.get(gm.toUpperCase()) ?? new Map<number, string[]>(),
    [byesByGm, gm],
  );
  const worstBye = useMemo(
    () => [...myByes.entries()].reduce<[number, string[]] | null>(
      (w, e) => (w === null || e[1].length > w[1].length ? e : w), null),
    [myByes],
  );
  /**
   * One row per team, WORST FIRST.
   *
   * It used to sit in board order on the argument that the list should not imply a ranking it did
   * not have. It does have one: the whole card is called "worst week", and a man with four out in
   * week seven is a different fact from a man with two, whatever seat he drafts from. Ordering by
   * seat buried him among nine rows that looked the same. Ties break by week and then by name so
   * the list does not shuffle as picks come in.
   */
  const worstByTeam = useMemo(() => order.map((g) => {
    const weeks = byesByGm.get(g.toUpperCase()) ?? new Map<number, string[]>();
    let worst: [number, string[]] | null = null;
    for (const e of weeks) if (worst === null || e[1].length > worst[1].length) worst = e;
    return { gm: g, week: worst?.[0] ?? null, n: worst?.[1].length ?? 0 };
  }).sort((a, b) => b.n - a.n || (a.week ?? 99) - (b.week ?? 99) || a.gm.localeCompare(b.gm)),
  [order, byesByGm]);

  return (
    <div className={`app wide draftpg${mock ? ' mockmode' : ''}${paused ? ' paused' : ''}`}>
      <Linkify>
      {/* Pass 17: the page has no headline; the mode row is the first thing, and it never moves. */}
      <h1 className="vh">{mock ? 'Mock draft' : 'Draft board'} · {made} of {board.length} picks in</h1>

      {/*
        THE ROW THAT SAYS WHERE YOU ARE AND WHAT YOU MAY DO.

        DIRECTLY UNDER THE HEADING AND ABOVE THE CLOCK, so it never moves. It sat lower down at
        first, below the trade and keeper cards — which meant the row jumped every time one of
        those opened or closed, and a control that moves is a control you have to find again
        mid-draft. The heading and the pick count do not move, so it is anchored to them.

        Left: the two boards. Beside them the mode switch, because "which board" and "is this
        real" are the two facts about where you are. Right: the commissioner's controls,
        condensed to a bar — they used to be a full card below the board they operate on.
      */}
      <div className="panes moderow">
        {/*
          THE RAIL TOGGLE (17a rev C). The board is the work surface and the rail is the tool
          beside it; on the night a commissioner reading the room wants all 1400px of grid, and
          on `All teams` the rail has nothing to say at all. One icon, at the head of the row
          where the two board switches are, because "how much board" is the same kind of choice.
        */}
        <button type="button" className={`railtoggle${railOpen ? ' on' : ''}`}
          aria-pressed={railOpen}
          title={railOpen ? 'Hide the rail — the board takes the width' : 'Show the rail'}
          onClick={() => setRailOpen((v) => !v)}>
          <span className="vh">{railOpen ? 'Hide the rail' : 'Show the rail'}</span>
          <i aria-hidden="true" />
        </button>
        <div className="seg" role="tablist" aria-label="Which board">
          <button type="button" role="tab" aria-selected={stage === 'main'}
            className={`pane ${stage === 'main' ? 'on' : ''}`}
            onClick={() => setStage('main')}>Main draft</button>
          {/*
            NO FARM TAB INSIDE A MOCK, BUT IT KEEPS ITS PLACE IN THE ROW.

            The auction has no sandbox — bidding is real FAAB against a real board, so it runs
            in one place only, and a tab that opens the live auction from inside something
            labelled "nothing is saved" is a trap. It used to be removed outright, which
            shortened the group and slid `Board / All teams` and everything past it left every
            time the switch was flipped
            (Tim, 6 September: "don't let the elements move around when toggling between mock
            and live draft"). A page that rearranges itself is a page you have to re-read, so
            the tab goes quiet rather than going away.
          */}
          <button type="button" role="tab" aria-selected={!mock && stage === 'farm'}
            className={`pane ${!mock && stage === 'farm' ? 'on' : ''}`}
            disabled={mock}
            title={mock
              ? 'The farm auction has no sandbox — bidding is real FAAB against the real board'
              : undefined}
            onClick={() => setStage('farm')}>Farm rounds</button>
        </div>

        {/*
          BOARD / ALL TEAMS MOVES UP HERE (17a rev C). It used to sit on the board card's own
          header beside a "The board" heading; both were saying what the tab already says, and
          the heading cost a row of the thing it named. The phone keeps its four-tab strip on
          the card, where the tabs are the page.
        */}
        {stage === 'main' && (
          <div className="seg deskonly viewseg" role="tablist" aria-label="Which view">
            <button type="button" role="tab" aria-selected={view !== 'team'}
              className={`pane ${view !== 'team' ? 'on' : ''}`}
              onClick={() => setView('grid')}>Board</button>
            <button type="button" role="tab" aria-selected={view === 'team'}
              className={`pane ${view === 'team' ? 'on' : ''}`}
              onClick={() => setView('team')}>All teams</button>
          </div>
        )}

        {/*
          UNDO IS THE ONE CONTROL EVERYBODY GETS IN THE MOCK. Sixteen rounds entered by hand for
          ten teams is a lot of typing to lose to one mis-tap, and nothing here is shared.
        */}
        {/*
          THE STATE PILL STANDS OUTSIDE THE BAR (Tim, 8 September: "move 'Live Now' just outside
          of the bar before 'Mark Draft Done'").
          
          It was the only thing inside that ring that is not a control — a fact about the room,
          filed among four things you can press, and it read as a fifth button you could not work
          out how to use. The bar is what a commissioner may DO; this is what the room IS, and it
          belongs beside it rather than in it.
        */}
        {stage === 'main' && (mock || privileged) && !mock && (
          <span className={`dstatus ccstate ${paused ? 'paused' : draft?.status ?? 'setup'}`}>
            {/*
              A ROOM THAT STOPPED FOR PIZZA HAS NOT `Not started`. Pausing writes `setup` back to
              the status, which is also the word for a board nobody has touched — so the one
              label on the row whose whole job is to say where the night stands said the night
              had not begun, over a board four rounds deep.
            */}
            {/*
              And `Paused` is a commissioner's decision while `Intermission` is the schedule's.
              The clocks cannot tell them apart, which is the point; the room can, which is also
              the point — one of them somebody has to end, the other ends itself on the bar.
            */}
            {paused ? (atBreak !== null ? 'Intermission' : 'Paused')
              : draft?.status === 'live' ? 'Live now'
                : draft?.status === 'complete' ? 'Complete' : 'Not started'}
          </span>
        )}
        {stage === 'main' && (mock || privileged) && (
          <div className="ccbar">
            {/*
              DONE LEADS THE ROW (Tim, 6 September), to the left of the state pill rather than
              buried among the clock controls, and under the whole sentence: `Done` beside
              `Pause` and two restarts read as one more thing that happens to a clock, and it
              is the only control on the row that ends the night.
            */}
            {/*
              AND IT STAYS WHILE THE ROOM IS STOPPED, for the reason the restarts do (Tim, 8
              September: "so the button doesn't jump"). Ending the night from a paused board is
              if anything the commoner case: the last pick goes in, somebody stops the clock, and
              then the room agrees it is over.
            */}
            {!mock && (draft?.status === 'live' || paused) && (
              <button type="button" className="ccbtn ccdone"
                title="Mark the draft finished"
                onClick={() => setConfirm('done')}>
                <span aria-hidden="true">✓</span> Mark draft done
              </button>
            )}

            {/* ==================================================================================
              UNDO AND REDO KEEP THEIR OWN COMPANY (Tim, 6 September: "give undo/redo their own
              space conceptually on the bar since they're not clock functions").

              Everything to the right of them acts on time — stop it, give it back, start the
              night over. These two act on what is written in a cell, and sitting in the same
              undivided run made them read as two more clock controls. A rule between the groups
              and a little air is all it takes.
            ================================================================================== */}
            <span className="ccgroup ccedits">
            <button type="button" className="ccbtn" disabled={!lastAction}
              title={lastAction
                ? `Undo ${pickCode(lastAction.pick, TEAMS)} — ${lastAction.pick.player}. `
                  + 'Clearing reopens that cell for the whole room; it does not move the clock.'
                : 'Nothing to undo yet'}
              onClick={() => lastAction && void undo(lastAction.pick)}>
              <UndoMark /> Undo last
              {/*
                NAMING THE PICK ON THE BUTTON, not only on the tooltip. Undo is the control a
                commissioner reaches for while somebody is talking over him, and "undo last" on
                its own asks him to remember which pick that was.
              */}
              {lastAction && (
                <span className="ccundo">
                  {' · '}{pickCode(lastAction.pick, TEAMS)}{' '}
                  {shortSurname(splitName(lastAction.pick.player ?? '').last)}
                </span>
              )}
            </button>

            {/*
              REDO IS NOT THERE UNTIL THERE IS SOMETHING TO REDO. A permanently greyed button is
              a control that has never worked as far as anybody looking at it can tell, and this
              one is empty for all but a few seconds of the night.
            */}
            {redoStack.length > 0 && (
              <button type="button" className="ccbtn"
                title={`Put ${redoStack[0]!.player} back on `
                  + `${pickCode({ round: redoStack[0]!.round, slot: redoStack[0]!.slot } as BoardPick, TEAMS)}`
                  + ` · ${redoStack.length} to redo`}
                onClick={() => void redoLast()}>
                <UndoMark forward /> Redo
              </button>
            )}
            </span>

            {/*
              RUNNING OR STOPPED IS ONE CONTROL (Tim, 6 September). "Start" and "Pause clock" were
              two buttons that could never both apply, so the bar changed shape every time the
              room stopped for pizza. One switch, in one place, wearing the state it is in — and
              nothing else on the row moves when it is pressed.
            */}
            <span className="ccgroup ccclocks">
            {!mock && (
              <button type="button"
                /*
                  THE BUTTON ASKS WHETHER THE ROOM IS STOPPED, not what the status says (Tim, 9
                  September). An intermission stops the room with the status still `live` — the
                  board is at its break, not shut — and a button reading the status offered
                  `Pause` to a room that was already paused: two presses to come back, the first
                  of which did nothing anybody could see.
                */
                className={`ccplay ${running && !paused ? 'running' : 'stopped'}`}
                disabled={!draft}
                aria-pressed={running && !paused}
                title={!draft ? 'Publish the draft order first — Draft › Order and lottery'
                  : paused
                    ? (atBreak !== null
                      ? `End the intermission and put the room back on the clock`
                      : 'Start the clocks again')
                    : draft.status === 'live'
                      ? 'Stop the clock without marking the draft finished — the pizza case'
                      : draft.status === 'complete' ? 'Reopen the draft and start the clock again'
                        : 'Start the draft — 1.01 goes on the clock now'}
                onClick={() => void setStatus(running && !paused ? 'setup' : 'live')}>
                <span className="ccplayic" aria-hidden="true">
                  {running && !paused ? <PauseMark /> : <PlayMark />}
                </span>
                <span className="ccplaylab">
                  {running && !paused ? 'Pause'
                    : atBreak !== null ? 'Resume'
                      : draft?.status === 'complete' ? 'Reopen' : 'Start'}
                </span>
              </button>
            )}
            {/*
              TWO RESTARTS, ONE ICON, TWO SCOPES (Tim, 6 September). `Pick` gives the man on the
              clock his time back and touches nothing else. `Draft` takes the board back to an
              empty night. Both ask first — the second one because it throws away everything
              typed so far, the first because a commissioner reaching past `Done` for it should
              not be able to reset somebody's clock with one stray tap.
            */}
            {/*
              AND THEY STAY WHILE THE ROOM IS STOPPED (Tim, 8 September: "when draft is paused,
              retain the reset pick and reset draft options so the button doesn't jump"). They
              were gated on `live`, so pressing Pause took two buttons out of the row and slid
              `Full screen` under the cursor — and a commissioner who stopped the clock BECAUSE
              something needed restarting found the restart gone. A board that has never been
              started still hides them: there is no clock to give back and no night to clear.
            */}
            {!mock && (draft?.status === 'live' || paused) && (
              <>
                <button type="button" className="ccbtn"
                  title="Give the man on the clock his full time back, without stopping the draft"
                  onClick={() => setConfirm('restart-pick')}>
                  <RestartIcon /> Pick
                </button>
                <button type="button" className="ccbtn"
                  title="Clear every pick made tonight and start the board over — keepers and trades stay"
                  onClick={() => setConfirm('restart-draft')}>
                  <RestartIcon /> Draft
                </button>
              </>
            )}
            </span>

            {mock && onResetMock && (
              <button type="button" className="ccbtn"
                title={draft?.status === 'live'
                  ? 'Throw away your mock picks and rebuild from the live board as it stands right now'
                  : 'Throw away your mock picks and rebuild from the live board — picks up any slate submitted since'}
                onClick={onResetMock}>
                <RestartIcon />
                {/*
                  IT NAMES WHERE IT RESETS FROM (Tim, 6 September). Once the real draft is
                  running, "reset mock" is ambiguous in the one way that matters — the board it
                  rebuilds from is the live one, picks and all.
                */}
                {draft?.status === 'live' ? ' Reset to live draft' : ' Reset mock'}
              </button>
            )}
            {/*
              NO RUNNING COUNT ON THE ROW (Tim, 6 September). "2 of 160 in" was the last thing
              on a row of controls, so it read as one, and it is the one figure on the page
              nobody has to act on — the board itself says how far the draft has got.
            */}
          </div>
        )}

        {/*
          FULL SCREEN AND THE MOCK SWITCH CLOSE THE ROW (17a rev C). Both are about the whole
          page rather than about a pick, so they sit past the commissioner's controls rather
          than among them — and the mock is a real switch, because it is the one control on the
          row that changes what everything else on the page means.
        */}
        {/*
          NO WALL IN THE SANDBOX (Tim, 6 September). Full screen is for the television in the
          room — the one screen ten people are looking at — and what it would put on it is one
          man's private rehearsal.
        */}
        {stage === 'main' && (
          <button type="button" className="btn small deskonly wallbtn"
            disabled={mock}
            title={mock
              ? 'The wall is the screen ten people are looking at — a mock is one man\u2019s rehearsal'
              : 'The board and the farm rounds alone, filling the screen — for the TV in the room'}
            onClick={() => {
              // The Fullscreen request has to be made inside the click itself; the wall
              // mounts either way, and its own effect closes it when the screen is given back.
              void enterFullscreen();
              setWall(true);
            }}>Full screen</button>
        )}
        {/* And the other direction: no way into the mock from the farm rounds. */}
        {stage === 'main' && (
        <button type="button" className={`mockswitch ${mock ? 'on' : ''}`}
          role="switch" aria-checked={mock}
          title="Mock draft — nothing you enter is saved or seen by anybody else"
          onClick={() => (mock ? onGoLive?.() : onGoMock?.())}>
          <span className="track" aria-hidden="true"><i /></span>
          <span className="lbl">{mock ? 'on · nothing is saved' : 'off'}</span>

        </button>
        )}

      </div>

      {/*
        NO MOCK BAND (Tim, 6 September: "remove the 'nothing is real' bar"). The switch on the
        mode row is on and violet, the pick panel says MOCK, the clock bar is violet and the
        Trade picks card says SANDBOX. A full-width line saying the same thing a fifth time was
        the only one of them that cost a row of the page.
      */}

      {/*
        Who is picking (11a).

        On the LIVE board the drawing makes this a dark bar — the same object as the strip that
        follows you round the app, deliberately, so what you look at when you come back is what
        you were watching while you were away. The mock keeps a light row: 8a draws its own strip
        there with the fill controls, and a mock has no room and no clock worth naming.
      */}
      {/*
        ONE BAR, TWO SKINS (Tim, 6 September: "on the clock bar layout should remain
        purple-tinted, but the layout should match the live draft").
        
        The mock had a light row of its own with a different shape, so the one thing on the page
        that says whose turn it is looked like a different object depending on which board you
        were on. Same bar, same places for the same facts, violet instead of near-black — and
        no `YOUR PICK` flag, because in a mock you play all ten teams and it was true of every
        pick.
      */}
      {stage === 'main' && !(projected && !mock) && (clock ? (
          <div className={`clockbar${mock ? ' mock' : ''} ${myTurn && !mock ? 'mine' : ''}${jammed ? ' jammed' : ''}`}>
            <span className="tag">
              <i aria-hidden="true" />
              {/*
                AND THE BAR SAYS WHICH IT IS. "ON THE CLOCK" over a board nobody has started is
                the same lie the phantom countdown was telling, in words.
              */}
              {jammed ? 'NOBODY ON THE CLOCK'
                : !mock && !running ? 'UP NEXT'
                  : myTurn && !mock ? 'YOUR PICK' : 'ON THE CLOCK'}
            </span>
            {jammed ? (
              /*
                Four in play, all late: no new pick opens until one of them is entered, and
                saying "on the clock: Ryan" over the oldest red cell would be a lie the room
                would act on. Name the jam and name the way out of it.
              */
              <span className="cbsaid">
                {openNow.length} picks open, all late — the board moves again when one of them
                {' '}is in.
              </span>
            ) : (<>
            <span className="cbwho">
              <b>{managerShort((boardClock ?? clock).owner)}</b>
              <code>{pickCode(boardClock ?? clock, TEAMS)}</code>
            </span>
            {/*
              A target where the commissioner has set one for this round, elapsed time where he
              has not. Advisory either way — nothing picks for anybody, so an overrun goes red
              and the room deals with it, which is what a room does anyway.
            */}
            {/* No clock in a sandbox: nobody is waiting, so there is nothing to count. */}
            {!mock && (
              <PickClock now={pausedAt ?? undefined} since={cellSince} seconds={cellSeconds}
                size="m" skin="dark" className="cbclock" />
            )}
            </>)}
            {/*
              EVERY LAPSED PICK, NAMED (17a rev C). A clock running out forfeits nothing: the
              draft moves on and the late man keeps his pick. One lapse is common and two is not
              rare — the room passes a phone round — so the bar counts them and then lists them,
              rather than naming the first and saying "and 1 more". The elapsed figures are what
              a commissioner uses to decide whom to chase.

              "— all still open" used to close the list and said nothing the amber `N LAPSED`
              tag had not already said, while taking enough of the line to push the cover
              controls onto a second one (Tim, 6 September). Gone.
            */}
            {lapsedNow.length > 0 && (
              <span className="cblapsed">
                <span className="cbltag">{lapsedNow.length} lapsed</span>
                {lapsedNow.map((o, i) => (
                  <span className="cbllate" key={o.pick.overall}>
                    {i > 0 && <span className="cbldot" aria-hidden="true">·</span>}
                    <code>{pickCode(o.pick, TEAMS)}</code>
                    {managerShort(o.pick.owner)}
                    <PickClock now={pausedAt ?? undefined} since={startedAt.get(o.pick.overall) ?? sinceLastPick} seconds={null}
                      size="s" skin="dark" className="cblclock" />
                  </span>
                ))}
              </span>
            )}
            {!jammed && (
            <span className="cbsaid">
              {(() => { const c = boardClock ?? clock; return (<>
              pick {c.overall} of {board.length}
              {c.owner.toUpperCase() !== c.seat.toUpperCase()
                && <> · {managerShort(c.seat)}&rsquo;s pick</>}
              {/*
                "IS -4 AWAY" IS NOT A THING. A lapsed clock leaves picks open BEHIND the one the
                board is counting on, so a man whose own pick is one of them was told his 4.05
                was minus four away. It is not away at all — it is open, and he can type into it.
              */}
              {myNext && myNext.overall !== c.overall && (
                <> · your <code>{pickCode(myNext, TEAMS)}</code> is{' '}
                  {myNext.overall < c.overall ? 'open now' : <>{myNext.overall - c.overall} away</>}</>
              )}
              {designates[c.owner.toUpperCase()] && (
                <> · {managerShort(designates[c.owner.toUpperCase()]!)} is drafting for him</>
              )}
              </>); })()}
            </span>
            )}
            {/*
              AND WHEN THERE IS A COVER, THAT END OF THE BAR IS NOT QUIET (Tim, 6 September:
              "if active, give that section of the On the Clock bar a bold bgcolor"). Somebody
              else is typing your picks; it is the one thing on this bar that is about you
              rather than about the room, and amber is what the board already uses for a state
              that wants dealing with. It hugs the two controls so the bar's own height, which
              the rest of the page is measured against, does not move.
            */}
            <span className={`cbact${designates[mine.toUpperCase()] ? ' covered' : ''}`}>
              {/*
                THE COVER LINE IS FOR THE MAN WHO HAS ONE, and nobody else. "Ask a commissioner
                to set a cover" sat on every GM's bar all night (Tim, 6 September) — an
                instruction nobody asked for, about a thing they mostly do not want, taking the
                right-hand end of the one bar that says whose turn it is. A commissioner does
                not need it either: the designates sit in his rail, under the pick panel.
              */}
              {designates[mine.toUpperCase()] && (<>
                <span className="cbnote">
                  {managerShort(designates[mine.toUpperCase()]!)} is covering you
                </span>
                {/*
                  AND A WAY OUT OF IT THAT IS HIS OWN (Tim, 6 September: "add a Take Back Control
                  option next to where it says [GM] is covering you").
                  
                  A cover is set by a commissioner because a man said he would be in a car. He
                  then walks into the room at 8.40 and his own picks are being typed by somebody
                  else until he can find whoever set it. The one person who always knows the
                  arrangement is over is the man it was made for, so he ends it — the database
                  lets him remove his own entry and nothing else (see firestore.rules).
                */}
                <button type="button" className="cbtake"
                  title="End the cover — you enter your own picks again"
                  onClick={() => setConfirm('uncover')}>Take back control</button>
              </>)}
            </span>
          </div>
      ) : (
        <div className="onclock done"><b>Draft complete — all {board.length} picks are in.</b></div>
      ))}

      {/*
        A scheduled breather, stated rather than left looking like a stall. It appears when the
        round it follows has just finished, and a commissioner still has Pause for everything
        unscheduled — which is most of them.
      */}
      {inFlight.map((x) => (
        <div className="tradeband" key={x.t.id}>
          <span className="dbtag">Trade in flight</span>
          <span className="dbsay">
            {managerShort(x.a)} sends <code>{x.aSends.join(', ') || 'players'}</code> to {managerShort(x.b)}
            {' '}for <code>{x.bSends.join(', ') || 'players'}</code>
            {/*
              THE WINDOW IS A POSITION ON THE BOARD, NOT A TIME (2017-II-3). So the band is
              measured in picks at both ends: where the room was when it was announced, and how
              far the first pick in it still is. A clock time here would be the one number
              nobody can act on.
            */}
            <span className="mut">
              {/*
                AND ZERO IS NOT A PICK NUMBER. A deal agreed before the room started is announced
                before the draft, which is the strongest position a draft-day trade can be in —
                the whole board is still ahead of the pick it hangs on.
              */}
              {' · '}{(() => {
                const n = announcedAt(x.t.agreedAt ?? x.t.filedAt);
                return n > 0 ? <>announced at pick {n}</> : <>announced before the draft</>;
              })()}
              {x.first && <> · counters close when {x.first.label.replace(`${SEASON} `, '')} is made</>}
              {x.away !== null && <> &mdash; <b>{x.away === 0 ? 'on the clock now' : `${x.away} pick${x.away === 1 ? '' : 's'} away`}</b></>}
            </span>
          </span>
          <button type="button" className="linkbtn" onClick={() => goTo('league', 'trades')}>Open in Trades</button>
        </div>
      ))}

      {running && intermission !== null && (
        <div className="dbanner intermission">
          <span className="dbtag">Intermission</span>
          <span className="dbsay">
            Intermission, {intermission} minutes. Scheduled after round {(clock?.round ?? 1) - 1}.
            Picks can still be entered; clocks resume when a commissioner restarts them.
          </span>
          {/*
            The countdown is derived, not stored (review note 14): the app keeps "after round N,
            for M minutes" and no instant, and the round ended when its last pick was made, whose
            timestamp is on the pick. So the clock counts from that.
          */}
          <PickClock since={clockSince ?? sinceLastPick} seconds={intermission * 60} size="m" skin="light" className="dbclock" />
        </div>
      )}

      {/*
        THE BOARD IS FULL AND NOBODY HAS SAID SO (Tim, 9 September: "more prominently tell me I
        need to mark the main draft as complete before the farm rounds can start when the board is
        fully filled out").
        
        The farm rounds are gated on `status === 'complete'`, which is a commissioner PRESSING a
        button — and the only thing that ever said so was a line on the farm tab reading "the farm
        rounds open once the main draft is finished". On a board with all hundred and sixty picks
        in, that sentence looks like a statement about the picks. It is a statement about a button,
        on a different tab from the button, and the room sits waiting on it.
        
        So it is said here, where the last pick was just entered, with the button in the banner.
        It stands down the moment the draft is marked done — this is a prompt, not a status.
      */}
      {!mock && draft?.status === 'live' && stage === 'main' && !projected
        && board.length > 0 && made === board.length && (
        <div className="dbanner alldone">
          <span className="dbtag">All {board.length} in</span>
          <span className="dbsay">
            Every pick is entered. <b>The farm rounds cannot open until the main draft is marked
            done</b> — that is a commissioner pressing the button, not something the last pick
            does on its own.
          </span>
          {privileged && (
            <button type="button" className="btn primary dbact" onClick={() => setConfirm('done')}>
              Mark draft done
            </button>
          )}
        </div>
      )}

      {!mock && draft && draft.status !== 'live' && stage === 'main' && !projected && made < board.length && (
        <div className="dbanner notstarted">
          <span className="dbtag dashed">{draft.status === 'complete' ? 'Closed' : 'Not started'}</span>
          <span className="dbsay">
            {draft.status === 'complete'
              ? `The board is closed with ${made} of ${board.length} picks in.`
              : <>Keepers are placed.{settings?.draftDate ? <> Draft day is {shortDay(settings.draftDate)}.</> : ''} The clock starts when a commissioner presses <b>Start</b>, whenever that is.</>}
          </span>
        </div>
      )}

      {projected && !mock && (
        <div className="deadline warn">
          <span className="ico" aria-hidden="true">▲</span>
          <span>
            <b>Projected board.</b> The lottery has not been published, so this assumes it goes to
            form — worst finisher chooses first, the Toilet Bowl winner lifted to second
            (2020-II-1 §3) — and that nobody exercises the 3rd/4th swap or submits a slot
            preference. Keepers and traded picks are real; the seating is not. Picks are locked
            until the order is published.
          </span>
        </div>
      )}

      {pickRefused && (
        <div className="dbanner refused" role="alert">
          <span className="dbtag">Pick refused</span>
          <span className="dbsay">{pickRefused}</span>
        </div>
      )}

      {errors.length > 0 && (
        <div className="dbanner refused">
          <span className="dbtag">Keeper placement</span>
          <span className="dbsay">
            {errors.length === 1 ? 'One keeper could not be placed:' : `${errors.length} keepers could not be placed:`}
            <ul className="dblist">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
            <span className="dbfoot">
              The five refusals are the resolver&rsquo;s own: pick count &ne; {ROUNDS} · cost waterfalled past
              Rd 1 · moved later (2015-III-7) · pick traded or taken · nothing left to pay with.
            </span>
          </span>
        </div>
      )}

      {dropNote && (
        <div className="dbanner refused" role="alert">
          <span className="dbtag">Not a pick he can be charged to</span>
          <span className="dbsay">{dropNote}</span>
        </div>
      )}

      {keeperUndo.length > 0 && stage === 'main' && (
        <p className="kmoveback">
          {keeperUndo.length === 1
            ? 'One keeper has been moved on this board.'
            : `${keeperUndo.length} keeper moves on this board.`}
          {!mock && ' Saved to the slates.'}
          <button type="button" className="linkish" onClick={() => { void undoKeeperMove(); }}>
            Put the last one back
          </button>
        </p>
      )}

      {/*
        THE DECISION, AS A MODAL.

        It began as a banner at the top of the page on the reasoning that nothing in the drawing
        is a modal and a keeper move is not urgent enough to take the board away. That was wrong
        about where the reader is: he has just dropped a cell in the middle of a sixteen-round
        grid, and the answer appeared above the fold, off screen, while the cell under his cursor
        did not visibly change. It read as nothing happening.

        So it is a modal, in the two shapes the app already uses — a shadowed card over a scrim on
        desktop, the same sheet the phone confirm slides up from under 760px. Escape and the scrim
        both mean "leave it as it was", which is the safe answer.
      */}
      {/*
        DOCKED (26 August), for the reason set out in `src/ui/dock.tsx`: this overlay is
        `position: fixed`, and pull-to-refresh's transform on `main` makes `main` the containing
        block for every fixed descendant inside it. Until this, "fixed" meant "fixed to a
        page-height column" — which on the keeper screen put a confirmation 2,626px down an 844px
        phone, where it could be pressed by somebody who could not see it.
      */}
      {blockedMove && dock(
        <div className="kmodalwrap" role="dialog" aria-modal="true"
          aria-labelledby="kmove-head">
          <button type="button" className="kmodalscrim" aria-label="Leave it as it was"
            onClick={() => { setBlocked(null); setConfirmDrop(false); }} />
          <div className="kmodal">
            <span className="pcgrab" aria-hidden="true" />
            <b className="kmovehead" id="kmove-head">
              {said(blockedMove.other)} is on Round {blockedMove.toRound}
            </b>
            <p className="kmovewhy">{blockedMove.why}</p>

            <div className="kmoveopts">
              {blockedMove.ifEscalated && (
                <button type="button" className="btn primary"
                  onClick={() => {
                    void commitPins(blockedMove.gm, pinsOf(blockedMove.ifEscalated!));
                    setBlocked(null);
                  }}>
                  Keep both — {blockedMove.escalationWords}
                </button>
              )}
              {/*
                THE EARLIER-ONLY ANSWER. The free re-fit above is allowed to slide somebody onto
                a later, cheaper pick — legal, costs him nothing, and still a surprise when you
                dragged one cell. This one caps every other keeper at the round he already holds,
                so nobody drifts backwards; in practice that also tends to move fewer men. Only
                shown when it actually differs from the free answer, or it is the same button
                twice under two names.
              */}
              {blockedMove.ifBumped && (
                <button type="button" className="btn"
                  onClick={() => {
                    void commitPins(blockedMove.gm, pinsOf(blockedMove.ifBumped!));
                    setBlocked(null);
                  }}>
                  Bump earlier only — {blockedMove.bumpWords}
                </button>
              )}
              {mock || confirmDrop ? (
                <button type="button" className="btn danger"
                  onClick={() => {
                    void commitPins(
                      blockedMove.gm, pinsOf(blockedMove.ifCleared), blockedMove.other,
                    );
                    setBlocked(null);
                    setConfirmDrop(false);
                  }}>
                  {mock
                    ? <>Take {said(blockedMove.other)} off the slate</>
                    : <>Yes — remove {said(blockedMove.other)} from{' '}
                      {managerShort(blockedMove.gm)}’s slate</>}
                </button>
              ) : (
                <button type="button" className="btn" onClick={() => setConfirmDrop(true)}>
                  Take {said(blockedMove.other)} off the slate
                </button>
              )}
              <button type="button" className="btn ghost"
                onClick={() => { setBlocked(null); setConfirmDrop(false); }}>
                Leave it as it was
              </button>
            </div>

            {!blockedMove.ifEscalated && (
              <p className="kmovewhy">
                There is no way to keep both. {blockedMove.stuckWords}
              </p>
            )}
            {confirmDrop && !mock && (
              <p className="kmovewhy bad">
                This edits {managerShort(blockedMove.gm)}’s submitted slate: {blockedMove.other}{' '}
                stops being kept, and his luxury tax is recomputed from what is left. You can put
                it back from the line under the board.
              </p>
            )}
          </div>
        </div>
      )}

      {mock && stage === 'main' && (
        <MockTrades
          board={board}
          order={order}
          slates={contexts}
          dropped={droppedKeepers}
          trades={mockTrades}
          onAdd={(t) => setMockTrades((prev) => [...prev, { ...t, id: prev.length + 1 }])}
          onUndo={(id) => setMockTrades((prev) => prev.filter((t) => t.id !== id))}
        />
      )}

      {/*
        THE HOLE THE MOCK USED TO HAVE. It placed only DECLARED keepers, so a mock run in August
        drafted a board with forty of the best players still in the pool — a mock of a draft that
        will not happen. Guessing at them is what closes it, and the guesses are private and saved
        (see `firestore.rules`, `mockKeepers`).
      */}
      {/*
        AND IT GOES WHEN THE SLATES DO (Tim, 6 September: "remove 'Guess the Keepers' after
        keepers are locked"). It exists because a mock run in August drafted a board with forty
        of the best players still in the pool. Once the declaration gate is shut, every keeper on
        the board is a fact, and a card asking you to guess at facts is worse than no card.
      */}
      {mock && stage === 'main' && gateOf(settings, 'keepers', now).open && (
        <details className="card mkcard">
          <summary>
            <h3 className="striphead">Guess the keepers</h3>
            <span className="railsum">
              {mockKeeperSet
                ? `${Object.values(mockKeeperSet.byGm).reduce((n, r) => n + r.length, 0)} guessed`
                : 'not started'}
            </span>
          </summary>
          <MockKeepers
            gm={gm}
            order={order}
            options={KEEPERS.map((k) => ({
              player: k.player,
              gm: (GM_OF[k.team] ?? k.team).toUpperCase(),
              pos: k.pos,
              cost: k.cost,
              mandatory: k.mandatory,
              farm: k.farm,
            }))}
            store={store}
          />
        </details>
      )}

      {/*
        TWO BOARDS AND A MODE, AND THEY MUST NOT LOOK LIKE THREE OF ANYTHING.
        Main and Farm are destinations — the same league, two boards, pick one. The mock is not a
        third board, it is a switch that decides whether what you type is REAL, and drawn as a
        third identical chip it read as one more place to go. That is the mistake that puts a
        real first-round pick into a sandbox on the night.

        So: a segmented control for the two boards, and the mock separated across the row as a
        switch that says which mode you are in. When it is on, it is loud — the whole board takes
        a practice frame and the switch is the way out of it.
      */}
      {wall && (
        <Wallboard store={store} board={board} order={order} rounds={ROUNDS} teams={TEAMS}
          paused={paused} clockNow={pausedAt}
          clock={boardClock} front={restartTarget} since={cellSince} seconds={cellSeconds}
          lapsed={lapsedOpen} startedAt={startedAt} inTrade={inTradeCells} moved={movedMarks}
          tagged={taggedMarks}
          next={boardClock
            ? board.filter((p) => !p.player && p.overall > boardClock.overall)
              .sort((a, b) => a.overall - b.overall).slice(0, 3)
            : []}
          me={mine} onClose={() => setWall(false)} />
      )}

      {/*
        THE COMMISSIONER'S DIALOG. Modal, because setting a cover is a thing you do and then
        stop doing — it is not a panel to leave open beside the board on draft night. Every
        cover is a whole sentence rather than a select showing a name: "Rajan picks for Vishal"
        is what the room said out loud, and `End` is the verb that undoes it.
      */}
      {/*
        DOCKED, for the reason in `src/ui/dock.tsx`: a `position: fixed` overlay rendered inside
        `main` is fixed to `main`, not to the viewport, because pull-to-refresh's transform makes
        `main` the containing block. `tests/dockedOverlays.test.ts` fails on a new one that is not.
      */}
      {desigOpen && privileged && !mock && dock(
        <div className="desigmodal" role="presentation" onClick={() => setDesigOpen(false)}>
          <div className="desigbox" role="dialog" aria-modal="true" aria-label="Designates"
            onClick={(e) => e.stopPropagation()}>
            <header className="desigboxhead">
              <b>Designates</b>
              <span className="pill amber">Commissioner</span>
              <button type="button" className="desigx" aria-label="Close"
                onClick={() => setDesigOpen(false)}>&times;</button>
            </header>
            <ul className="desigrows">
              {order.filter((g) => designates[g.toUpperCase()]).map((g) => (
                <li key={g}>
                  <span className="desigsay">
                    <b>{managerShort(designates[g.toUpperCase()]!)}</b> picks for <b>{managerShort(g)}</b>
                  </span>
                  <button type="button" className="desigend"
                    title={`${managerShort(g)} goes back to entering his own picks`}
                    onClick={() => void setDesignate(g, '')}>End</button>
                </li>
              ))}
              {order.every((g) => !designates[g.toUpperCase()]) && (
                <li className="designone">Everyone is on his own picks.</li>
              )}
            </ul>
            <div className="designew">
              <span className="desigeyebrow">New designate</span>
              <div className="designewrow">
                <select aria-label="Who picks" value={coverBy}
                  onChange={(e) => setCoverBy(e.target.value)}>
                  {GMS.map((x) => <option key={x} value={x}>{managerShort(x)}</option>)}
                </select>
                <span className="desigfor">for</span>
                <select aria-label="Who is away" value={coverFor}
                  className={coverFor ? '' : 'placeholder'}
                  onChange={(e) => setCoverFor(e.target.value)}>
                  <option value="">GM away…</option>
                  {order.filter((g) => !designates[g.toUpperCase()]
                    && g.toUpperCase() !== coverBy.toUpperCase())
                    .map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
                </select>
                <button type="button" className="btn primary desigstart"
                  disabled={!coverBy || !coverFor}
                  onClick={() => {
                    void setDesignate(coverFor, coverBy);
                    setCoverFor(''); setCoverBy('');
                  }}>Start</button>
              </div>
            </div>
            <p className="desigboxfoot">
              The audit log records both names. Ends when the draft closes.
            </p>
          </div>
        </div>,
      )}

      {/*
        THE COMMISSIONER'S CONFIRMATIONS (Tim, 6 September).

        Three actions on the control row now ask before they act, and all three ask in the same
        box: restart this pick's clock, restart the whole draft, and mark the draft finished.
        Each one names what it is about to do in the room's own terms — whose clock, how many
        picks, how many cells are still empty — because "Are you sure?" over a draft board is a
        question nobody can answer.

        Docked for the same reason the designates dialog is: `main` carries pull-to-refresh's
        transform, so a fixed overlay inside it is fixed to `main` rather than to the viewport.
      */}
      {/* ====================================================================================
        YOUR TURN, AND THE TOP OF YOUR LIST IS STILL THERE.

        An offer, never an action. The board does not pick for anybody — a queue that fires by
        itself takes the one decision a manager came to make, and gets it wrong the first time
        somebody's plans changed between queueing and picking. So: his name, his pick, and two
        ways out — take him, or go and choose. Dismissing does not empty the list.
      ==================================================================================== */}
      {queueOffer && !mock && dock(
        <div className="pqoffer" role="presentation" onClick={() => setQueueOffer(null)}>
          <div className="pqbox" role="dialog" aria-modal="true" aria-label="Your pick"
            onClick={(e) => e.stopPropagation()}>
            <header className="pqboxhead">
              <b>You&rsquo;re up</b>
              <code>{pickCode(queueOffer.pick, TEAMS)}</code>
              <span className="pill green">Top of your list</span>
            </header>
            <div className="pqboxwho">
              <span className="pqboxname">
                <b>{splitName(queueOffer.entry.player).last}</b>{' '}
                <i>{splitName(queueOffer.entry.player).first}</i>
              </span>
              <span className="pqboxmeta">
                {queueOffer.entry.position && (
                  <span className={`poschip ident pos-${queueOffer.entry.position}`}>
                    {queueOffer.entry.position}
                  </span>
                )}
                <NflMark player={queueOffer.entry.player} />
              </span>
            </div>
            <p className="pqboxsay">
              He is still on the board. Nothing is entered until you say so.
            </p>
            <div className="pqboxact">
              <button type="button" className="btn ghost"
                onClick={() => { setTarget(queueOffer.pick.overall); setQueueOffer(null); }}>
                Choose somebody else
              </button>
              {/*
                THE DIALOG IS THE CONFIRM. It named the man, so sending him back through the
                panel's confirm card would ask the same question twice — and the second one
                would arrive after the dialog had gone, over an empty panel.
              */}
              <button type="button" className="btn primary"
                onClick={() => {
                  const { pick, entry } = queueOffer;
                  setQueueOffer(null);
                  void makePick(entry.player, pick);
                  queueDrop(entry.player);
                }}>
                Pick {splitName(queueOffer.entry.player).last}
              </button>
            </div>
          </div>
        </div>,
      )}

      {/*
        THE ONE DIALOG HERE A GM CAN OPEN. Every other case in this box is a commissioner's, so
        the gate used to be `privileged`; taking your own cover back is the man's own business
        and is deliberately let through it.
      */}
      {confirm && (privileged || confirm === 'uncover') && !mock && dock(
        <div className="ccconfirm" role="presentation"
          onClick={() => { if (!wiping) setConfirm(null); }}>
          <div className={`ccbox ${confirm}`} role="dialog" aria-modal="true"
            aria-label={confirm === 'done' ? 'Finish the draft'
              : confirm === 'uncover' ? 'Take back control'
                : confirm === 'restart-draft' ? 'Restart the draft' : 'Restart the pick clock'}
            onClick={(e) => e.stopPropagation()}>
            <header className="ccboxhead">
              <b>
                {confirm === 'done' ? 'Finish the draft'
                  : confirm === 'uncover' ? 'Take back control'
                    : confirm === 'restart-draft' ? 'Restart the draft'
                      : 'Restart the pick clock'}
              </b>
              {confirm !== 'uncover' && <span className="pill amber">Commissioner</span>}
            </header>
            <div className="ccboxsay">
              {confirm === 'restart-pick' && (<>
                {/*
                  IT NAMES THE PICK THAT WILL ACTUALLY RESTART, which in a jam is the newest of
                  the open ones rather than the oldest — see `openPicks`. A dialog that says
                  "Ryan gets his time back" and then hands it to Tim is worse than no dialog.
                */}
                <p>
                  {restartTarget
                    ? <><b>{managerShort(restartTarget.owner)}</b> gets his full time back on
                      {' '}<code>{pickCode(restartTarget, TEAMS)}</code>, from now.</>
                    : <>The clock starts again from now.</>}
                </p>
                {/*
                  AND "BEHIND HIM" DOES NOT COUNT HIM. In a jam the man at the front is lapsed
                  too, so the count included the very pick the dialog had just named: four late
                  picks, one of them Ari's, read as "the 4 picks behind him" (Tim, 8 September).
                */}
                {(() => {
                  const behind = lapsedNow.filter((o) => o.pick.overall !== restartTarget?.overall);
                  if (!behind.length) return null;
                  const one = behind.length === 1;
                  return (
                    <p className="ccboxkeep">
                      The {one ? 'pick' : `${behind.length} picks`} behind him
                      {' '}{one ? 'stays' : 'stay'} open and
                      {' '}{one ? 'keeps its' : 'keep their'} elapsed time.
                    </p>
                  );
                })()}
              </>)}
              {confirm === 'restart-draft' && (<>
                <p>
                  This clears <b>{board.filter((p) => p.player && !p.keeper).length} picks</b> made
                  {' '}tonight and puts the board back to <b>Not started</b>.
                </p>
                <p className="ccboxkeep">
                  Keepers, franchise tags and every traded pick stay exactly where they are.
                  {' '}Nothing else on this board is touched.
                </p>
                <p className="ccboxwarn">There is no undo for this.</p>
              </>)}
              {confirm === 'uncover' && (<>
                <p>
                  <b>{managerShort(designates[mine.toUpperCase()] ?? '')}</b> stops entering your
                  {' '}picks. You enter your own from here, including any that are open now.
                </p>
                <p className="ccboxkeep">
                  Picks he already made stay made, under his name. A commissioner can set the
                  {' '}cover again if you need to step out later.
                </p>
              </>)}
              {confirm === 'done' && (<>
                <p>
                  The board closes, the clock stops, and the room is shown a finished draft.
                </p>
                {board.some((p) => !p.player) && (
                  <p className="ccboxwarn">
                    The board is not completely filled out
                    {' '}&mdash; <b>{board.filter((p) => !p.player).length}</b> of {board.length}
                    {' '}{board.filter((p) => !p.player).length === 1 ? 'cell is' : 'cells are'}
                    {' '}still empty. Are you sure?
                  </p>
                )}
                <p className="ccboxkeep">You can reopen it afterwards.</p>
              </>)}
            </div>
            <div className="ccboxact">
              <button type="button" className="btn ghost" disabled={wiping}
                onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button"
                className={`btn ${confirm === 'restart-draft' ? 'danger' : 'primary'}`}
                disabled={wiping}
                onClick={() => {
                  if (confirm === 'uncover') { void setDesignate(mine, ''); setConfirm(null); return; }
                  if (confirm === 'restart-pick') { void restartClock(); setConfirm(null); return; }
                  if (confirm === 'done') { void setStatus('complete'); setConfirm(null); return; }
                  void restartDraft().then(() => setConfirm(null));
                }}>
                {wiping ? 'Clearing the board…'
                  : confirm === 'uncover' ? 'Take back control'
                    : confirm === 'restart-pick' ? 'Restart the clock'
                      : confirm === 'restart-draft' ? 'Clear the board'
                        : 'Finish the draft'}
              </button>
            </div>
          </div>
        </div>,
      )}


      {stage === 'farm' ? (
        /*
          THE TOGGLE REACHES THE FARM ROUNDS TOO (Tim, 8 September: "the sidebar collapse button
          should affect the farm rounds view too"). It is the same button over the same kind of
          rail, and it sat in the row above a screen it did nothing to — which reads as broken
          rather than as not applying.
        */
        <FarmDraft order={order} actor={actor} privileged={privileged} role={role} store={store}
          sandbox={mock} projected={projected}
          /* The board is this screen's to count, so the farm tab is told rather than asking. */
          mainBoardFull={!mock && board.length > 0 && made === board.length
            && draft?.status !== 'complete'}
          onFinishDraft={privileged && !mock ? () => setConfirm('done') : undefined}
          railOpen={railOpen} railW={railW} onRailW={setRailW} />
      ) : (
      <div className={`draftcols${view === 'team' || !railOpen ? ' norail' : ''}`}
        style={{ '--rail': `${railW}px` } as CSSProperties}>
       {view !== 'team' && railOpen && (
       <div className="railcol">
        {/*
          The rail's own confirm is the DESKTOP's. On a phone the sheet is the confirm, so the
          whole panel stands down while one is pending rather than sitting behind the scrim as an
          empty card with a heading — see `.pickpanel.pending` in the stylesheet.
        */}
        <section className={`card pickpanel${pending ? ' pending' : ''}`}>
          <div className="pphead">
            <h3 className="striphead">
              {/*
                WITH SEVERAL PICKS LIVE THE PANEL IS NOT ABOUT ONE OF THEM (17a rev C). A lapsed
                clock leaves more than one cell enterable at once, so the title stops naming a
                pick and the chips below become the choice: which of the open picks is being
                typed into. With one open pick the old title is better and stays.
              */}
              {projected && !mock ? 'Locked'
                : !entering ? 'Draft complete'
                  : mock ? 'Enter any pick'
                    : manyOpen ? 'Entering a pick'
                      : canEnterTarget
                        ? `Enter ${pickCode(entering, TEAMS)}, ${managerShort(entering.owner)}`
                        : notYetOpen
                          ? (coveringEntering
                            ? `${managerShort(entering.owner)}'s ${pickCode(entering, TEAMS)}, later`
                            : `${pickCode(entering, TEAMS)} is yours, later`)
                          : 'Not your pick'}
              {mock && <span className="pill violet">Mock</span>}
            </h3>
            {manyOpen && (
              <div className="pptargets" role="tablist" aria-label="Which open pick">
                {myOpen.map((o) => (
                  <button type="button" key={o.pick.overall} role="tab"
                    aria-selected={entering?.overall === o.pick.overall}
                    className={`pptarget${entering?.overall === o.pick.overall ? ' on' : ''}${o.lapsed ? ' late' : ' live'}`}
                    title={o.lapsed
                      ? `${managerShort(o.pick.owner)}'s clock ran out — the pick is still his`
                      : `${managerShort(o.pick.owner)} is on the clock`}
                    onClick={() => setTarget(o.pick.overall)}>
                    <i className="dot" aria-hidden="true" />
                    <code>{pickCode(o.pick, TEAMS)}</code>
                    <span className="sep" aria-hidden="true">·</span>
                    {/*
                      THE NAME IS ITS OWN ELEMENT so the desktop chip can put it on a second
                      line under the pick code. A bare text node cannot be given a line of
                      its own, and four open picks across a three-hundred-pixel rail need one.
                    */}
                    <span className="ppwho">{managerShort(o.pick.owner)}</span>
                  </button>
                ))}
              </div>
            )}
            {/*
              THE SUBTITLE IS THE STATE (17a, 17b). A commissioner who targeted a cell other than
              the one on the clock is told which is which; a GM waiting on somebody else is told
              who; nobody gets "not your pick" over a confirm block for another man.
            */}
            {entering && !manyOpen && !(projected && !mock) && (
              <p className="ppsub">
                {/*
                  NOTHING IN THE PANEL SAYS "THIS IS A MOCK" ANY MORE (Tim, 6 September). The
                  pill beside the heading says it, the clock bar is violet and the switch is on;
                  a sentence under the title said it a fourth time and pushed the field down.
                */}
                {mock ? null
                  : boardClock && entering.overall !== boardClock.overall
                    ? <>Targeted from the board · {managerShort(boardClock.owner)} is on the clock at {pickCode(boardClock, TEAMS)}.</>
                    : canEnterTarget
                      ? (ownsEntering ? 'Your pick.' : `${managerShort(entering.owner)} is on the clock; you are entering for him.`)
                      : notYetOpen
                        ? <>Nobody enters a pick before the board reaches it.{coveringEntering
                          ? <> You are covering {managerShort(entering.owner)}; it opens when the board gets there.</>
                          : <> Queue him on your picklist instead and he will be waiting.</>}</>
                        : <>
                          {/*
                            TWO LINES, AND THE SECOND ONE IS THE ONLY THING HE WANTED (Tim,
                            6 September: 'just say "[GM] is on the clock / Your next pick: 3.04
                            - 7 picks away"').

                            The card used to say it three times over — a title, this line with a
                            jump link, and a paragraph underneath explaining that the pick belongs
                            to somebody else and he should ask him. A man watching another man's
                            pick is not confused about whose it is. What he actually wants off
                            this rail is when he is up, so that is what is left: whose clock it
                            is, and how far away his own pick is. The code is the jump.
                          */}
                          {managerShort(entering.owner)} is on the clock
                          {myNext && myNext.overall !== entering.overall && (() => {
                            const from = (boardClock ?? entering).overall;
                            const away = myNext.overall - from;
                            return (
                              <><br />Your next pick:{' '}
                                <button type="button" className="linkbtn"
                                  title="Show it on the board"
                                  onClick={() => setTarget(myNext.overall)}>
                                  {pickCode(myNext, TEAMS)}
                                </button>
                                {away > 0
                                  ? <> &mdash; {away} pick{away === 1 ? '' : 's'} away</>
                                  : <> &mdash; open now</>}
                              </>
                            );
                          })()}
                        </>}
              </p>
            )}
            {target !== null && !manyOpen && (
              <button type="button" className="linkbtn ppback" onClick={() => setTarget(null)}>
                back to the clock
              </button>
            )}
          </div>

          {/* Mock only, by design — the live draft has no Fill (11a). */}
          {mock && clock && (
            <div className="fillrow">
              <span className="mut filllab">Fill:</span>
              <button type="button" className="btn small"
                disabled={adpRank.size === 0 || fillable.thisPick.length === 0}
                title={adpRank.size === 0
                  ? 'Load an ADP file on Admin › Draft tools first'
                  : 'Enter the pick on the clock from the ADP consensus'}
                onClick={() => void fill(fillable.thisPick)}>
                This Pick
              </button>
              <button type="button" className="btn small"
                disabled={adpRank.size === 0 || fillable.toMyTurn.length === 0}
                title={adpRank.size === 0
                  ? 'Load an ADP file on Admin › Draft tools first'
                  : `Enter the next ${fillable.toMyTurn.length} pick(s) from the ADP consensus`}
                onClick={() => void fill(fillable.toMyTurn)}>
                To My Next Pick{fillable.toMyTurn.length > 0 && ` · ${fillable.toMyTurn.length}`}
              </button>
              <button type="button" className="btn small"
                disabled={adpRank.size === 0 || fillable.all.length === 0}
                title={adpRank.size === 0
                  ? 'Load an ADP file on Admin › Draft tools first'
                  : `Enter all ${fillable.all.length} remaining pick(s) that are not yours,`
                    + ' from the ADP consensus'}
                onClick={() => void fill(fillable.all)}>
                Entire Board{fillable.all.length > 0 && ` · ${fillable.all.length}`}
              </button>
              <span className="mut fillsrc">from the ADP consensus</span>
            </div>
          )}

          {projected && !mock ? (
            <p className="note">
              Nothing can be entered against a projected order. Publish the lottery on
              <b> Order &amp; lottery</b>, or play it out on <b>Mock draft</b>.
            </p>
          ) : !entering ? (
            <p className="note">Every pick is in.</p>
          ) : !canEnterTarget ? (
            <>
              {/*
                TWO REFUSALS, TWO SENTENCES (Tim, 6 September). "That pick belongs to somebody
                else" is wrong when the pick is his own and the board has not got there yet —
                and it is the more common of the two, because a GM clicks his own next cell
                constantly. He is told what to do instead, which is the picklist.
              */}
              {notYetOpen && (
                <p className="note bad">
                  <b>{pickCode(entering, TEAMS)}</b> is{' '}
                  {coveringEntering
                    ? <>{managerShort(entering.owner)}&rsquo;s, and yours to enter while you cover him</>
                    : <>yours</>}, but the board is at{' '}
                  {boardClock ? <b>{pickCode(boardClock, TEAMS)}</b> : 'an earlier pick'}.
                  Picks are entered when they open, not before.
                </p>
              )}
              {/*
                11a puts a way out of this state rather than leaving it a dead end: the thing a
                GM wants while somebody else picks is his own next pick, queued up and ready. On
                the "not your pick" side the subtitle now carries that jump, so the button here
                would be the same link twice.
              */}
              {notYetOpen && myNext && myNext.overall !== entering.overall && (
                <button type="button" className="btn small"
                  onClick={() => setTarget(myNext.overall)}>
                  Jump to my {pickCode(myNext, TEAMS)}
                </button>
              )}
            </>
          ) : (
            <>
              {/*
                THE PANEL IS THE CONFIRM NOW (Tim, 6 September). Its field, its position chips
                and its result list all moved down into Best available, which had a ranking,
                chips and rows of its own — two lists of players stacked on one rail, doing the
                same job with different controls. What is left here is what only this card can
                say: which pick is being entered, and the confirm block when a man is chosen.
              */}
              {!pending && picksForOthers && (
                <div className="ppsearch">
                  <input className="search" type="search" value={panelQuery}
                    autoFocus={!isCoarsePointer()}
                    placeholder={ownsEntering ? 'Last name, initials or club…'
                      : `Enter ${pickCode(entering, TEAMS)} for ${managerShort(entering.owner)}…`}
                    aria-label={`Search for a player to enter at ${pickCode(entering, TEAMS)}`}
                    onChange={(e) => setPanelQuery(e.target.value)} />
                  {panelQuery.trim().length >= 2 && (
                    <ul className="playerlist">
                      {panelResults.map((pl) => (
                        <li key={`${pl.name}|${pl.pos}`}>
                          <button type="button"
                            onClick={() => { choose(pl.name); setPanelQuery(''); }}>
                            <b>{pl.name}</b>
                            {positionsOf(pl).map((x) => (
                              <span key={x} className={`poschip pos-${x}`}>{x}</span>
                            ))}
                            <span className="mut bye">bye {pl.bye ?? '—'}</span>
                          </button>
                        </li>
                      ))}
                      {panelResults.length === 0 && (
                        <li className="note">
                          Nobody left matching that.
                          {privileged && (
                            <button type="button" className="linkbtn"
                              onClick={() => { choose(panelQuery.trim()); setPanelQuery(''); }}>
                              draft &ldquo;{panelQuery.trim()}&rdquo; anyway
                            </button>
                          )}
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              )}
              {/*
                THE POINTER IS FOR THE LIVE BOARD ONLY. In a mock every row in the card below
                picks with one tap and nothing needs confirming, so a line explaining a
                round trip that does not happen was just another line.
              */}
              {!pending && !picksForOthers && !mock && (
                <p className="ppfind">
                  Search or star a name in <b>My picklist</b> below; it comes back here to
                  confirm.
                </p>
              )}
              {pending && (
              /*
               * 11b, state one: chosen but not committed. The card carries the pick, the clock and
               * the man, and says plainly what confirming does — including the asymmetry, that a GM
               * cannot take it back and a commissioner can.
               */
              <div className={`confirmpick ${ownsEntering ? '' : 'forother'}`}>
                {/*
                  "YOUR PICK" is only true when it is. A commissioner entering somebody else's
                  pick gets the same confirm step — that is the point of the step — but in amber
                  and saying whose it is, because the red state means "you are up".
                */}
                {/*
                  ONE HEAD LINE, NOT TWO (Tim, 6 September: "make this confirmation dialog a lot
                  more compact in the live draft"). Whose pick it is and which pick it is were a
                  stacked eyebrow and a stacked mono line — two rows and two gaps to say eight
                  words. They read as one sentence, so they sit on one line, the clock riding the
                  right-hand end where it does on every other bar in this app.
                */}
                <div className="cphead">
                <span className="cptag">
                  {ownsEntering ? 'Your pick' : `Entering for ${managerShort(entering.owner)}`}
                </span>
                {/*
                  THE PICK AND ITS CLOCK. On a lapsed cell this is the figure the commissioner is
                  acting on — how long the room has been waiting — so it belongs inside the block
                  he is about to press, not only up on the bar.
                */}
                <span className="cpmeta">
                  <b>{pickCode(entering, TEAMS)}</b>
                  <span className="sep" aria-hidden="true">·</span>
                  <PickClock now={pausedAt ?? undefined} since={startedAt.get(entering.overall) ?? clockSince}
                    seconds={entering.overall === (boardClock?.overall ?? -1) ? cellSeconds : null}
                    size="s" skin="light" className="cpclock" />
                  {/*
                    The word ELAPSED came off with the second line it was forcing: `+10:36` says
                    it already — every counting-up figure in this app wears the plus — and the
                    clock's own tooltip spells it out for anybody it does not.
                  */}
                </span>
                </div>
                <div className="cpwho">
                  <span className="cpname"><b>{splitName(pending.name).last}</b> {splitName(pending.name).first}</span>
                  {pending.pos && <span className={`poschip ident pos-${pending.pos}`}>{pending.pos}</span>}
                  <NflMark player={pending.name} />
                </div>
                {/*
                  THE CONSEQUENCE, in the needs engine's own three sentences (review note 15).
                  Nothing here is arithmetic the app does not do, and it is one line: the room
                  clause moved onto the button's tooltip, where it is read once rather than every
                  time somebody arms a pick.
                */}
                {pendingFills && <p className="cpsay">{pendingFills}</p>}
                <div className="cpacts">
                  <button type="button" className="btn primary wide"
                    title={`Confirming writes ${pickCode(entering, TEAMS)} for the whole room; only a commissioner can clear it.`}
                    onClick={() => void makePick(pending.name)}>
                    {ownsEntering ? 'Confirm pick' : `Confirm for ${managerShort(entering.owner)}`}
                  </button>
                  <button type="button" className="btn" onClick={() => setPending(null)}>
                    Cancel
                  </button>
                </div>
              </div>
              )}
            </>
          )}

        {/*
          DESIGNATES, IN THE RAIL UNDER THE PICK PANEL (17a rev C).

          It was a form the width of the page below the board — the one thing left down there
          after the commissioner's card became the control bar, and a long way from the panel
          whose behaviour it changes. Collapsed it is the exceptions and nothing else: one pill
          per cover, reading cover → covered, because that is the direction the room says it
          ("Rajan's got Vishal"). The chevron opens the selects and `+ cover…`.
        */}
        {/*
          DESIGNATES: A SUMMARY IN THE RAIL, THE WORK IN A DIALOG (Tim, 6 September).

          Collapsed it is the exceptions and nothing else — one pill per cover, reading cover →
          covered, because that is the direction the room says it ("Rajan's got Vishal"). The
          chevron opens the commissioner's dialog, where a cover is a sentence with an End
          beside it and a new one is built left to right: who picks, for whom, Start.
        */}
        {privileged && !mock && (
          <div className="desigsec">
            <button type="button" className="desighead" aria-expanded={desigOpen}
              aria-haspopup="dialog"
              onClick={() => setDesigOpen(true)}>
              <b className="desiglab">Designates</b>
              <span className="pill amber">Commissioner</span>
              <i className="chev" aria-hidden="true">›</i>
            </button>
            <div className="desigpills">
              {order.filter((g) => designates[g.toUpperCase()]).map((g) => (
                <span className="desigpill" key={g}
                  title={`${managerShort(designates[g.toUpperCase()]!)} enters ${managerShort(g)}'s picks`}>
                  {managerShort(designates[g.toUpperCase()]!)}
                  <i aria-hidden="true">→</i>
                  {managerShort(g)}
                </span>
              ))}
              {order.every((g) => !designates[g.toUpperCase()])
                && <span className="mut">Everyone is on his own picks.</span>}
            </div>
          </div>
        )}
      </section>

        {/* ====================================================================================
          THE PICKLIST (Tim, 6 September).

          Directly under the pick panel, because it is the same job at a different moment: the
          panel is what you do on your turn, this is what you do for the twenty-seven picks
          before it. Yours alone — nobody else can read it, not even a commissioner — and it
          never picks for you.

          Reorderable two ways on purpose. Drag is what everybody reaches for and is wrong about
          half the time on a trackpad at a table; the arrows always work and are the only thing
          that works on a phone.
        ==================================================================================== */}

        {/*
          The rail, per 8a — and per 11a the live board keeps it, because "what am I short of"
          is a draft-night question, not a rehearsal one. Each section collapses to a headline
          that summarises itself, so a closed section still answers at a glance.

          Rebuilt 2026-08-16 against the drawn canvas after a first pass built from the README's
          one-line summaries got four things wrong. Anything below that departs from the drawing
          says so in its own comment.
        */}
        {/*
          NOT WHEN THE PANE IS ALREADY IT. On a phone `Available` is one of the four panes, and it
          renders this same `availList` from the same `bestAvailable` — so with that pane selected
          the rail was showing the identical list a second time, a few hundred pixels lower, under
          a different heading. Two names for one list reads as two lists.
        */}
        {/* ====================================================================================
          ONE CARD (Tim, 6 September: "combine picklist and best available into one card with
          picklist at the top, search under that, then best available list below").

          They were two cards doing one job with two search fields between them — one to add a
          name to the queue, one to find a name to pick. The queue is what you have decided; the
          ranking is what is left to decide from; and one field serves both, because every row
          in the list carries a star and a pick button. Read top to bottom it is: what I want,
          what I am looking for, and what there is.
        ==================================================================================== */}
        {view !== 'avail' && (
        <details className="card railsec bestsec picksec" open>
          {/*
            THE CARD IS THE PICKLIST (Tim, 6 September: "remove the Best Available/Top100
            header. replace with My Picklist. Just let the list below speak for itself"). The
            ranking underneath needs no heading — a numbered column of players under a search
            field is not something anybody has to have named.
          */}
          <summary>
            {/*
              THE TITLE NAMES THE PAIR (Tim, 7 September: "change card title to Player Lists").
              The card holds two of them and the tab row under this says which one you are looking
              at, so the title does not need to spell both out again.
            */}
            <h3 className="striphead">
              Player Lists
            </h3>
            {/*
              ONE SHORT LINE. `.railsum` is `white-space:nowrap`, so a summary long enough to
              want two lines instead sets the card's minimum width and pushes the chevron, the
              filter chips and the list edges out past the rail.
            */}
            {/*
              THE COUNT SAYS THERE IS MORE THAN YOU CAN SEE (Tim, 7 September: "picklist shows 4
              players at a time but if there are more than that there's no indication that they
              exist"). The list is a 300px scroller; on a phone that is four rows and a sliver,
              and a sliver is only an affordance once you already suspect there is something
              under it. `9 queued` needs no gesture and no scrollbar.
            */}
            {/*
              The count moved to the tab, where it belongs — it is a fact about that list, not
              about the card. What is left is the state of whichever list is open: the board says
              what it is filtered to, the queue says that nobody else can see it.
            */}
            <span className="railsum">
              {pqTab === 'mine' ? 'private' : (query.trim() ? `searching "${query.trim()}"` : '')}
            </span>
            <i className="chev" aria-hidden="true" />
          </summary>

          {/*
            TWO LISTS, TWO TABS — the same row the reference card at the foot of the rail uses,
            because it is the same question: which pane of this card am I looking at.

            The count rides the queue's own label. It is the one number that says the list is
            there at all when it is not the tab you are on, and it is the answer to "have I
            queued him already" without opening anything.
          */}
          {!projected && (
          <div className="reftabs pqtabs" role="tablist" aria-label="Player list and picklist">
            <button type="button" role="tab" aria-selected={pqTab === 'board'}
              className={`reftab${pqTab === 'board' ? ' on' : ''}`}
              onClick={() => showTab('board')}>Player list</button>
            <button type="button" role="tab" aria-selected={pqTab === 'mine'}
              className={`reftab${pqTab === 'mine' ? ' on' : ''}`}
              onClick={() => showTab('mine')}>
              <span className="pqstar" aria-hidden="true">&#9733;</span>
              My picklist
              <span className="pqcount">{queueView.length}</span>
            </button>
          </div>
          )}

          {/* -- what I want, in order ---------------------------------------------------- */}
          {!projected && pqTab === 'mine' && (
          <div className={`pqblock${mock ? ' readonly' : ''}`}>
          {queueView.length === 0 ? (
            <p className="note railpad">
              {mock
                ? 'Nobody on your list is left on this board.'
                : `Queue the players you want next. If somebody takes one, he comes off the list `
                  + '— and when your pick opens, the top name still standing is offered to you.'}
            </p>
          ) : (
            <PickQueue items={queueView} readonly={mock} onMove={queueMove} onRemove={queueDrop}
              onPick={entering ? choose : null} canPick={canEnterTarget}
              pickSay={entering ? `Only ${managerShort(entering.owner)} can enter ${pickCode(entering, TEAMS)}` : undefined} />
          )}
          </div>
          )}

          {(projected || pqTab === 'board') && (<>

          {/* -- what I am looking for, and what there is --------------------------------- */}
          <div className="basearch">
            <input
              className="search"
              type="search"
              /* Desktop: the caret is waiting for you. Touch: see `isCoarsePointer`. */
              autoFocus={!isCoarsePointer() && canEnterTarget && !pending}
              placeholder="Last name, initials or club…"
              value={query}
              aria-label="Search for a player"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {/*
            MULTI-SELECT, EXCEPT `All` (Tim, 6 September). "The best receiver or tight end left"
            is one question and a single-select answered a different one. `All` is not a
            position, so it is not a toggle either: pressing it clears the rest.
          */}
          <div className="bafilter" role="group" aria-label="Filter by position">
            <button type="button" aria-pressed={baFilters.length === 0}
              className={`chip${baFilters.length === 0 ? ' on' : ''}`}
              onClick={() => setBaFilters([])}>All</button>
            {RAIL_POS.map((x) => (
              <button type="button" key={x} aria-pressed={baFilters.includes(x)}
                className={`chip${baFilters.includes(x) ? ' on' : ''}`}
                onClick={() => setBaFilters((prev) => (prev.includes(x)
                  ? prev.filter((y) => y !== x)
                  : [...prev, x]))}>{x}</button>
            ))}
            {/*
              NO `refresh rosters` HERE (Tim, 6 September). Six chips fill this row to the pixel,
              so the link was clipped wherever it was put — and it is a maintenance job on the
              player bank rather than anything to do with a pick. It lives on the players index,
              which is the screen about the bank.
            */}
          </div>
          {/*
            11a: when the pick is not yours the pick button GOES GREY with a tooltip naming who
            can use it, rather than disappearing — a row that loses a control on somebody else's
            turn changes shape ten times a round. That lives in `availList`, which the phone's
            Available tab renders too.
          */}
          {availList()}
          </>)}
        </details>
        )}

        {/*
          THE REFERENCE CARD (Pass 17 rev B, the rail call). On the night the only two things
          anybody opens are the pick panel and Best available, so those are the rail's only fixed
          cards; needs, counts, byes and the room fold into one card with four tabs at the rail
          foot — one glance-height, no accordion stack to manage mid-pick. Room is the default
          tab while live (covers are operational); an amber condition inside a hidden tab
          surfaces as a dot on its label, never a count. `RAIL_LAYOUT` flips back to the
          five-card stack for one release if the room disagrees.
        */}
        <section className="card refcard">
          <div className="reftabs" role="tablist" aria-label="Reference">
            {/*
              NO DOTS ON THE TABS (Tim, 6 September). Three of the four carried an amber dot
              whenever something inside them was short, which on a board four rounds in is
              nearly always — so the row wore three permanent warnings that said nothing about
              which one to open. What is short is stated inside each pane, where it can be acted
              on: an amber figure in Counts, an amber week in Byes.
            */}
            {(mock ? REF_TABS.filter((t) => t.key !== 'room') : REF_TABS).map((t) => (
              <button type="button" key={t.key} role="tab" aria-selected={refTab === t.key}
                className={`reftab${refTab === t.key ? ' on' : ''}`}
                onClick={() => setRefTab(t.key)}>
                {t.label}
              </button>
            ))}
            <button type="button" className={`refchev${refOpen ? ' open' : ''}`}
              aria-expanded={refOpen}
              title={refOpen ? 'Fold the reference card away' : 'Open the reference card'}
              onClick={() => setRefOpen((v) => !v)}>
              <span className="vh">{refOpen ? 'Collapse' : 'Expand'}</span>
            </button>
          </div>
          {refOpen && (<>
        {refTab === 'roster' && (
        <div className="refpane rostersec">
          {/*
            YOUR TEAM, BY SLOT (17a rev C). The tab that replaced Needs, and it answers the same
            question better: a list of who you actually have, in the order the lineup is filled,
            says "no quarterback" more plainly than a bar labelled QB ever did. An empty slot is
            an em dash rather than a zero — a known nothing, drawn as nothing.
          */}
          <ul className="rosterslots">
            {RAIL_POS.map((pos) => {
              const held = myRoster
                .map((n) => ({ name: n, info: nflInfoOf(n) }))
                .filter((x) => (x.info?.pos ?? '').toUpperCase() === pos);
              return (
                <li key={pos}>
                  <span className="slot">{pos}</span>
                  {held.length === 0 ? <span className="none" aria-label="none yet">—</span> : (
                    <ol className="rosterheld">
                      {held.map((x, i) => {
                        const { last, first } = splitName(x.name);
                        const cell = board.find((b) => b.player === x.name);
                        return (
                          <li key={x.name}>
                            <span className="n">{i + 1}.</span>
                            <span className="pn"><b>{last}</b> <i>{first}</i></span>
                            {cell && taggedMarks.has(cell.overall)
                              && <span className="kbadge t" title="Held under the franchise tag">T</span>}
                            <NflMark player={x.name} />
                            <span className="bye">{x.info?.bye ?? '—'}</span>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        )}

        {refTab === 'counts' && (
        <div className="refpane countsec">
          <p className="refsum">{made} picks in</p>
          <table className="railcounts">
            <thead>
              <tr>
                <th><span className="vh">Manager</span></th>
                {RAIL_POS.map((pos) => <th key={pos}>{pos}</th>)}
              </tr>
            </thead>
            <tbody>
              {order.map((g) => {
                const row = counts.get(g.toUpperCase());
                const mine = g.toUpperCase() === gm.toUpperCase();
                return (
                  <tr key={g}
                    style={mine ? { background: `hsl(${hueFor(g)} 62% 98%)` } : undefined}>
                    <th className="nm">{managerShort(g)}</th>
                    {RAIL_POS.map((pos) => {
                      const n = row?.[pos] ?? 0;
                      // Amber is "below the starting lineup", not "zero" — one receiver in a
                      // two-receiver league is the same hole as none. Before the first pick
                      // nobody is short of anything yet, and fifty amber zeros on a board that
                      // has not started is the paint chart the colour rule exists to prevent.
                      return (
                        <td key={pos} className={made > 0 && belowStarters(pos, n) ? 'short' : ''}
                          title={`${managerShort(g)} · ${n} ${pos}`}>{n}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}

        {refTab === 'byes' && (
        <div className="refpane byesec">
          {/*
            TWO QUESTIONS, TWO VIEWS (17a rev C). "Which of my weeks are stacked" is a roster
            question and reads as a list; "who else is exposed in week 11" is a trade question
            and reads as ten rows. They were stacked in one pane and the first one — the one a
            GM actually asks between picks — was under a chart of itself.
          */}
          <div className="byeswitch" role="tablist" aria-label="Whose byes">
            <button type="button" role="tab" aria-selected={byeView === 'mine'}
              className={`pane${byeView === 'mine' ? ' on' : ''}`}
              onClick={() => setByeView('mine')}>My team</button>
            <button type="button" role="tab" aria-selected={byeView === 'league'}
              className={`pane${byeView === 'league' ? ' on' : ''}`}
              onClick={() => setByeView('league')}>League</button>
          </div>

          {byeView === 'mine' ? (
            <ul className="byemine">
              {/*
                WORST WEEK FIRST (Tim, 6 September). Sorted by week number, the list read as a
                calendar and buried the only line that matters — the week three of your starters
                are out — somewhere in the middle. The question a GM asks between picks is "where
                am I thin", so the answer leads: most players on bye first, and the earlier week
                breaks a tie, because the earlier one is the one he has less time to fix.
              */}
              {[...myByes.entries()]
                .sort((a, b) => b[1].length - a[1].length || a[0] - b[0])
                .map(([week, names]) => (
                <li key={week} className={names.length >= 2 ? 'stack' : ''}>
                  <span className="wk">wk {week}</span>
                  <span className="who">
                    {names.map((n) => shortSurname(splitName(n).last)).join(' · ')}
                  </span>
                  <span className="n">&times;{names.length}</span>
                </li>
              ))}
              {myByes.size === 0 && <li className="note">No byes yet — you have nobody on the board.</li>}
            </ul>
          ) : (
          <>
          <div className="byeblock">
            <div className="eyebrow">Your players, by week</div>
            <div className="byechart">
              {BYE_WEEKS.map((week) => {
                const names = myByes.get(week) ?? [];
                const stacked = names.length >= 2;
                return (
                  <div key={week} title={names.length ? `Week ${week}: ${names.join(', ')}` : `Week ${week}: nobody`}>
                    <span
                      className={`bar ${names.length === 0 ? 'none' : ''} ${stacked ? 'stack' : ''}`}
                      style={names.length ? { height: `${Math.min(46, names.length * 12)}px` } : undefined}
                    />
                    <span className="wk">{week}</span>
                  </div>
                );
              })}
            </div>
            <p className="byenote">
              {worstBye && worstBye[1].length >= 2
                ? `Week ${worstBye[0]} stacks ${worstBye[1].length} of your ${myRoster.length}.`
                : 'Nobody doubles up yet.'}
            </p>
          </div>

          {/*
            WORST WEEK, EVERY TEAM — rebuilt, because the old one answered none of the questions
            it raised.

            It drew ten unlabelled colour swatches against ten near-identical bars: at these
            magnitudes 2, 3 and 4 are 36, 54 and 72 pixels, which is a chart you have to measure.
            Worse, it never said WHICH WEEK, and "who is exposed in week 7" is the only reason to
            look at somebody else's byes while you are drafting — if two of his are out in the
            week two of yours are, that is a trade conversation, not a swatch.

            So: the manager by name, the week, and the count as dots. A count of nought to five
            reads instantly as dots and needs no scale, and the row sorts worst-first so the
            people to worry about are at the top rather than in roster order.
          */}
          <div className="byeteams">
            <div className="eyebrow">Worst week, every team</div>
            {worstByTeam.map((t) => (
              <div className={`row ${t.n >= 3 ? 'bad' : ''} ${t.gm.toUpperCase() === mine.toUpperCase() ? 'me' : ''}`}
                key={t.gm}
                title={t.week
                  ? `${managerShort(t.gm)} — ${t.n} player${t.n === 1 ? '' : 's'} out in week ${t.week}`
                  : `${managerShort(t.gm)} · no roster yet`}>
                <span className="who">{managerShort(t.gm)}</span>
                <span className="wk">{t.week ? `wk ${t.week}` : <span className="mut">—</span>}</span>
                <span className="dots" aria-hidden="true">
                  {Array.from({ length: t.n }, (_, i) => <i key={i} />)}
                </span>
                <span className="n">{t.n || ''}</span>
              </div>
            ))}
          </div>

          </>
          )}
        </div>
        )}

        {/*
          The room (11a). Live only — a mock has no room. The count says "drafting for
          themselves" rather than "here", because that is the fact the app actually holds: see the
          note on `room` above. When nobody is being covered for, the line says so, which is the
          normal case and worth stating plainly rather than leaving a blank card.
        */}
        {!mock && refTab === 'room' && (
          <div className="refpane roomsec">
            <div className="roomhead">
              {/*
                COUNT THE COVERS, NOT THE HEADS (17a rev C). "7 of 10 on their own picks" made
                the room sound short-handed; what is actually true is that three men have
                somebody entering for them, which is the arrangement working. Green either way,
                as drawn — a cover is not a warning.
              */}
              <span className="ok">
                {room.away.length === 0
                  ? 'Everyone is on his own picks'
                  : `${room.away.length} GM${room.away.length === 1 ? ' has' : 's have'} someone covering for them`}
              </span>
            </div>
            <div className="roomdots">
              {order.map((g) => {
                const cover = designates[g.toUpperCase()];
                return (
                  <span className={`roomdot ${cover ? 'away' : ''}`} key={g}
                    title={cover
                      ? `${managerShort(g)} is away — ${managerShort(cover)} is covering his picks`
                      : `${managerShort(g)} is drafting his own picks`}>
                    {managerShort(g)}
                  </span>
                );
              })}
            </div>
            {room.away.length > 0 && (
              <ul className="roomcovers">
                {room.away.map((a) => (
                  <li key={a.gm}>{managerShort(a.cover)} is picking for {managerShort(a.gm)}</li>
                ))}
              </ul>
            )}
          </div>
        )}
          </>)}
        </section>
       </div>
       )}

       {view !== 'team' && railOpen && <RailGrip w={railW} onW={setRailW} />}

       <div className="boardcol">
        {/*
          THE CLOCK BAR IS PINNED, AND IT COSTS 40px ON EVERY PHONE SCREEN.
          Turn 12 is explicit about the trade and it is the right one: on a phone the thing you
          lose is your place in the queue, and a clock that scrolls away is a clock nobody reads.
          It says the same three facts in every tab — the pick on the clock, whose it is, and how
          far away yours is — so switching tabs never costs you the thread.
        */}
        {boardClock && (
          <div className={`pclockrow phoneonly ${myTurn ? 'yours' : ''}`}>
            <span className="dot" aria-hidden="true" />
            <span className="pcnow">
              {myTurn
                ? <><b>{pickCode(boardClock, TEAMS)}</b> is yours</>
                : <>
                  <b>{pickCode(boardClock, TEAMS)}</b> · {managerShort(boardClock.owner)}
                  {/*
                    Same lie as the clock bar's, in the narrow words: a lapsed clock leaves
                    picks open BEHIND the counting one, so "yours in -4" was on the phone too.
                  */}
                  {myNext && myNext.overall !== boardClock.overall
                    && (myNext.overall < boardClock.overall
                      ? <> · yours is open</>
                      : <> · yours in {myNext.overall - boardClock.overall}</>)}
                </>}
            </span>
            {/* No clock in a sandbox here either — the desk bar has never drawn one. */}
            {!mock && (
              <PickClock now={pausedAt ?? undefined} since={cellSince} seconds={cellSeconds}
                size="s" skin="dark" className="pctime" />
            )}
          </div>
        )}

        {/*
          THE COVER, AND THE WAY OUT OF IT, ON A PHONE (Tim, 6 September). `Amit is covering you`
          and `Take back control` live on the clock bar, and the clock bar is `display:none`
          under 760px — while a phone is exactly where a covered man is, because the cover was
          set for the man who said he would be in a car. The same two things get a row of their
          own under the phone clock, wearing the same button, opening the same box.
        */}
        {!mock && boardClock && designates[mine.toUpperCase()] && (
          <div className="pcoverrow phoneonly">
            <span className="pcnote">
              {managerShort(designates[mine.toUpperCase()]!)} is covering you
            </span>
            <button type="button" className="cbtake"
              title="End the cover — you enter your own picks again"
              onClick={() => setConfirm('uncover')}>Take back control</button>
          </div>
        )}

        <section className="card">
          {/*
            THE CARD'S OWN HEADER IS THE PHONE'S TAB STRIP AND NOTHING ELSE (17a rev C). On the
            desktop the switch and Full screen sit in the control row with the other two board
            switches, and a heading that said "The board" above the board has gone with them.
          */}
          {/*
            AND ON THE LIVE BOARD THERE IS ONLY ONE PANE (Tim, 8 September: "remove the My Team
            and Available tabs from this section on mobile on the live main draft. My Team is
            reflected at the bottom under the Roster tab and Available is the Player List
            section").

            Both were built before the rail was folded into the phone, and the rail arrived with
            better versions of each: `Roster` is his team with its counts and byes, and the
            player list is the ranking with a search, the position filters and the star. Three
            tabs at the top and the same two things again below is a page that answers every
            question twice and neither the same way.

            The SANDBOX keeps all three, because `Available` is where its fill controls live —
            there is no other route to them on a phone.
          */}
          {mock && (
          <div className="striptop phoneonly">
            <div className="panes small">
              <button type="button" className={`pane ${view === 'mine' ? 'on' : ''}`}
                onClick={() => setView('mine')}>My team</button>
              <button type="button" className={`pane ${view === 'grid' ? 'on' : ''}`}
                onClick={() => setView('grid')}>Board</button>
              <button type="button" className={`pane ${view === 'avail' ? 'on' : ''}`}
                onClick={() => setView('avail')}>Available</button>
            </div>
          </div>
          )}

          {view === 'mine'
            ? <MyDraft board={board} me={mine} teams={TEAMS} clock={boardClock} moved={movedMarks}
                onGoBoard={() => setView('grid')} />
            : view === 'avail'
              ? (
                <>
                  {/*
                    THE FILL CONTROLS, ON THE PHONE, IN THE MOCK ONLY — Tim's call.
                    `8b` draws them and the live board must never have them (11a): a fill writes
                    other managers' picks, which is a sandbox toy and a catastrophe on the night.
                    The same `mock` guard as the desktop row, so the two cannot drift.
                  */}
                  {mock && clock && (
                    <div className="fillrow">
                      <span className="mut filllab">Fill:</span>
                      <button type="button" className="btn small"
                        disabled={adpRank.size === 0 || fillable.thisPick.length === 0}
                        onClick={() => void fill(fillable.thisPick)}>
                        This Pick
                      </button>
                      <button type="button" className="btn small"
                        disabled={adpRank.size === 0 || fillable.toMyTurn.length === 0}
                        onClick={() => void fill(fillable.toMyTurn)}>
                        To My Next Pick{fillable.toMyTurn.length > 0 && ` · ${fillable.toMyTurn.length}`}
                      </button>
                      <button type="button" className="btn small"
                        disabled={adpRank.size === 0 || fillable.all.length === 0}
                        onClick={() => void fill(fillable.all)}>
                        Entire Board{fillable.all.length > 0 && ` · ${fillable.all.length}`}
                      </button>
                      <span className="mut fillsrc">from the ADP consensus · never your own picks</span>
                    </div>
                  )}
                  {/* The phone's Available tab is the same list, so it gets the same field. */}
                  <div className="basearch phonefind">
                    <input className="search" type="search" value={query}
                      placeholder="Last name, initials or club…"
                      aria-label="Search for a player"
                      onChange={(e) => setQuery(e.target.value)} />
                  </div>
                  {availList()}
                </>
              )
              : view === 'grid'
                ? (
                  <>
                    {/* One round as ten rows on a phone; the ten-column grid above 720px. */}
                    <div className="phoneonly">
                      <PhoneBoard board={board} rounds={ROUNDS} teams={TEAMS} clock={boardClock}
                        me={mine} onPick={setTarget}
                        canEnterPick={(p) => canEnter(p, actorInfo)} lapsed={lapsedOpen}
                        since={cellSince} startedAt={startedAt}
                        clockNow={pausedAt}
                        seconds={cellSeconds} />
                    </div>
                    <div className="deskonly">
                      <BoardGrid board={board} order={order} rounds={ROUNDS} clock={boardClock}
                        me={mine} onPick={setTarget} canEnterPick={(p) => canEnter(p, actorInfo)}
                        lapsed={lapsedOpen} onUndo={privileged ? undo : undefined}
                        activeRound={boardClock?.round ?? null}
                        inTrade={inTradeCells} moved={movedMarks} tagged={taggedMarks}
                        lapsedSince={cellSince} startedAt={startedAt}
                        clockSeconds={cellSeconds}
                        clockNow={pausedAt}
                        drag={mayMoveKeepers ? {
                          held: heldKeeper?.player ?? null,
                          canGrab: canGrabKeeper,
                          canDrop: canDropKeeper,
                          onGrab: (p) => {
                            setDropNote(null);
                            setHeldKeeper(p && p.player
                              ? { player: p.player, gm: p.owner, round: p.round }
                              : null);
                          },
                          onDrop: (p) => { void dropKeeper(p); },
                        } : undefined} />
                    </div>
                  </>
                )
                : <TeamCards board={board} order={order} clock={clock} teams={TEAMS} me={mine} />}
        </section>
       </div>
      </div>
      )}

      {/*
        TURN 12'S CONFIRM SHEET, and it is the phone's confirm rather than a second one.
        `pending` is the same state the desktop card in the rail reads, so a GM cannot be holding
        two different unconfirmed picks depending on how wide his window is — the sheet is the
        11b card at 390px, drawn the way the canvas draws it.

        THE MOCK KEEPS THE SINGLE TAP. `choose` never sets `pending` in practice mode, so this
        never appears there, which is deliberate: nothing is saved, nobody sees it, and a sheet on
        every pick would tax the one screen where a mistake costs nothing.
      */}
      {pending && entering && (
        <PhoneConfirm
          code={pickCode(entering, TEAMS)}
          name={pending.name}
          pos={pending.pos}
          bye={pendingFacts.bye}
          adp={pendingFacts.adp}
          fills={pendingFills}
          clock={pickClock?.text}
          clockOver={pickClock?.over}
          since={startedAt.get(entering.overall) ?? clockSince}
          seconds={entering.overall === (boardClock?.overall ?? -1) ? cellSeconds : null}
          yours={ownsEntering}
          owner={managerShort(entering.owner)}
          onConfirm={() => void makePick(pending.name)}
          onCancel={() => setPending(null)}
        />
      )}

      {/*
        11b, state three: the board becomes the record. Shown to everybody, because "all 160
        picks are in" is not a commissioner's private fact.
      */}
      {!mock && finished && (
        <section className="card donecard">
          <span className="eyebrow good">Draft complete</span>
          {/*
            The drawing's headline is "All 160 picks are in", which is the normal case. A
            commissioner can also close a draft early — a GM who never turns up, a round nobody
            wants — and then the honest headline is the count, not the claim.
          */}
          <h3>
            {made === board.length
              ? `All ${board.length} picks are in.`
              : `The draft is closed with ${made} of ${board.length} picks in.`}
          </h3>
          <p>
            The board becomes the record. Keeper costs for 2027 are computed from these rounds and
            appear on each GM&rsquo;s slate; the season rolls over from Admin.
          </p>
          <div className="cardacts">
            <button type="button" className="btn" onClick={exportBoard}>Export the board</button>
            <button type="button" className="btn"
              onClick={() => goTo('keepers', 'slate')}>
              See 2027 keeper costs
            </button>
          </div>
        </section>
      )}

      {/*
        11b, state two used to be a full commissioner CARD here — status, the last pick, start and
        pause, the pass list. It has moved into the bar beside the board switch, because a card
        below the board is the wrong place for the switch that turns the board on, and because
        Tim asked for the space back. What is left below is the part that is genuinely a form
        rather than a control: who is drafting for whom.

        The one thing the card carried that the bar does not is the sentence explaining what
        clearing does. It now lives on the Undo button's tooltip, next to the thing it describes.
      */}
      </Linkify>
    </div>
  );
}


/* ------------------------------------------------------------------------------------------ */

/**
 * Hypothetical pick trades, inside a mock.
 *
 * Kept deliberately concrete: you select actual picks off each man's board rather than typing
 * "a 3rd", because "a 3rd" is exactly the ambiguity that makes real trades in this league take
 * three days to settle. Picks a keeper already sits on, and picks somebody has already made in
 * the mock, are not selectable — moving those would produce a board that could not happen.
 *
 * There is no valuation and no approval. A mock is for asking whether the shape of a deal helps
 * you, not for deciding whether it is fair; the second argument belongs in Discord.
 */
function MockTrades(
  { board, order, slates, dropped, trades, onAdd, onUndo }: {
    board: BoardPick[];
    order: readonly string[];
    /** Each GM's keepers and picks, so a deal can be priced against the slate it breaks. */
    slates: Readonly<Record<string, { rows: SlateRow[]; ownedRounds: number[] }>>;
    dropped: Readonly<Record<string, readonly string[]>>;
    trades: MockTrade[];
    onAdd: (t: Omit<MockTrade, 'id'>) => void;
    onUndo: (id: number) => void;
  },
) {
  const [open, setOpen] = useState(false);
  const [a, setA] = useState(order[0] ?? '');
  const [b, setB] = useState(order[1] ?? '');
  const [aPicks, setAPicks] = useState<string[]>([]);
  const [bPicks, setBPicks] = useState<string[]>([]);

  const key = (p: PickRef) => `${p.round}:${p.seat}`;
  /*
   * A PICK A KEEPER IS SITTING ON IS STILL A PICK YOU CAN TRADE.
   *
   * It used to be excluded, on the reasoning that trading it would produce a board that could not
   * happen. That was wrong about the rules: 2015-III-7 charges a keeper to any pick at or before
   * his cost round, so selling the 4th you are paying Harrison with does not un-keep him — it
   * re-prices him to the 3rd, and the deal is worth considering precisely because of what it
   * costs you elsewhere. What cannot happen is trading so many that the slate stops fitting, and
   * that is a thing to SAY rather than a thing to hide by greying the row out.
   *
   * A pick already MADE in the mock stays out: that one really is gone.
   */
  const tradable = (gm: string) => board
    .filter((p) => p.owner.toUpperCase() === gm.toUpperCase() && (!p.player || p.keeper))
    .map((p) => ({
      round: p.round,
      seat: p.seat,
      overall: p.overall,
      code: pickCode(p, TEAMS),
      keeper: p.player && p.keeper ? p.player : null,
    }));

  const aList = tradable(a);
  const bList = tradable(b);

  /**
   * What the pending selection would do to each man's keepers, worked out before he presses Swap.
   *
   * Both directions matter and they are different sentences. Giving picks away can leave a slate
   * unpayable, which is a refusal naming the round he has to keep back. Giving them away without
   * breaking it still moves keepers onto earlier picks, which is a warning naming who moves —
   * the cost of the deal is usually that, not the picks themselves, and it is invisible unless
   * the app says it.
   */
  const preview = useMemo(() => {
    const forSide = (gm: string, gives: string[], gets: string[], theirs: typeof aList) => {
      const ctx = slates[gm.toUpperCase()];
      const gone = new Set(dropped[gm.toUpperCase()] ?? []);
      const keepers = (ctx?.rows ?? [])
        .filter((r) => !gone.has(r.player))
        .map((r) => ({ player: r.player, costRound: r.costRound }));
      if (!keepers.length) return null;

      const after = [...(ctx?.ownedRounds ?? [])];
      for (const k of gives) {
        const i = after.indexOf(Number(k.split(':')[0]));
        if (i >= 0) after.splice(i, 1);
      }
      for (const p of theirs) if (gets.includes(key(p))) after.push(p.round);

      const shortfall = feasibility(keepers, after);
      if (shortfall) return { gm, shortfall, moves: [] as string[] };

      const now = new Map(
        board
          .filter((p) => p.owner.toUpperCase() === gm.toUpperCase() && p.player && p.keeper)
          .map((p) => [p.player!, p.round] as const),
      );
      const moves = [...fitKeepers(keepers, after).placed]
        .filter(([player, round]) => now.has(player) && now.get(player) !== round)
        .map(([player, round]) => `${said(player)} to Round ${round}`);
      return { gm, shortfall: null, moves };
    };

    return [
      forSide(a, aPicks, bPicks, bList),
      forSide(b, bPicks, aPicks, aList),
    ].filter((x): x is NonNullable<typeof x> => x !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b, aPicks, bPicks, slates, dropped, board]);

  const refused = preview.filter((x) => x.shortfall);
  const shifting = preview.filter((x) => !x.shortfall && x.moves.length);

  function swap() {
    const aGets = bList.filter((p) => bPicks.includes(key(p))).map((p) => ({ round: p.round, seat: p.seat }));
    const bGets = aList.filter((p) => aPicks.includes(key(p))).map((p) => ({ round: p.round, seat: p.seat }));
    if (aGets.length + bGets.length === 0) return;
    onAdd({ a, b, aGets, bGets });
    setAPicks([]);
    setBPicks([]);
  }

  const toggle = (list: string[], set: (v: string[]) => void, k: string) =>
    set(list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);

  /** "4.09" the way the board writes it, with the original seat only when it is not the obvious one. */
  const label = (picks: readonly PickRef[], from: string) => picks
    .map((p) => {
      const onBoard = board.find((x) => x.round === p.round && x.seat === p.seat);
      const code = onBoard ? pickCode(onBoard, TEAMS) : `${p.round}.??`;
      return p.seat.toUpperCase() === from.toUpperCase() ? code : `${code} (${p.seat}'s)`;
    })
    .join(', ');

  return (
    <section className="card mocktrades">
      <button type="button" className="striptop pairtoggle" aria-expanded={open}
        onClick={() => setOpen(!open)}>
        <h3 className="striphead">Trade picks</h3>
        <span className="pill violet">Sandbox</span>
        <span className="mut">
          {trades.length === 0
            ? 'lands on this board only'
            : `${trades.length} hypothetical trade${trades.length === 1 ? '' : 's'} in this mock`}
        </span>
        <span className="mut caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <>
          <div className="tradepick">
            <Column
              label="gives"
              gm={a}
              gms={order}
              onGm={(g) => { setA(g); setAPicks([]); }}
              picks={aList}
              selected={aPicks}
              onToggle={(k) => toggle(aPicks, setAPicks, k)}
              other={b}
            />
            <div className="tradego">
              <button type="button" className="btn primary"
                disabled={a === b || aPicks.length + bPicks.length === 0 || refused.length > 0}
                title={refused.length ? 'This deal leaves a slate that cannot be paid' : undefined}
                onClick={swap}>
                Swap
              </button>
              <span className="mut">
                {aPicks.length} ⇄ {bPicks.length}
              </span>
            </div>
            <Column
              label="gives"
              gm={b}
              gms={order}
              onGm={(g) => { setB(g); setBPicks([]); }}
              picks={bList}
              selected={bPicks}
              onToggle={(k) => toggle(bPicks, setBPicks, k)}
              other={a}
            />
          </div>

          {refused.map((r) => (
            <p className="tradewarn bad" key={r.gm}>
              <b>This trade cannot be completed.</b>{' '}
              {managerShort(r.gm)} would be left without the picks to pay his keepers.{' '}
              {shortfallSays(r.shortfall)}
            </p>
          ))}

          {shifting.map((r) => (
            <p className="tradewarn" key={r.gm}>
              {managerShort(r.gm)}’s keepers re-price onto earlier picks: {andList(r.moves)}.{' '}
              <span className="mut">
                A keeper is charged to any pick at or before his cost round, so selling the one he
                sits on moves him up rather than dropping him.
              </span>
            </p>
          ))}

          {trades.length > 0 && (
            <ul className="mocktradelist">
              {trades.map((t) => (
                <li key={t.id}>
                  <b>{managerShort(t.a)}</b> gets {label(t.aGets, t.b) || 'nothing'}
                  <span className="mut"> ⇄ </span>
                  <b>{managerShort(t.b)}</b> gets {label(t.bGets, t.a) || 'nothing'}
                  <span className="mut"> — lands on this board only.</span>
                  <button type="button" className="linkbtn tiny" onClick={() => onUndo(t.id)}>undo</button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function Column(
  { label, gm, gms, onGm, picks, selected, onToggle, other }: {
    label: string;
    gm: string;
    gms: readonly string[];
    onGm: (g: string) => void;
    picks: Array<{
      round: number; seat: string; overall: number; code: string; keeper: string | null;
    }>;
    selected: string[];
    onToggle: (key: string) => void;
    other: string;
  },
) {
  return (
    <div className="tradecol">
      <div className="tradecolhead">
        <select value={gm} aria-label="Manager" onChange={(e) => onGm(e.target.value)}>
          {gms.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
        </select>
        <span className="mut">{label}</span>
      </div>
      {picks.length === 0 && <p className="mut small">no picks left to trade</p>}
      <div className="pickchips">
        {picks.map((p) => {
          const k = `${p.round}:${p.seat}`;
          return (
            <button
              key={k}
              type="button"
              className={`chip pickchip ${selected.includes(k) ? 'on' : ''}${p.keeper ? ' spent' : ''}`}
              onClick={() => onToggle(k)}
              title={[
                p.seat.toUpperCase() === gm.toUpperCase()
                  ? `${p.code} — his own` : `${p.code} — originally ${p.seat}'s`,
                p.keeper ? `paying for ${p.keeper}, who moves to an earlier pick if it goes`
                  : null,
              ].filter(Boolean).join(' · ')}
            >
              {p.code}
              {p.seat.toUpperCase() !== gm.toUpperCase() && (
                <span className="mut">{p.seat.slice(0, 2)}</span>
              )}
              {/* The keeper's name, not a generic marker: which keeper it is decides the deal. */}
              {p.keeper && <span className="onit">{said(p.keeper)}</span>}
            </button>
          );
        })}
      </div>
      <span className="mut small">to {other}</span>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ */

/**
 * THE RESTART MARK. One circular arrow, drawn rather than typed: `↻` is missing from the display
 * face and fell back to a glyph half the size of the label beside it, so both restart buttons
 * read as bare words. An inline SVG inherits `currentColor` and lines up with the cap height.
 */

